import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, NavLink } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';
import {
    Car, Fuel, Wallet, PlusCircle, MinusCircle, Plus,
    History, Settings, TrendingUp, Trash2,
    Calendar, AlertCircle, CheckCircle2, Moon, Sun, X,
    ChevronRight, ChevronLeft, AlertTriangle, User, Bike,
    MapPin, Gauge, Pencil, Droplet,
    Download, FileText, Calculator,
    Wrench, Disc, CircleDashed, ArrowRight, ArrowLeft,
    ChevronDown, Target, CheckSquare, Banknote, Edit3,
    Map as MapLucide, Navigation, Clock, DollarSign, Repeat, CalendarClock, Package,
    BarChart3, ThumbsUp, Phone, MessageCircle, Upload, LayoutGrid, ListChecks, Home,
    Bell, Star, HelpCircle, Share2, Smartphone, Timer, Play, Pause, Square,
    Settings as SettingsIcon, LogOut, Menu, Shield, Crown, RefreshCw, CreditCard, Mic, Lock, ShoppingBag
} from 'lucide-react';
import aiVoiceService from './services/aiVoiceService';
import aiOCRService from './services/aiOCRService';
import { auth, db, logAnalyticsEvent } from './firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
    doc, getDoc, onSnapshot, collection, query, where, orderBy, limit, updateDoc, increment
} from 'firebase/firestore';


import MissionTracker from './components/Tools/MissionTracker';

import DataStorageInfoModal from './components/shared/DataStorageInfoModal';
import ConfirmationModal from './components/shared/ConfirmationModal';
import HelpModal from './components/shared/HelpModal';

import TrackerWidget from './components/Tracker/TrackerWidget';
import { TrackingService } from './services/TrackingService';
import LocationDisclosureModal from './components/shared/LocationDisclosureModal';

// Componentes otimizados
import StatCard from './components/shared/StatCard';
import Toast from './components/shared/Toast';
import NotificationBanner from './components/shared/NotificationBanner';
import CategoryBarChart from './components/charts/CategoryBarChart';
import LoadingSpinner from './components/shared/LoadingSpinner';

import errorService from './services/errorService';

// Hooks customizados
import { useApp } from './contexts/AppContext';
import useNotifications from './hooks/useNotifications';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useWakeLock } from './hooks/useWakeLock';

import { isNativeApp, getPlatform, isPWA, isDirectDistribution, getAppDistributionInfo } from './utils/platformUtils';
import { checkDirectUpdate } from './services/directUpdateService';
import { getTodayDateKey } from './utils/dateHelpers';
import { formatCurrency, formatValueBRL, parseValueBRL, formatNumber, formatWorkHours } from './utils/formatters';
import { resolvePlatformCategory, normalizeTransaction } from './utils/transactionUtils';
import { isAdmin } from './config/adminConfig';

import {
    saveUserData, fetchUserData, subscribeUnreadSupportMessages,
    markMessagesAsRead, resetSupportCooldown, hasOpenTicket,
    subscribeFeatureFlags, subscribeGlobalBanner, subscribeAppSettings, getDefaultTrialDays,
    subscribeSubscriptionAlerts, markUserAsLoggedOut, deleteUserAccount, clearUserDataRemote,
    saveTransactionRemote, deleteTransactionRemote, fetchTransactionsRemote,
    fetchUpdatedTransactionsRemote, updateUserMetricsRemote,
    saveLojaItems, fetchLojaItems, completeRemoteCommand
} from './services/dbService';
import SubscriptionAlertModal from './components/SubscriptionAlertModal';
import storage from './services/storageService';
import { AppProvider } from './contexts/AppContext';
import { ModalProvider, useModal } from './contexts/ModalContext';
import { APP_VERSION } from './version';
import { compareVersions } from './services/directUpdateService';
import { AppUpdate, AppUpdateAvailability, FlexibleUpdateInstallStatus } from '@capawesome/capacitor-app-update';

/**
 * Avalia se a versão instalada corresponde ao critério de versão alvo (ex: '< 1.11.10', '>= 1.10.0')
 */
const matchesTargetVersion = (currentVer, targetRule) => {
    if (!targetRule || targetRule === 'all') return true;
    const op = targetRule.match(/(<=|>=|<|>|==|=)/)?.[0] || '==';
    const targetVer = targetRule.replace(op, '').trim();
    const cmp = compareVersions(currentVer, targetVer);
    if (op === '<') return cmp < 0;
    if (op === '<=') return cmp <= 0;
    if (op === '>') return cmp > 0;
    if (op === '>=') return cmp >= 0;
    return cmp === 0;
};

// Otimização: Lazy Loading das páginas para reduzir bundle inicial
const ToolsPage = React.lazy(() => import('./pages/ToolsPage'));
const AnalyticsPage = React.lazy(() => import('./pages/AnalyticsPage'));
const TransactionFormPage = React.lazy(() => import('./pages/TransactionFormPage'));
const CalculatorsPage = React.lazy(() => import('./pages/CalculatorsPage'));
const MissionsPage = React.lazy(() => import('./pages/MissionsPage'));
const AgendaPage = React.lazy(() => import('./pages/AgendaPage'));
const FlexCalcPage = React.lazy(() => import('./pages/FlexCalcPage'));
const TripCalcPage = React.lazy(() => import('./pages/TripCalcPage'));
const FinancingCalcPage = React.lazy(() => import('./pages/FinancingCalcPage'));
const UberProCalcPage = React.lazy(() => import('./pages/UberProCalcPage'));
const AppToolsPage = React.lazy(() => import('./pages/AppToolsPage'));
const LojaABordoPage = React.lazy(() => import('./pages/LojaABordoPage'));
const GoalsPage = React.lazy(() => import('./pages/GoalsPage'));
const FixedExpensesPage = React.lazy(() => import('./pages/FixedExpensesPage'));
const HistoryPage = React.lazy(() => import('./pages/HistoryPage'));
const VehiclesPage = React.lazy(() => import('./pages/VehiclesPage'));
const OperationalCostPage = React.lazy(() => import('./pages/OperationalCostPage'));

const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const FlightTrackerPage = React.lazy(() => import('./pages/FlightTrackerPage'));

// Modais e páginas de rastreamento pesados com Lazy Loading
const TrackerPage = React.lazy(() => import('./pages/TrackerPage'));
const TrackerHistoryPage = React.lazy(() => import('./pages/TrackerHistoryPage'));
const FinancingCalculatorModal = React.lazy(() => import('./components/Tools/FinancingCalculatorModal'));
const FlexCalcModal = React.lazy(() => import('./components/Calculators/FlexCalcModal'));
const TripCalcModal = React.lazy(() => import('./components/Calculators/TripCalcModal'));
const DailyStatsEditModal = React.lazy(() => import('./components/History/DailyStatsEditModal'));
const LinkAccountModal = React.lazy(() => import('./components/auth/LinkAccountModal'));
const InstallPwaModal = React.lazy(() => import('./components/InstallPwaModal'));
const AnnouncementModal = React.lazy(() => import('./components/shared/AnnouncementModal'));
const EarningsTrackerModal = React.lazy(() => import('./components/EarningsTrackerModal'));
const CloudRestoreModal = React.lazy(() => import('./components/shared/CloudRestoreModal'));
const AppearanceQuickModal = React.lazy(() => import('./components/shared/AppearanceQuickModal'));
const DirectUpdateModal = React.lazy(() => import('./components/shared/DirectUpdateModal'));


// --- CONFIGURAÇÃO PWA ---
// Nome do Aplicativo: MeuPainel
// A persistência de dados é feita via storageService (abstração resiliente para Preferences/Local).

// --- CONFIGURAÇÃO API GEMINI ---
// A chave é carregada de variáveis de ambiente por segurança.
// Configure o arquivo .env na raiz do projeto com: REACT_APP_GEMINI_API_KEY=sua_chave_aqui
const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

// Validação da API Key
if (!apiKey) {
    console.error('âš ï¸ ERRO: API Key do Gemini não encontrada!');
    console.error('Configure o arquivo .env com REACT_APP_GEMINI_API_KEY=sua_chave_aqui');
}

// --- FUNÇÃO AUXILIAR DE DATA (REMOVIDA POIS AGORA VEM DE UTILS) ---
// const getTodayDateKey = () => new Date().toISOString().split('T')[0];

// NOTA: StatCard, Toast, ProfitHistoryChart e CategoryBarChart agora são importados
// de components/shared/ e components/charts/ com React.memo para melhor performance

// --- CATEGORIAS PADRÃO (movidas para fora do componente para estabilidade) ---
const DEFAULT_INCOME_CATEGORIES = ['Uber', '99', 'iFood', 'Rappi', 'Zé Delivery', 'Lalamove', 'Loggi', 'inDrive', 'Particular', 'Gorjeta'];
const DEFAULT_EXPENSE_CATEGORIES = ['Gasolina', 'Etanol', 'GNV', 'Elétrico', 'Alimentação', 'Manutenção', 'Lava Jato', 'Contas Fixas'];


function AppContent({ firebaseUser, onLogout, onResetPassword }) {
    const {
        transactions, setTransactions, transactionsRef,
        vehicles, setVehicles, setVehiclesDirectly, vehiclesRef, vehiclesLastUpdated, setVehiclesLastUpdated, vehiclesLastUpdatedRef,
        userProfile, setUserProfile, userProfileRef,
        theme, setTheme, themeRef,
        systemPrefersDark, setSystemPrefersDark,
        isDarkModeActive,
        kmRecords, setKmRecords, kmRecordsRef,
        formatKm,
        customCategories, setCustomCategories, customCategoriesRef,
        scheduledTrips, setScheduledTrips, scheduledTripsRef,
        dailyManualStats, setDailyManualStats, dailyManualStatsRef,
        activeShift, setActiveShift, activeShiftRef,
        activeShiftKey, setActiveShiftKey, activeShiftKeyRef,
        isInitialLoadDone, setIsInitialLoadDone,
        isLocalDataLoaded, setIsLocalDataLoaded,
        isStorageReady, setIsStorageReady,
        lastRemoteSync, setLastRemoteSync,
        isSyncing, setIsSyncing,
        deletedDefaultCategories, setDeletedDefaultCategories,
        subscription, setSubscription,
        isSubscriptionLoaded, setIsSubscriptionLoaded,
        accentColor, setAccentColor,
        compactMode, setCompactMode,
        goals, setGoals, setGoalsDirectly, goalsRef,
        goalsLastUpdated, setGoalsLastUpdated, goalsLastUpdatedRef,
        pinnedGoalId, setPinnedGoalId, pinnedGoalIdRef,
        simulationPeriod, setSimulationPeriod, simulationPeriodRef,
        simulationDailyAmount, setSimulationDailyAmount, simulationDailyAmountRef,
        simulatorDaysOff, setSimulatorDaysOff, simulatorDaysOffRef,
        simulatorRideValue, setSimulatorRideValue, simulatorRideValueRef,
        isCompactViewSettings, setIsCompactViewSettings,
        dailyGoal, setDailyGoal, dailyGoalRef,
        monthlyWorkDays, setMonthlyWorkDays, monthlyWorkDaysRef,
        monthlyProfitTarget, setMonthlyProfitTarget, monthlyProfitTargetRef,
        manualSavedAmount, setManualSavedAmount, manualSavedAmountRef,
        manualVarCost, setManualVarCost, manualVarCostRef,
        manualVarCostType, setManualVarCostType, manualVarCostTypeRef,
        varCostMode, setVarCostMode, varCostModeRef,
        pastDueExpenses, setPastDueExpenses, pastDueExpensesRef,
        pastDueExpensesLastUpdated, setPastDueExpensesLastUpdated, pastDueExpensesLastUpdatedRef, setPastDueExpensesDirectly,
        setPastDueTombstones, pastDueTombstonesRef, removePastDueWithTombstone,
        vehicleTombstones, setVehicleTombstones, vehicleTombstonesRef, removeVehicleWithTombstone,
        hideAdminPanel, setHideAdminPanel, hideAdminPanelRef,
        lastRolloverMonth, setLastRolloverMonth,
        fixedExpenses, setFixedExpenses, fixedExpensesRef,
        fixedExpensesLastUpdated, setFixedExpensesLastUpdated, fixedExpensesLastUpdatedRef, setFixedExpensesDirectly,
        selectedVehicleId, setSelectedVehicleId,
        localTracker, setLocalTracker,
        showCardComparisons, setShowCardComparisons,
        dynamicGoalsConfig, setDynamicGoalsConfig, dynamicGoalsConfigRef,
        maintenanceFundConfig, setMaintenanceFundConfig, maintenanceFundConfigRef,
        lastRolloverMonthRef,
        toast, showToast, getCategories
    } = useApp();

    const localTrackerRef = useRef(localTracker);
    useEffect(() => {
        localTrackerRef.current = localTracker;
    }, [localTracker]);

    // Ref para controlar se já buscamos as transações da subcoleção para evitar loops
    const hasFetchedRemoteTransactionsRef = useRef(false);
    const isMigratingRef = useRef(false);

    // --- TOMBSTONES DE ATRASADOS (anti-zumbi) ---
    // Une os tombstones vindos da nuvem aos locais e retorna a lista unificada.
    // Usado no merge para que uma dívida baixada em outro aparelho nunca volte.
    const mergeRemoteTombstones = useCallback((remoteTombstones) => {
        const local = Array.isArray(pastDueTombstonesRef.current) ? pastDueTombstonesRef.current : [];
        if (!Array.isArray(remoteTombstones) || remoteTombstones.length === 0) return local;
        const merged = [...local];
        remoteTombstones.forEach(id => { if (typeof id === 'string' && !merged.includes(id)) merged.push(id); });
        const capped = merged.slice(-1000);
        pastDueTombstonesRef.current = capped;
        setPastDueTombstones(capped);
        storage.setItem('rotaDoLucroPastDueTombstones', capped);
        return capped;
    }, [setPastDueTombstones]);

    // --- TOMBSTONES + MERGE DE VEÍCULOS (união por ID) ---
    // Une tombstones da nuvem aos locais e retorna a lista unificada.
    const mergeRemoteVehicleTombstones = useCallback((remoteTombstones) => {
        const local = Array.isArray(vehicleTombstonesRef.current) ? vehicleTombstonesRef.current.map(String) : [];
        if (!Array.isArray(remoteTombstones) || remoteTombstones.length === 0) return local;
        const merged = [...local];
        remoteTombstones.forEach(id => {
            const key = String(id);
            if (!merged.includes(key)) merged.push(key);
        });
        const capped = merged.slice(-1000);
        vehicleTombstonesRef.current = capped;
        setVehicleTombstones(capped);
        storage.setItem('rotaDoLucroVehicleTombstones', capped);
        return capped;
    }, [setVehicleTombstones]);

    // Une duas listas de veículos por String(id). Para o mesmo id, os campos
    // escalares seguem o lado mais novo; customMaintenance/history são unidos
    // por id para não perder registros concorrentes. Tombstones filtram exclusões.
    const mergeVehicleLists = useCallback((localList, remoteList, preferRemote, tombstones) => {
        const tombSet = new Set((Array.isArray(tombstones) ? tombstones : []).map(String));
        const map = new Map();
        (Array.isArray(localList) ? localList : []).forEach(v => {
            if (v && v.id !== undefined && !tombSet.has(String(v.id))) map.set(String(v.id), v);
        });
        (Array.isArray(remoteList) ? remoteList : []).forEach(v => {
            if (!v || v.id === undefined || tombSet.has(String(v.id))) return;
            const key = String(v.id);
            if (map.has(key)) {
                const cur = map.get(key);
                const mergedItem = preferRemote ? { ...cur, ...v } : { ...v, ...cur };
                const unionById = (a, b) => {
                    const m = new Map();
                    [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])].forEach(it => {
                        if (it && it.id !== undefined) m.set(String(it.id), it);
                    });
                    return [...m.values()];
                };
                if (cur?.customMaintenance || v?.customMaintenance) {
                    mergedItem.customMaintenance = unionById(cur?.customMaintenance, v?.customMaintenance);
                }
                if (cur?.maintenanceHistory || v?.maintenanceHistory) {
                    const hist = unionById(cur?.maintenanceHistory, v?.maintenanceHistory);
                    hist.sort((x, y) => new Date(y.date || 0) - new Date(x.date || 0));
                    mergedItem.maintenanceHistory = hist;
                }
                map.set(key, mergedItem);
            } else {
                map.set(key, v);
            }
        });
        return [...map.values()];
    }, []);

    // --- FUNÇÃO DE MIGRAÇÃO DE TRANSAÇÕES ---
    const migrateTransactionsToSubcollection = useCallback(async (userId, legacyTransactions) => {
        if (!legacyTransactions || legacyTransactions.length === 0 || isMigratingRef.current) return;

        isMigratingRef.current = true;
        console.log(`APP_JS: Iniciando migração de ${legacyTransactions.length} transações para subcoleções...`);
        showToast('Otimizando banco de dados...', 'info');

        try {
            // Salva cada transação individualmente na subcoleção com tratamento de erro individual
            let successCount = 0;
            let failCount = 0;

            for (const tx of legacyTransactions) {
                try {
                    const ok = await saveTransactionRemote(userId, tx);
                    if (ok) successCount++;
                    else failCount++;
                } catch (e) {
                    console.error(`APP_JS: Erro ao migrar transação ${tx.id}:`, e);
                    failCount++;
                }
            }

            console.log(`APP_JS: Migração de transações concluída. Sucesso: ${successCount}, Falhas: ${failCount}`);

            // Só marca como concluído se tivemos algum sucesso ou se não havia nada para migrar
            if (successCount > 0 || legacyTransactions.length === 0) {
                await saveUserData(userId, { removeTransactionsField: true });
                console.log('APP_JS: Documento principal marcado para limpeza.');
            }

            // Após migrar todas, remove o campo 'transactions' do documento principal e atualiza métricas
            const metrics = {
                transactionsCount: legacyTransactions.length,
                totalIncome: legacyTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + ((typeof t.amount === 'number' ? t.amount : parseValueBRL(t.value || t.amount)) || 0), 0),
                totalExpense: legacyTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + ((typeof t.amount === 'number' ? t.amount : parseValueBRL(t.value || t.amount)) || 0), 0),
                vehiclesCount: vehiclesRef.current?.length || 0
            };

            await saveUserData(userId, { removeTransactionsField: true });
            await updateUserMetricsRemote(userId, metrics);

            console.log('APP_JS: Migração concluída com sucesso!');
            showToast('Banco de dados otimizado!', 'success');
        } catch (error) {
            console.error('APP_JS: Erro durante a migração:', error);
        } finally {
            isMigratingRef.current = false;
        }
    }, [showToast]);

    /**
     * Sincroniza métricas de resumo (contadores/totais) com o documento principal.
     */
    const syncUserMetrics = useCallback(async (currentTxs) => {
        if (!firebaseUser?.uid) return;
        const txs = currentTxs || transactionsRef.current || [];
        const metrics = {
            transactionsCount: txs.length,
            totalIncome: txs.filter(t => t.type === 'income').reduce((sum, t) => sum + ((typeof t.amount === 'number' ? t.amount : parseValueBRL(t.value || t.amount)) || 0), 0),
            totalExpense: txs.filter(t => t.type === 'expense').reduce((sum, t) => sum + ((typeof t.amount === 'number' ? t.amount : parseValueBRL(t.value || t.amount)) || 0), 0),
            vehiclesCount: vehiclesRef.current?.length || 0
        };
        await updateUserMetricsRemote(firebaseUser.uid, metrics);
    }, [firebaseUser]);

    // --- MOTOR DE VIRADA DE MÊS (MONTH ROLLOVER ENGINE) ---
    const performMonthRollover = useCallback((currentMonthKey) => {
        console.log(`APP_JS: Iniciando Rollover de Mês para ${currentMonthKey}...`);

        let newPastDue = [...(pastDueExpensesRef.current || [])];
        let currentFixed = [...(fixedExpensesRef.current || [])];
        let hasChanges = false;

        // Determina o mês inicial do processamento
        let startMonthKey = lastRolloverMonth;
        if (!startMonthKey) {
            // Se lastRolloverMonth não existe, verifica se há despesas pagas com datas antigas
            const oldestLegacyMonth = currentFixed
                .filter(e => e.paid && e.paidDate)
                .map(e => e.paidDate.substring(0, 7))
                .sort()[0];
            startMonthKey = oldestLegacyMonth || null;
        }

        // Calcula todos os meses intermediários que viraram sem pagamento (ex: de 2026-05 até 2026-09)
        const getMonthsToRollover = (fromKey, toKey) => {
            if (!fromKey || !toKey || fromKey >= toKey) return [];
            const months = [];
            let [y, m] = fromKey.split('-').map(Number);
            const [endY, endM] = toKey.split('-').map(Number);

            let safetyCounter = 0;
            while ((y < endY || (y === endY && m < endM)) && safetyCounter < 36) {
                months.push(`${y}-${String(m).padStart(2, '0')}`);
                m++;
                if (m > 12) {
                    m = 1;
                    y++;
                }
                safetyCounter++;
            }
            return months;
        };

        const monthsToProcess = getMonthsToRollover(startMonthKey, currentMonthKey);
        if (monthsToProcess.length === 0 && startMonthKey && startMonthKey < currentMonthKey) {
            monthsToProcess.push(startMonthKey);
        }

        if (monthsToProcess.length > 0) {
            console.log(`APP_JS: Processando ${monthsToProcess.length} mês(es) de virada em atraso:`, monthsToProcess);

            // Tombstones: dívidas já pagas/excluídas pelo usuário nunca são regeneradas.
            const tombSet = new Set(pastDueTombstonesRef.current || []);

            monthsToProcess.forEach((missedMonth) => {
                // 1. Processar contas NÃO PAGAS deste mês e enviar para o Past Due com a parcela do respectivo mês
                const unpaidExpenses = currentFixed.filter(e => !e.paid);
                if (unpaidExpenses.length > 0) {
                    hasChanges = true;
                    unpaidExpenses.forEach(e => {
                        const debtId = `pastdue_${e.originalId || e.id}_${missedMonth}`;
                        // Pula dívidas já baixadas/excluídas e as que já estão na lista
                        if (tombSet.has(debtId)) return;
                        if (!newPastDue.some(p => p.id === debtId)) {
                            newPastDue.push({
                                ...e,
                                originalId: e.originalId || e.id,
                                id: debtId,
                                missedMonth: missedMonth,
                                currentInstallment: e.currentInstallment || null,
                                totalInstallments: e.totalInstallments || null
                            });
                        }
                    });
                }

                // 2. Progredir as contas para o ciclo seguinte
                currentFixed = currentFixed.map(e => {
                    let updated = { ...e, paid: false, paidDate: null };

                    // Lógica de Parcelas
                    if (updated.totalInstallments && updated.currentInstallment) {
                        if (updated.currentInstallment < updated.totalInstallments) {
                            updated.currentInstallment += 1;
                            hasChanges = true;
                            return updated;
                        } else {
                            // Se atingiu a última parcela no mês encerrado, a conta termina
                            hasChanges = true;
                            return null;
                        }
                    }
                    hasChanges = true;
                    return updated;
                }).filter(Boolean);
            });
        } else {
            // Se não há meses anteriores a processar mas foi acionado por despesas antigas marcadas pagas
            currentFixed = currentFixed.map(e => {
                if (e.paid && e.paidDate && e.paidDate.substring(0, 7) < currentMonthKey) {
                    hasChanges = true;
                    return { ...e, paid: false, paidDate: null };
                }
                return e;
            });
        }

        if (hasChanges) {
            // Garantia final: remove da lista qualquer dívida tombstoned que possa
            // ter vindo do cache local/nuvem antes dos tombstones chegarem.
            const tombSetFinal = new Set(pastDueTombstonesRef.current || []);
            if (tombSetFinal.size > 0) {
                newPastDue = newPastDue.filter(p => !tombSetFinal.has(p.id));
            }
            setPastDueExpenses(newPastDue);
            setFixedExpenses(currentFixed);
            pastDueExpensesRef.current = newPastDue;
            fixedExpensesRef.current = currentFixed;
            console.log(`APP_JS: Rollover multi-mês concluído com sucesso. Total de pendências acumuladas: ${newPastDue.length}`);
        }

        setLastRolloverMonth(currentMonthKey);
        lastRolloverMonthRef.current = currentMonthKey;
        storage.setItem('meupainel-last-rollover-month', currentMonthKey);
    }, [lastRolloverMonth, setLastRolloverMonth, setPastDueExpenses, setFixedExpenses]);

    // Verificação de Rollover na Inicialização
    // IMPORTANTE: só executa após a primeira sincronização com a nuvem
    // (hasSyncedWithFirestoreRef). Sem isso, um aparelho desatualizado gera
    // atrasados a partir da lista antiga e ressuscita contas já pagas/excluídas.
    useEffect(() => {
        if (!isInitialLoadDone || !hasSyncedWithFirestoreRef.current) return; // Aguarda carregar dados locais e remotos

        const currentMonthKey = new Date().toISOString().substring(0, 7); // Ex: "2026-09"

        // Verificação extra: Se houver despesas pagas de meses anteriores que não foram resetadas, força o rollover
        const hasLegacyPaidExpenses = fixedExpensesRef.current?.some(e =>
            e.paid && e.paidDate && e.paidDate.substring(0, 7) < currentMonthKey
        );

        if (!lastRolloverMonth) {
            // Se é a primeira vez e temos despesas legadas pagas, executa o rollover para limpar
            if (hasLegacyPaidExpenses) {
                performMonthRollover(currentMonthKey);
            } else {
                setLastRolloverMonth(currentMonthKey);
                lastRolloverMonthRef.current = currentMonthKey;
                storage.setItem('meupainel-last-rollover-month', currentMonthKey);
            }
        } else if (lastRolloverMonth !== currentMonthKey || hasLegacyPaidExpenses) {
            // O MÊS VIROU (pode ter virado 1 ou vários meses)
            performMonthRollover(currentMonthKey);
        }
    }, [isInitialLoadDone, lastRolloverMonth, performMonthRollover, setLastRolloverMonth]);


    const { requestWakeLock, releaseWakeLock } = useWakeLock();
    const [activeAnnouncement, setActiveAnnouncement] = useState(null);
    const [directUpdateData, setDirectUpdateData] = useState(null);
    const [keepScreenOn, setKeepScreenOn] = useState(() => {
        return storage.getItemSync('meupainel-keep-screen-on', true);
    });

    // --- CAPTURA DE CUPOM E UTMs VIA URL ---
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const refCode = params.get('ref')?.toUpperCase()?.trim();
        const campaignSlug = params.get('c')?.toLowerCase()?.trim() || params.get('campanha')?.toLowerCase()?.trim();

        // Captura de UTMs e IDs de Rastreamento (Google/Meta Ads)
        const utmParams = {
            source: params.get('utm_source'),
            medium: params.get('utm_medium'),
            campaign: params.get('utm_campaign'),
            term: params.get('utm_term'),
            content: params.get('utm_content'),
            gclid: params.get('gclid'),
            fbclid: params.get('fbclid'),
            ref: refCode || null // Salva também o cupom de origem para contexto
        };

        // Verifica se há algum parâmetro de rastreamento REAL
        const hasTracking = Object.values(utmParams).some(val => val !== null && val !== undefined && val !== '');

        if (hasTracking) {
            utmParams.capturedAt = new Date().toISOString();
            console.log('APP_JS: Rastreamento detectado na URL:', utmParams);
            storage.setItem('pending_utm', utmParams);
        }

        if (refCode && refCode.length >= 3 && refCode.length <= 30) {
            console.log('APP_JS: Cupom detectado na URL:', refCode);
            storage.setItem('pending_coupon', refCode);
            showToast(`Cupom ${refCode} ativado com sucesso!`, 'success');
        }

        if (campaignSlug) {
            const processCampaign = async () => {
                try {
                    const campaignRef = doc(db, 'marketing_links', campaignSlug);
                    const campaignSnap = await getDoc(campaignRef);

                    if (campaignSnap.exists()) {
                        const campaignData = campaignSnap.data();

                        // 1. Evita contagem duplicada de cliques na mesma sessão
                        const sessionKey = `mp_camp_${campaignSlug}`;
                        if (!sessionStorage.getItem(sessionKey)) {
                            await updateDoc(campaignRef, {
                                clicksCount: increment(1)
                            });
                            sessionStorage.setItem(sessionKey, 'true');
                        }

                        // 2. Salva campanha pendente
                        storage.setItem('pending_campaign', campaignSlug);

                        // 3. Se tiver cupom associado, ativa o cupom
                        if (campaignData.couponCode) {
                            const coupon = campaignData.couponCode.toUpperCase().trim();
                            storage.setItem('pending_coupon', coupon);
                            console.log('APP_JS: Cupom de campanha ativado:', coupon);
                            showToast(`Cupom ${coupon} ativado!`, 'success');
                        }
                    } else {
                        console.warn('APP_JS: Campanha de marketing não encontrada:', campaignSlug);
                    }
                } catch (error) {
                    console.error('APP_JS: Erro ao processar clique da campanha:', error);
                }
            };
            processCampaign();
        }

        // Limpa os parâmetros da URL sem recarregar a página para manter a estética
        if (refCode || hasTracking || campaignSlug) {
            const url = new URL(window.location.href);
            url.searchParams.delete('ref');
            url.searchParams.delete('c');
            url.searchParams.delete('campanha');
            url.searchParams.delete('utm_source');
            url.searchParams.delete('utm_medium');
            url.searchParams.delete('utm_campaign');
            url.searchParams.delete('utm_term');
            url.searchParams.delete('utm_content');
            url.searchParams.delete('gclid');
            url.searchParams.delete('fbclid');
            window.history.replaceState({}, document.title, url.pathname + url.search);
        }
    }, [showToast]);

    // 1. Inicializa Monitoramento de Erros
    useEffect(() => {
        if (firebaseUser) {
            errorService.setUserId(firebaseUser.uid);

            // Ordem de prioridade para o nome no log:
            // 1. Nome real do perfil (se disponível e não for o padrão)
            // 2. Nome de exibição do Google/Auth
            // 3. E-mail da conta
            const profileName = userProfile?.name;
            const authName = firebaseUser.displayName;
            const authEmail = firebaseUser.email;

            if (profileName && profileName !== 'Piloto') {
                errorService.setUserName(profileName);
            } else if (authName) {
                errorService.setUserName(authName);
            } else if (authEmail) {
                errorService.setUserName(authEmail);
            }
        }
    }, [firebaseUser, userProfile?.name]);

    useEffect(() => {
        errorService.init(firebaseUser?.uid, userProfile?.name);
    }, []); // Only on mount, but updates via setters above



    // 2. Listener de Notificações Globais/Push (Firestore)
    // Diferido 3s após login para não competir com carregamento inicial de dados (Android startup)
    const { sendSystemNotification } = useNotifications(scheduledTrips, selectedVehicleId);

    useEffect(() => {
        if (!firebaseUser) return;

        let unsubscribe = null;
        const timer = setTimeout(() => {
            const notifRef = collection(db, 'notifications');
            const q = query(
                notifRef,
                where('status', '==', 'active'),
                orderBy('createdAt', 'desc'),
                limit(5)
            );

            unsubscribe = onSnapshot(q, (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added') {
                        const data = change.doc.data();
                        const notifId = change.doc.id;

                        // Verifica se já viu esta notificação
                        const seenNotifs = storage.getItemSync('seen_global_notifs', []);
                        if (seenNotifs.includes(notifId)) return;

                        // Filtra por segmento
                        const userPlan = subscription?.plan || 'free';
                        const isBeta = userProfile?.isBeta || subscription?.isTrial;

                        let shouldShow = false;
                        if (!data.segment || data.segment === 'all') shouldShow = true;
                        else if (data.segment === 'premium' && userPlan === 'premium') shouldShow = true;
                        else if (data.segment === 'beta' && isBeta) shouldShow = true;
                        else if (data.segment === 'free' && userPlan === 'free') shouldShow = true;

                        // Filtros granulares adicionais configurados pelo Admin
                        if (shouldShow && data.targetVersion && !matchesTargetVersion(APP_VERSION, data.targetVersion)) {
                            shouldShow = false;
                        }

                        if (shouldShow && data.targetPlatform && data.targetPlatform !== 'all') {
                            const currentPlatform = getPlatform();
                            if (data.targetPlatform !== currentPlatform) {
                                shouldShow = false;
                            }
                        }

                        if (shouldShow && Array.isArray(data.targetUids) && data.targetUids.length > 0) {
                            if (!data.targetUids.includes(firebaseUser.uid)) {
                                shouldShow = false;
                            }
                        }

                        if (shouldShow && data.targetCampaign) {
                            const userCamp = storage.getItemSync('pending_campaign') || '';
                            if (userCamp.toLowerCase() !== data.targetCampaign.toLowerCase()) {
                                shouldShow = false;
                            }
                        }

                        if (shouldShow) {
                            // Exibe Notificação de Sistema (se permitido)
                            sendSystemNotification(data.title, data.body, `global-${notifId}`);

                            // Define o comunicado ativo para exibir o Modal
                            setActiveAnnouncement({ id: notifId, ...data });
                        }
                    }
                });
            }, (err) => {
                // Silencia erros de permissão para evitar floods no console/crash
                if (err.code !== 'permission-denied') {
                    console.error('Erro no listener de notificações:', err);
                }
            });
        }, 3000); // Diferido 3s: prioriza carregamento de dados essenciais

        return () => {
            clearTimeout(timer);
            if (unsubscribe) unsubscribe();
        };
    }, [firebaseUser, subscription, userProfile, sendSystemNotification, showToast]);

    // --- ROUTER HOOKS ---
    const navigate = useNavigate();
    const location = useLocation();

    // Derived state for backward compatibility
    const view = location.pathname === '/' || location.pathname === '' ? 'dashboard' : location.pathname.substring(1);

    // Rastreamento de página para contexto nos logs de erro e Analytics
    useEffect(() => {
        errorService.setPage(view);

        // Log de evento do Google Analytics
        logAnalyticsEvent('screen_view', {
            firebase_screen: view,
            firebase_screen_class: 'App',
            platform: getPlatform(),
            is_native: isNativeApp(),
            is_pwa: isPWA()
        });
    }, [view]);


    const changeView = useCallback((newView, state = {}) => {
        if (newView === 'dashboard') navigate('/', { state });
        else navigate(`/${newView}`, { state });
    }, [navigate]);

    const setView = changeView;

    const { confirmModal, setConfirmModal } = useModal();
    const [showResetModal, setShowResetModal] = useState(false);
    const [settingsInitialTab, setSettingsInitialTab] = useState('profile');
    const [goalsInitialTab, setGoalsInitialTab] = useState('overview');
    const [isLocked, setIsLocked] = useState(false);
    const [isExpired, setIsExpired] = useState(false);
    const [initialRedirectDone, setInitialRedirectDone] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);

    // Flag de Primeira Execução (Para evitar restauração acidental sobre deleções locais)
    const [isFirstRun, setIsFirstRun] = useState(false);
    const [isFirstRunChecked, setIsFirstRunChecked] = useState(false);

    // Modal de restauração da nuvem ao logar
    const [cloudRestoreData, setCloudRestoreData] = useState(null); // { remoteData, cloudStats, localStats }

    // Efeito para checar isFirstRun após o storage estar pronto (importante para Android)
    useEffect(() => {
        if (!isStorageReady) return;

        const checkFirstRun = async () => {
            const installed = await storage.getItem('rotaDoLucroInstalled');
            setIsFirstRun(!installed);
            setIsFirstRunChecked(true);
            console.log('APP_JS: Verificação de isFirstRun concluída:', !installed);
        };
        checkFirstRun();
    }, [isStorageReady]);

    // --- FUNÇÃO PARA ABRIR RENOVAÇÃO PREMIUM ---
    const handleOpenPremium = useCallback(() => {
        setShowPremiumModal(true);
    }, []);

    const [voiceData, setVoiceData] = useState(null);
    const [globalBanner, setGlobalBanner] = useState(null);
    const [appSettings, setAppSettings] = useState(() => ({ defaultTrialDays: getDefaultTrialDays() }));
    // Sistema de Alertas Inteligentes (Trial, Plano e Modo Leitura)
    const [subscriptionAlertSettings, setSubscriptionAlertSettings] = useState(() => {
        return storage.getItemSync('subscriptionAlertSettings', {
            enabled: true,
            trialWarningDays: 3,
            planWarningDays: 3
        });
    });
    const [activeAlert, setActiveAlert] = useState(null); // { type, daysLeft }
    // Feature flags do painel admin — padrão seguro
    const [featureFlags, setFeatureFlags] = useState({
        jornadaPro: { visible: false, bannerVisible: false },
        aiVoice: { visible: true, allowAdmin: true, allowPartner: true, allowPremium: true, allowTrial: true, showToFree: true, bannerVisible: false },
        flightRadar: { visible: false, bannerVisible: false },
        agenda: { visible: true, bannerVisible: false },
        calculators: { visible: true, bannerVisible: false },
        vehicles: { visible: true, bannerVisible: false },
        appTools: { visible: true, bannerVisible: false },
        lojaABordo: { visible: false, bannerVisible: false }
    });

    // Loja a Bordo Quick Sale status
    const [hasLojaItems, setHasLojaItems] = useState(false);
    useEffect(() => {
        if (featureFlags?.lojaABordo?.visible && firebaseUser?.uid) {
            import('./services/dbService').then(({ fetchLojaItems }) => {
                fetchLojaItems(firebaseUser.uid).then(items => {
                    // Verifica se existem produtos cadastrados no estoque
                    if (items && items.length > 0) {
                        setHasLojaItems(true);
                    } else {
                        setHasLojaItems(false);
                    }
                });
            });
        } else {
            setHasLojaItems(false);
        }

        const handleLojaItemsUpdated = (e) => {
            const items = e.detail;
            setHasLojaItems(items && items.length > 0);
        };

        window.addEventListener('lojaItemsUpdated', handleLojaItemsUpdated);
        return () => window.removeEventListener('lojaItemsUpdated', handleLojaItemsUpdated);
    }, [featureFlags?.lojaABordo?.visible, firebaseUser?.uid]);

    // Listener para o Banner Global
    // Diferido 3s após login para não competir com carregamento inicial (Android startup)
    useEffect(() => {
        if (!firebaseUser) return;
        let unsubscribe = null;
        const timer = setTimeout(() => {
            unsubscribe = subscribeGlobalBanner((data) => {
                if (data && data.active) {
                    setGlobalBanner({ ...data, id: 'global-banner' });
                } else {
                    setGlobalBanner(null);
                }
            });
        }, 3000); // Diferido 3s: prioriza carregamento de dados essenciais
        return () => {
            clearTimeout(timer);
            if (unsubscribe) unsubscribe();
        };
    }, [firebaseUser]);

    // Listener para Feature Flags (Jornada Pro, Falar para Adicionar, etc.)
    // Diferido 3s após login para não competir com carregamento inicial (Android startup)
    useEffect(() => {
        if (!firebaseUser) return;
        let unsubscribe = null;
        const timer = setTimeout(() => {
            unsubscribe = subscribeFeatureFlags((flags) => {
                // Suporte a Whitelist de Beta Testers / Acesso antecipado por UID
                const resolvedFlags = {};
                Object.keys(flags || {}).forEach(key => {
                    const feat = flags[key];
                    if (feat && typeof feat === 'object') {
                        const inWhitelist = Array.isArray(feat.whitelistUids) && feat.whitelistUids.includes(firebaseUser.uid);
                        resolvedFlags[key] = {
                            ...feat,
                            visible: inWhitelist ? true : feat.visible
                        };
                    } else {
                        resolvedFlags[key] = feat;
                    }
                });
                setFeatureFlags(prev => ({
                    ...prev,
                    ...resolvedFlags
                }));
            });
        }, 3000); // Diferido 3s: prioriza carregamento de dados essenciais
        return () => {
            clearTimeout(timer);
            if (unsubscribe) unsubscribe();
        };
    }, [firebaseUser]);

    // Listener Global de Respostas de Suporte Não Lidas
    useEffect(() => {
        if (!firebaseUser?.uid) return;
        let prevCount = null;
        const unsubscribe = subscribeUnreadSupportMessages(firebaseUser.uid, (unreadCount) => {
            if (prevCount !== null && unreadCount > prevCount) {
                // Nova resposta recebida da equipe enquanto o app está aberto
                sendSystemNotification(
                    '💬 Suporte MeuPainel',
                    'Você recebeu uma nova resposta da nossa equipe!',
                    'support-reply'
                );
                showToast('Nova resposta do suporte recebida!', 'info');
            }
            prevCount = unreadCount;
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [firebaseUser?.uid, sendSystemNotification, showToast]);

    // Listener para Configurações Gerais do App (Dias de Teste / Trial configurados no Painel Admin)
    useEffect(() => {
        if (!firebaseUser) return;
        const unsubscribe = subscribeAppSettings((data) => {
            if (data) {
                setAppSettings(data);
            }
        });
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [firebaseUser]);

    // Listener para Configurações do Sistema de Pop-ups de Aviso e Modo Leitura
    useEffect(() => {
        if (!firebaseUser) return;
        let unsubscribe = null;
        const timer = setTimeout(() => {
            unsubscribe = subscribeSubscriptionAlerts((data) => {
                if (data) {
                    setSubscriptionAlertSettings(data);
                }
            });
        }, 3000);
        return () => {
            clearTimeout(timer);
            if (unsubscribe) unsubscribe();
        };
    }, [firebaseUser]);


    // Avaliação Global de Acesso aos Recursos (Jornada Pro, Voz IA, Loja a Bordo)
    const { showJornadaPro, lockJornadaPro, showAiVoice, lockAiVoice, showFlightRadar, lockFlightRadar, showVehicles, lockVehicles, showLoja, lockLoja } = useMemo(() => {
        const jFlags = featureFlags?.jornadaPro || {};
        const vFlags = featureFlags?.aiVoice || {};
        const fFlags = featureFlags?.flightRadar || {};
        const vehFlags = featureFlags?.vehicles || {};
        const lojaFlags = featureFlags?.lojaABordo || {};

        const evaluateAccess = (flags, featureKey) => {
            if (Object.keys(flags).length === 0) return { show: false, lock: true };

            // 0. WHITELIST (Beta Testers): acesso garantido por UID, independente de plano,
            // plataforma ou modo isca (upsell) — prioridade máxima definida no Painel Admin
            const inWhitelist = Array.isArray(flags.whitelistUids) && flags.whitelistUids.includes(firebaseUser?.uid);
            if (inWhitelist) {
                console.log(`[ACCESS] ${featureKey}: Liberado por Whitelist (beta tester)`);
                return { show: true, lock: false };
            }

            // 1. RESTRIÇÃO DE PLATAFORMA (androidOnly): Oculta completamente para TODOS se não for Android nativo
            // Esta regra tem prioridade máxima — afeta inclusive Admin e Parceiros
            if (flags.androidOnly && getPlatform() !== 'android') {
                console.log(`[ACCESS] ${featureKey}: Oculto por plataforma (androidOnly - não é Android nativo)`);
                return { show: false, lock: true };
            }

            const userIsAdmin = isAdmin(firebaseUser?.uid);

            // 2. ADMIN E PARCEIRO: Acesso total se a flag permitir
            if (userIsAdmin && flags.allowAdmin) {
                console.log(`[ACCESS] ${featureKey}: Liberado para Admin`);
                return { show: true, lock: false };
            }
            if (userProfile?.isPartner && flags.allowPartner) {
                console.log(`[ACCESS] ${featureKey}: Liberado para Parceiro`);
                return { show: true, lock: false };
            }

            // 3. TRIAL: Verifica se é usuário em período de teste e a flag allowTrial está ativa
            const isTrial = subscription?.isTrial === true;
            if (isTrial && flags.allowTrial) {
                console.log(`[ACCESS] ${featureKey}: Liberado para Usuário em Teste (trial)`);
                return { show: true, lock: false };
            }

            // 4. PREMIUM: Verifica se tem plano ativo, não expirado e NÃO é trial
            const hasValidPlan = subscription?.plan
                && subscription?.plan !== 'free'
                && subscription?.plan !== 'trial'
                && !isTrial;
            const isPremiumActive = hasValidPlan && !isExpired;

            if (isPremiumActive && flags.allowPremium) {
                console.log(`[ACCESS] ${featureKey}: Liberado para Assinante`);
                return { show: true, lock: false };
            }

            if (flags.showToFree) {
                console.log(`[ACCESS] ${featureKey}: Exibindo como Upsell (lock)`);
                return { show: true, lock: true };
            }

            console.log(`[ACCESS] ${featureKey}: Oculto (Sem acesso)`);
            return { show: false, lock: true };
        };

        const jornada = evaluateAccess(jFlags, 'JornadaPro');
        const voice = evaluateAccess(vFlags, 'AiVoice');
        const flight = evaluateAccess(fFlags, 'FlightRadar');
        const vehiclesAccess = evaluateAccess(vehFlags, 'Vehicles');
        const lojaAccess = evaluateAccess(lojaFlags, 'LojaABordo');

        return {
            showJornadaPro: jornada.show,
            lockJornadaPro: jornada.lock,
            showAiVoice: voice.show,
            lockAiVoice: voice.lock,
            showFlightRadar: flight.show,
            lockFlightRadar: flight.lock,
            showVehicles: vehiclesAccess.show,
            lockVehicles: vehiclesAccess.lock,
            showLoja: lojaAccess.show,
            lockLoja: lojaAccess.lock
        };
    }, [featureFlags, userProfile, subscription, isExpired, firebaseUser]);

    const handleVoiceAction = useCallback(async () => {
        if (lockAiVoice) {
            handleOpenPremium();
            return;
        }

        try {
            setIsVoiceRecording(true);
            const transcript = await aiVoiceService.listen();
            setIsVoiceRecording(false);

            setVoiceLoading(true);
            const result = await aiVoiceService.interpretCommand(transcript);
            setVoiceLoading(false);

            if (result && result.amount > 0) {
                // Preenche os campos para a página de adição
                setTransType(result.type || 'expense');

                // Se for uma despesa, verifica se a categoria existe ou cria temporariamente
                const category = result.category || 'Outros';

                // Navega para a página de adição com os dados pré-preenchidos
                // Nota: O TransactionFormPage precisaria aceitar props iniciais.
                // Como ele usa estado interno baseado em setView('add'), 
                // vamos simular o preenchimento enviando para a função de salvamento rápido 
                // ou apenas setando os estados que o formulário usa se eles forem globais aqui.

                // No App.js, o formulário é uma página separada (TransactionFormPage) que usa setView('add').
                // Vamos passar os dados via estado temporário.
                setVoiceData(result);
                setShowAddModal(false);
                setView('add');
            } else {
                showToast("Não entendi o valor ou o comando. Tente novamente.", "error");
            }
        } catch (error) {
            setIsVoiceRecording(false);
            setVoiceLoading(false);
            showToast(error.message || "Erro ao processar voz.", "error");
        }
    }, [lockAiVoice, handleOpenPremium]);

    const handleConfirmAnnouncement = (dontShowAgain) => {
        if (!activeAnnouncement) return;

        if (dontShowAgain) {
            const seenNotifs = storage.getItemSync('seen_global_notifs', []);
            const updatedSeen = [...new Set([...seenNotifs, activeAnnouncement.id])];
            storage.setItem('seen_global_notifs', updatedSeen);
        }

        setActiveAnnouncement(null);
    };

    const [kmInput, setKmInput] = useState({
        isOpen: false,
        type: null,
        isShiftStart: false,
        tempValue: '',
        tempStartValue: '',
        tempHours: ''
    });

    const [showNewVehicleForm, setShowNewVehicleForm] = useState(false);
    const [showLinkAccountModal, setShowLinkAccountModal] = useState(false);

    // --- DATA DO DASHBOARD ---
    const [dashboardDate, setDashboardDateRaw] = useState(getTodayDateKey());
    const setDashboardDate = (date) => {
        // Validação: Se a data for vazia ou inválida, reseta para hoje
        if (!date || isNaN(new Date(date).getTime())) {
            const today = getTodayDateKey();
            setDashboardDateRaw(today);
            setDailyFilter(today);
            return;
        }

        if (date > getTodayDateKey()) {
            showToast("Não é possível selecionar datas futuras no painel.", 'info');
            return;
        }
        setDashboardDateRaw(date);
        setDailyFilter(date); // Sincroniza a data do extrato
    };


    const lastSyncAttemptRef = useRef(0);
    const loadDoneAtRef = useRef(0);
    const hasSyncedWithFirestoreRef = useRef(false);
    // Retry flag: se um sync forçado chegar enquanto isSyncing=true, agenda retry automático
    const pendingSyncRef = useRef(false);
    // Ref espelhando isSyncing (evita deps no useCallback e re-renders em filhos)
    const isSyncingRef = useRef(false);

    const [showDailyManualModal, setShowDailyManualModal] = useState(false);
    const [showFlexModal, setShowFlexModal] = useState(false);
    const [showTripCalcModal, setShowTripCalcModal] = useState(false);
    const [transType, setTransType] = useState('income');
    const [historyView, setHistoryView] = useState('daily');
    const [dailyFilter, setDailyFilter] = useState(getTodayDateKey());
    const [weeklyFilter, setWeeklyFilter] = useState(getTodayDateKey());
    const [monthlyFilter, setMonthlyFilter] = useState(new Date().toISOString().substring(0, 7));
    const [yearlyFilter, setYearlyFilter] = useState(new Date().getFullYear().toString());
    const [showFinishDayConfirmModal, setShowFinishDayConfirmModal] = useState(false);
    const [showDataInfoModal, setShowDataInfoModal] = useState(false);
    const [showPwaModal, setShowPwaModal] = useState(false);





    const [editingTransactionId, setEditingTransactionId] = useState(null);

    const [showMissionModal, setShowMissionModal] = useState(false);
    const [showCalculatorMenu, setShowCalculatorMenu] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showLocationDisclosure, setShowLocationDisclosure] = useState(false);
    const [pendingTrackerAction, setPendingTrackerAction] = useState(null);
    const [showAppearanceModal, setShowAppearanceModal] = useState(false);



    // Efeito para abrir o modal ao acessar a aba de dados




    // Estados do formulário de veículo






    const fileInputRef = useRef(null);






    // --- NOVO ESTADO PARA CATEGORIAS CUSTOMIZADAS (SEPARADO) ---




    // --- FUNÇÃ•ES DE DADOS (BACKUP / CSV) ---



    // --- ESTADO PWA ---
    const [isIOS, setIsIOS] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isPwaInstalled, setIsPwaInstalled] = useState(false);
    const [showEarningsModal, setShowEarningsModal] = useState(false);


    const [isFinancingCalcOpen, setIsFinancingCalcOpen] = useState(false);
    const [isVoiceRecording, setIsVoiceRecording] = useState(false);
    const [voiceLoading, setVoiceLoading] = useState(false);

    // Configura API Key do Gemini nos serviços de IA
    useEffect(() => {
        if (apiKey) {
            aiVoiceService.setApiKey(apiKey);
            aiOCRService.setApiKey(apiKey);
        }
    }, [apiKey]);
    // Novo controle simples para UI
    const [isExternalModalOpen, setIsExternalModalOpen] = useState(false);


    // --- CRONÔMETRO DE TRABALHO ---
    const [workTimer, setWorkTimer] = useState(() => {
        return storage.getItemSync('meupainel-work-timer', {
            serviceStartTime: null,   // timestamp fixo de quando iniciou (nunca muda)
            startTime: null,           // timestamp do segmento ativo atual
            elapsedTime: 0,            // tempo ativo acumulado (sem pausa)
            isRunning: false,
            isPaused: false,
            offlineElapsed: 0,         // ms totais acumulados de pausa
            offlinePauseStart: null,   // quando a pausa atual começou
            hasPausedOnce: false
        });
    });
    const [timerDisplay, setTimerDisplay] = useState('00:00:00');
    const [autoStartTimer, setAutoStartTimer] = useState(true); // Para checkbox no modal de turno (Padrão selecionado)

    // --- NOVO: CHECAGEM CENTRALIZADA DE MODAIS ABERTOS ---
    const isAnyModalOpen = useMemo(() => {
        return (
            showAddModal ||
            showCalculatorMenu ||
            showFlexModal ||
            showTripCalcModal ||
            kmInput.isOpen ||
            showDailyManualModal ||
            showResetModal ||
            showFinishDayConfirmModal ||
            showLinkAccountModal ||
            showPwaModal ||
            isFinancingCalcOpen ||
            showMissionModal ||
            showDataInfoModal ||
            showEarningsModal ||
            showPremiumModal ||
            isExternalModalOpen ||
            !!confirmModal
        );
    }, [
        showAddModal, showCalculatorMenu, showFlexModal, showTripCalcModal,
        kmInput.isOpen, showDailyManualModal, showPremiumModal, showResetModal,
        showFinishDayConfirmModal, showLinkAccountModal, showPwaModal,
        isFinancingCalcOpen, showMissionModal, showDataInfoModal, confirmModal, showEarningsModal, isExternalModalOpen
    ]);
    // --- FUNÇAO PARA FORÇAR SINCRONIZAÇÃO REMOTA (AGORA USANDO REFS) ---
    const triggerRemoteSync = useCallback(async (force = false) => {
        if (!firebaseUser?.uid || !isInitialLoadDone || !hasSyncedWithFirestoreRef.current) {
            if (!isInitialLoadDone || !hasSyncedWithFirestoreRef.current) {
                console.log('APP_JS: Sync ignorado - Carregamento inicial ou merge pendente.');
            }
            return;
        }

        // Se um sync já está em curso e este é forçado, agenda retry para após o término
        if (isSyncingRef.current) {
            if (force) {
                console.log('APP_JS: Sync forçado agendado (isSyncing=true). Será executado ao final do sync atual.');
                pendingSyncRef.current = true;
            }
            return;
        }

        // --- TRAVA DE SEGURANÇA: THROTTLING (Evita custos extras no plano pago) ---
        const now = Date.now();
        const MIN_SYNC_INTERVAL = 10 * 1000; // REDUZIDO: 10 segundos para maior agilidade nas metas

        if (!force && (now - lastSyncAttemptRef.current < MIN_SYNC_INTERVAL)) {
            console.log(`APP_JS: Sync bloqueado por throttling (Aguarde ${Math.ceil((MIN_SYNC_INTERVAL - (now - lastSyncAttemptRef.current)) / 1000)}s)`);
            return;
        }

        lastSyncAttemptRef.current = now;
        console.log(`APP_JS: Disparando sincronização remota(Forçado: ${force})...`);

        // --- PREPARA DATA DO RELATÓRIO USANDO REFS PARA EVITAR STALE CLOSURES ---
        // Otimização: Não enviamos campos que o Admin controla no root para evitar reversão acidental.
        // Identifica se este build é APK direto ou Play Store (usado nos filtros do painel admin).
        const { distributionChannel, appId } = await getAppDistributionInfo();
        const payload = {
            email: firebaseUser.email,
            authEmail: firebaseUser.email,
            authProvider: firebaseUser.providerData[0]?.providerId || 'password',
            platform: getPlatform(),
            isPWA: isPWA(),
            vehicles: vehiclesRef.current,
            vehiclesLastUpdated: vehiclesLastUpdatedRef.current,
            vehicleTombstones: vehicleTombstonesRef.current || [],
            // Apenas enviamos o userProfile se ele for o básico 'Piloto' ou se quisermos forçar atualização de campos locais.
            // O dbService.js agora faz o merge, então enviamos apenas o que o cliente controla.
            userProfile: {
                name: (userProfileRef.current?.name && userProfileRef.current?.name !== 'Piloto')
                    ? userProfileRef.current.name
                    : (firebaseUser.displayName || 'Piloto')
            },
            kmRecords: kmRecordsRef.current,
            customCategories: customCategoriesRef.current,
            fixedExpenses: fixedExpensesRef.current,
            fixedExpensesLastUpdated: fixedExpensesLastUpdatedRef.current,
            scheduledTrips: scheduledTripsRef.current,
            dailyManualStats: dailyManualStatsRef.current,
            dailyGoal: dailyGoalRef.current,
            monthlyWorkDays: monthlyWorkDaysRef.current,
            monthlyProfitTarget: monthlyProfitTargetRef.current,
            manualSavedAmount: manualSavedAmountRef.current,
            manualVarCost: manualVarCostRef.current,
            manualVarCostType: manualVarCostTypeRef.current,
            varCostMode: varCostModeRef.current,
            pastDueExpenses: pastDueExpensesRef.current,
            pastDueExpensesLastUpdated: pastDueExpensesLastUpdatedRef.current,
            pastDueTombstones: pastDueTombstonesRef.current || [],
            activeShiftKey: activeShiftKeyRef.current,
            activeShift: activeShiftRef.current,
            hideAdminPanel: hideAdminPanelRef.current,
            theme: themeRef.current,
            goals: goalsRef.current,
            goalsLastUpdated: goalsLastUpdatedRef.current,
            pinnedGoalId: pinnedGoalIdRef.current,
            simulationPeriod: simulationPeriodRef.current,
            simulationDailyAmount: simulationDailyAmountRef.current,
            simulatorDaysOff: simulatorDaysOffRef.current,
            simulatorRideValue: simulatorRideValueRef.current,
            dynamicGoalsConfig: dynamicGoalsConfigRef.current,
            maintenanceFundConfig: maintenanceFundConfigRef.current,
            selectedVehicleId: selectedVehicleId,
            lastRolloverMonth: lastRolloverMonthRef.current,
            opCostData: storage.getItemSync('opCostData', {}),
            appVersion: APP_VERSION,
            distributionChannel,
            appId,
            missions: storage.getItemSync('mission_tracker_storage_v2', [])
        };

        isSyncingRef.current = true;
        setIsSyncing(true);
        const success = await saveUserData(firebaseUser.uid, payload);
        isSyncingRef.current = false;
        setIsSyncing(false);

        if (success) {
            setLastRemoteSync(Date.now());
        }

        // --- RETRY: Se um sync forçado foi agendado durante este sync, executa agora ---
        if (pendingSyncRef.current) {
            pendingSyncRef.current = false;
            console.log('APP_JS: Executando sync pendente agendado...');
            // Pequeno delay para evitar burst de escrita no Firestore
            setTimeout(() => {
                // Chama novamente mas sem usar o triggerRemoteSync (que está em closure)
                // Usa a mesma lógica de save direto
                if (firebaseUser?.uid && hasSyncedWithFirestoreRef.current) {
                    const retryPayload = {
                        email: firebaseUser.email,
                        authEmail: firebaseUser.email,
                        authProvider: firebaseUser.providerData[0]?.providerId || 'password',
                        platform: getPlatform(),
                        isPWA: isPWA(),
                        vehicles: vehiclesRef.current,
                        vehiclesLastUpdated: vehiclesLastUpdatedRef.current,
                        vehicleTombstones: vehicleTombstonesRef.current || [],
                        userProfile: {
                            name: (userProfileRef.current?.name && userProfileRef.current?.name !== 'Piloto')
                                ? userProfileRef.current.name
                                : (firebaseUser.displayName || 'Piloto')
                        },
                        kmRecords: kmRecordsRef.current,
                        customCategories: customCategoriesRef.current,
                        fixedExpenses: fixedExpensesRef.current,
                        fixedExpensesLastUpdated: fixedExpensesLastUpdatedRef.current,
                        scheduledTrips: scheduledTripsRef.current,
                        dailyManualStats: dailyManualStatsRef.current,
                        dailyGoal: dailyGoalRef.current,
                        monthlyWorkDays: monthlyWorkDaysRef.current,
                        monthlyProfitTarget: monthlyProfitTargetRef.current,
                        manualSavedAmount: manualSavedAmountRef.current,
                        manualVarCost: manualVarCostRef.current,
                        manualVarCostType: manualVarCostTypeRef.current,
                        varCostMode: varCostModeRef.current,
                        pastDueExpenses: pastDueExpensesRef.current,
                        pastDueExpensesLastUpdated: pastDueExpensesLastUpdatedRef.current,
                        pastDueTombstones: pastDueTombstonesRef.current || [],
                        activeShiftKey: activeShiftKeyRef.current,
                        activeShift: activeShiftRef.current,
                        hideAdminPanel: hideAdminPanelRef.current,
                        theme: themeRef.current,
                        goals: goalsRef.current,
                        goalsLastUpdated: goalsLastUpdatedRef.current,
                        pinnedGoalId: pinnedGoalIdRef.current,
                        simulationPeriod: simulationPeriodRef.current,
                        simulationDailyAmount: simulationDailyAmountRef.current,
                        simulatorDaysOff: simulatorDaysOffRef.current,
                        simulatorRideValue: simulatorRideValueRef.current,
                        dynamicGoalsConfig: dynamicGoalsConfigRef.current,
                        maintenanceFundConfig: maintenanceFundConfigRef.current,
                        selectedVehicleId: selectedVehicleId,
                        lastRolloverMonth: lastRolloverMonthRef.current,
                        opCostData: storage.getItemSync('opCostData', {}),
                        appVersion: APP_VERSION,
                        distributionChannel,
                        appId,
                        missions: storage.getItemSync('mission_tracker_storage_v2', [])
                    };
                    isSyncingRef.current = true;
                    setIsSyncing(true);
                    saveUserData(firebaseUser.uid, retryPayload).then(retrySuccess => {
                        isSyncingRef.current = false;
                        setIsSyncing(false);
                        if (retrySuccess) {
                            setLastRemoteSync(Date.now());
                            console.log('APP_JS: Sync pendente concluído com sucesso.');
                        }
                    }).catch(() => { isSyncingRef.current = false; setIsSyncing(false); });
                }
            }, 1500);
        }
    }, [
        firebaseUser,
        isInitialLoadDone,
        deletedDefaultCategories // Mantendo este como dependência pois é menos crítico de mudar rápido, mas poderia ser Ref também
    ]);

    const handleAddQuickTransaction = useCallback((date, data) => {
        const uniqueId = Date.now() + Math.floor(Math.random() * 1000);
        const newTransaction = {
            id: uniqueId,
            date: date,
            time: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            // tripCount: respeita o valor passado em data, fallback para 1 se for income sem especificar
            tripCount: data.tripCount ?? (data.type === 'income' ? 1 : 0),
            ...data
        };
        setTransactions(prev => {
            const updated = [newTransaction, ...prev];
            transactionsRef.current = updated;
            storage.setItem('rotaDoLucroData', updated);
            return updated;
        });
        showToast('Lançamento adicionado!', 'success');

        // Sincronização individual
        if (firebaseUser?.uid) {
            saveTransactionRemote(firebaseUser.uid, newTransaction);
        }

        // Sincroniza logo
        triggerRemoteSync(true);
    }, [setTransactions, transactionsRef, firebaseUser, triggerRemoteSync, showToast]);

    const handleSubmitLocalTracker = useCallback((silent = false) => {
        const currentTracker = localTrackerRef.current || localTracker;
        if (!currentTracker || Object.keys(currentTracker.platforms || {}).length === 0) {
            if (!silent) showToast('Nenhuma plataforma preenchida.', 'error');
            return;
        }

        // Determina a data correta do turno:
        // 1º Prioridade: data do turno ativo (activeShift.startDate) para garantir alinhamento total com hodômetro
        // 2º Prioridade: data de início persistida no tracker (currentTracker.startDate)
        // 3º Prioridade: data do cronômetro (workTimer.startTime)
        // 4º Fallback: data local de hoje
        let dateStr;
        if (activeShiftRef.current?.startDate) {
            dateStr = activeShiftRef.current.startDate;
        } else if (activeShift?.startDate) {
            dateStr = activeShift.startDate;
        } else {
            let dateToUse = currentTracker.startDate || (workTimer && workTimer.startTime ? workTimer.startTime : Date.now());
            const dObj = new Date(dateToUse);
            if (!isNaN(dObj.getTime())) {
                const year = dObj.getFullYear();
                const month = String(dObj.getMonth() + 1).padStart(2, '0');
                const day = String(dObj.getDate()).padStart(2, '0');
                dateStr = `${year}-${month}-${day}`;
            } else {
                dateStr = getTodayDateKey();
            }
        }

        let addedCount = 0;
        const incomeCategories = typeof getCategories === 'function' ? getCategories('income') : [];

        Object.entries(currentTracker.platforms).forEach(([platformId, data]) => {
            const formatPlatName = (platId) => {
                const map = {
                    'uber': 'Uber',
                    'indrive': 'InDrive',
                    '99': '99',
                    'particular': 'Particular',
                    'outros': 'Outros'
                };
                return map[platId] || capitalizeFirstLetter(platId);
            };

            const platformName = formatPlatName(platformId);
            const rawAmt = Number(data.amount) || 0;
            const rawTrips = Number(data.trips) || 0;
            const startAmt = data.hasStartingBalance ? (Number(data.startAmount) || 0) : 0;
            const startTrips = data.hasStartingBalance ? (Number(data.startTrips) || 0) : 0;

            const effectiveAmt = Math.round(Math.max(0, rawAmt - startAmt) * 100) / 100;
            const effectiveTrips = Math.max(0, rawTrips - startTrips);
            const platformCategory = resolvePlatformCategory(platformId, platformName, incomeCategories);

            if (effectiveAmt > 0 || effectiveTrips > 0) {
                // Main transaction - inclui o total líquido de ganhos e corridas com a categoria correta
                handleAddQuickTransaction(dateStr, {
                    type: 'income',
                    category: platformCategory,
                    amount: effectiveAmt,
                    originalAmount: effectiveAmt,
                    tripCount: effectiveTrips, // corridas líquidas da plataforma
                    description: `Ganhos ${platformName}${data.hasStartingBalance && startAmt > 0 ? ' (Desc. Saldo Inicial)' : ''}`,
                    platform: platformName,
                    paymentMethod: 'Pix'
                });
                addedCount++;
            }

            // Cutoff transactions (corridas e ganhos pós-virada)
            if (data.hasCutoff) {
                const amtAfter = Math.round((Number(data.amountAfterCutoff) || 0) * 100) / 100;
                const tripsAfter = Number(data.tripsAfterCutoff) || 0;
                if (amtAfter > 0 || tripsAfter > 0) {
                    handleAddQuickTransaction(dateStr, {
                        type: 'income',
                        category: platformCategory,
                        amount: amtAfter,
                        originalAmount: amtAfter,
                        tripCount: tripsAfter, // corridas reais pós-corte
                        description: `Ganhos ${platformName} (Pós-Corte)`,
                        platform: platformName,
                        paymentMethod: 'Pix'
                    });
                    addedCount++;
                }
            }
        });

        if (addedCount > 0) {
            showToast(`${addedCount} lançamento(s) de ganhos salvo(s) com sucesso!`, 'success');
            // Sync with Firebase immediately
            triggerRemoteSync(true);
            const emptyTracker = { amount: 0, trips: 0, totalAmount: 0, totalTrips: 0, startDate: null, platforms: {} };
            setLocalTracker(emptyTracker);
            localTrackerRef.current = emptyTracker;
            storage.setItem('rotaDoLucroLocalTracker', emptyTracker);
        } else {
            if (!silent) showToast('Nenhum ganho válido encontrado para lançar.', 'error');
        }
    }, [localTracker, activeShift, workTimer, getCategories, handleAddQuickTransaction, triggerRemoteSync, setLocalTracker, showToast]);

    // --- AUTO-SYNC REMOTO PARA O SIMULADOR DE METAS E CONFIGS ---
    // Garante que qualquer alteração nas metas ou nas configurações do simulador
    // seja sincronizada com o Firestore após o re-render e a atualização das refs locais.
    const prevGoalsStateRef = useRef({
        updated: goalsLastUpdated,
        period: simulationPeriod,
        daily: simulationDailyAmount,
        daysOff: simulatorDaysOff,
        rideVal: simulatorRideValue,
        pinned: pinnedGoalId
    });

    useEffect(() => {
        if (!isInitialLoadDone || !firebaseUser?.uid) return;

        const prev = prevGoalsStateRef.current;
        const hasChanged =
            goalsLastUpdated !== prev.updated ||
            simulationPeriod !== prev.period ||
            simulationDailyAmount !== prev.daily ||
            simulatorDaysOff !== prev.daysOff ||
            simulatorRideValue !== prev.rideVal ||
            pinnedGoalId !== prev.pinned;

        if (hasChanged) {
            prevGoalsStateRef.current = {
                updated: goalsLastUpdated,
                period: simulationPeriod,
                daily: simulationDailyAmount,
                daysOff: simulatorDaysOff,
                rideVal: simulatorRideValue,
                pinned: pinnedGoalId
            };
            console.log('APP_JS: Alteração no Planejador de Conquistas detectada. Disparando sync remoto...');
            const timer = setTimeout(() => {
                triggerRemoteSync(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [goalsLastUpdated, simulationPeriod, simulationDailyAmount, simulatorDaysOff, simulatorRideValue, pinnedGoalId, isInitialLoadDone, firebaseUser, triggerRemoteSync]);

    // --- AUTO-SYNC REMOTO PARA A FROTA DE VEÍCULOS ---
    // Garante que qualquer alteração nos veículos ou no veículo selecionado
    // seja sincronizada com o Firestore após o re-render e a atualização das refs locais.
    const prevVehiclesStateRef = useRef({
        updated: vehiclesLastUpdated,
        selectedId: selectedVehicleId
    });

    useEffect(() => {
        if (!isInitialLoadDone || !firebaseUser?.uid) return;

        const prev = prevVehiclesStateRef.current;
        const hasChanged =
            vehiclesLastUpdated !== prev.updated ||
            selectedVehicleId !== prev.selectedId;

        if (hasChanged) {
            prevVehiclesStateRef.current = {
                updated: vehiclesLastUpdated,
                selectedId: selectedVehicleId
            };
            console.log('APP_JS: Alteração na frota de veículos detectada. Disparando sync remoto...');
            const timer = setTimeout(() => {
                triggerRemoteSync(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [vehiclesLastUpdated, selectedVehicleId, isInitialLoadDone, firebaseUser, triggerRemoteSync]);

    // --- AUTO-SYNC REMOTO PARA DESPESAS FIXAS + ATRASADOS ---
    // Garante que qualquer criação, edição, pagamento, baixa ou alteração nas despesas fixas
    // E nos atrasados seja sincronizada imediatamente com o Firestore e refletida nos outros dispositivos.
    const prevFixedExpensesStateRef = useRef({
        updated: fixedExpensesLastUpdated,
        pastDueUpdated: pastDueExpensesLastUpdated
    });

    useEffect(() => {
        if (!isInitialLoadDone || !firebaseUser?.uid) return;

        const prev = prevFixedExpensesStateRef.current;
        const hasChanged = fixedExpensesLastUpdated !== prev.updated ||
            pastDueExpensesLastUpdated !== prev.pastDueUpdated;

        if (hasChanged) {
            prevFixedExpensesStateRef.current = {
                updated: fixedExpensesLastUpdated,
                pastDueUpdated: pastDueExpensesLastUpdated
            };
            console.log('APP_JS: Alteração em Despesas Fixas/Atrasados detectada. Disparando sync remoto...');
            const timer = setTimeout(() => {
                triggerRemoteSync(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [fixedExpensesLastUpdated, pastDueExpensesLastUpdated, isInitialLoadDone, firebaseUser, triggerRemoteSync]);

    // --- AUTO-SYNC REMOTO PARA CONFIGS DO PLANEJADOR (metas dinâmicas / fundo manutenção) ---
    // Essas configs ficavam só no aparelho local (nunca subiam para a nuvem).
    // Agora sobem junto com o payload para refletir em todos os dispositivos.
    const prevPlannerConfigRef = useRef({ dyn: null, maint: null, initialized: false });

    useEffect(() => {
        if (!isInitialLoadDone || !firebaseUser?.uid) return;

        const dynSig = JSON.stringify(dynamicGoalsConfigRef.current ?? dynamicGoalsConfig);
        const maintSig = JSON.stringify(maintenanceFundConfigRef.current ?? maintenanceFundConfig);

        if (!prevPlannerConfigRef.current.initialized) {
            prevPlannerConfigRef.current = { dyn: dynSig, maint: maintSig, initialized: true };
            return;
        }

        const prev = prevPlannerConfigRef.current;
        if (dynSig !== prev.dyn || maintSig !== prev.maint) {
            prevPlannerConfigRef.current = { dyn: dynSig, maint: maintSig, initialized: true };
            console.log('APP_JS: Alteração nas configs do Planejador detectada. Disparando sync remoto...');
            const timer = setTimeout(() => {
                triggerRemoteSync(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [dynamicGoalsConfig, maintenanceFundConfig, isInitialLoadDone, firebaseUser, triggerRemoteSync]);

    // --- FUNÇÃO DE LOGOUT SEGURO (Limpa dados locais antes de sair) ---
    const handleLogout = useCallback(async () => {
        console.log('APP_JS: Executando logout seguro, limpando dados...');

        // Marca como deslogado na nuvem antes de limpar local
        if (firebaseUser?.uid) {
            try {
                await triggerRemoteSync(true); // Força um último salvamento antes de sair
                await markUserAsLoggedOut(firebaseUser.uid);
            } catch (err) {
                console.error('APP_JS: Erro ao sincronizar antes do logout:', err);
            }
        }

        // Limpa TODO o storage local e banco de dados IndexedDB de forma segura
        await storage.clear();

        // Reseta estados React para valores iniciais (Garante UI limpa para o próximo login)
        setTransactions([]);
        setVehicles([]);
        setKmRecords({});
        setUserProfile({ name: 'Piloto', photo: null, provider: { name: '', cpf: '', address: '', phone: '', email: '' } });
        setCustomCategories({ income: [], expense: [] });
        setDeletedDefaultCategories({ income: [], expense: [] });
        setFixedExpensesDirectly([]);
        setPastDueExpensesDirectly([]);
        setPastDueTombstones([]);
        pastDueTombstonesRef.current = [];
        storage.removeItem('rotaDoLucroPastDueTombstones');
        setVehicleTombstones([]);
        vehicleTombstonesRef.current = [];
        storage.removeItem('rotaDoLucroVehicleTombstones');
        setFixedExpensesLastUpdated(0);
        setPastDueExpensesLastUpdated(0);
        setScheduledTrips([]);
        setDailyManualStats({});
        setActiveShift(null);
        setActiveShiftKey(null);
        setDailyGoal(300);
        setMonthlyWorkDays(31);
        setMonthlyProfitTarget(3000);
        setManualVarCost(0);
        setManualSavedAmount(0);
        setIsInitialLoadDone(false);
        setLastRemoteSync(0);
        hasSyncedWithFirestoreRef.current = false;

        // Reseta estados adicionais para evitar vazamento visual/lógico
        setSubscription({ plan: 'free', expiresAt: null });
        setIsSubscriptionLoaded(false);
        setLocalTracker({ amount: 0, trips: 0, totalAmount: 0, totalTrips: 0, startDate: null, platforms: {} });
        setGoals([]);
        setPinnedGoalId(null);
        setSimulationPeriod('endOfYear');
        setSimulationDailyAmount(0);
        setSimulatorDaysOff(1);
        setSimulatorRideValue(25);
        setHideAdminPanel(false);

        // Chama o logout do Firebase
        onLogout();
    }, [firebaseUser, triggerRemoteSync, onLogout, setTransactions, setVehicles, setKmRecords, setUserProfile, setCustomCategories, setDeletedDefaultCategories, setFixedExpenses, setPastDueExpenses, setScheduledTrips, setDailyManualStats, setActiveShift, setActiveShiftKey, setDailyGoal, setMonthlyWorkDays, setMonthlyProfitTarget, setManualVarCost, setManualSavedAmount, setIsInitialLoadDone, setLastRemoteSync, setSubscription, setIsSubscriptionLoaded, setLocalTracker, setGoals, setPinnedGoalId, setSimulationPeriod, setSimulationDailyAmount, setSimulatorDaysOff, setSimulatorRideValue, setHideAdminPanel]);

    const handleDeleteAccount = useCallback(async () => {
        if (!firebaseUser) return;

        setConfirmModal({
            type: 'deleteAccount',
            title: 'Excluir Conta?',
            message: 'ATENÇÃO: Isso excluirá permanentemente sua conta, assinaturas e todos os seus dados salvos. Esta ação NÃO pode ser desfeita. Deseja continuar?',
            confirmText: 'Excluir Definitivamente',
            onConfirm: async () => {
                setConfirmModal({
                    type: 'deleteAccountFinal',
                    title: 'Último Aviso!',
                    message: 'Todos os seus registros financeiros, veículos e agenda serão apagados para sempre. Confirma a exclusão definitiva?',
                    confirmText: 'Sim, Apagar Tudo',
                    onConfirm: async () => {
                        setConfirmModal(null);
                        try {
                            showToast("Excluindo sua conta e dados...", "info");
                            await deleteUserAccount(firebaseUser.uid);
                            const { deleteUser } = await import('firebase/auth');
                            await deleteUser(firebaseUser);
                            showToast("Sua conta foi excluÃ­da com sucesso.", "success");
                            handleLogout();
                        } catch (error) {
                            console.error("Erro ao excluir conta:", error);
                            if (error.code === 'auth/requires-recent-login') {
                                showToast("Para sua segurança, esta ação exige um login recente. Por favor, saia e entre novamente antes de excluir.", "error");
                            } else {
                                showToast("Erro ao excluir conta: " + error.message, "error");
                            }
                        }
                    }
                });
            }
        });
    }, [firebaseUser, handleLogout, showToast]);

    const handleClearAllData = useCallback(async () => {
        if (!firebaseUser) return;
        setConfirmModal({
            type: 'clearAllData',
            title: 'Apagar Todos os Dados?',
            message: 'Isso apagará permanentemente todos os seus registros: corridas, gastos, KM, metas e veículos. Sua CONTA será mantida. Esta ação NÃO pode ser desfeita.',
            confirmText: 'Sim, Apagar Dados',
            onConfirm: async () => {
                setConfirmModal({
                    type: 'clearAllDataFinal',
                    title: 'Último Aviso!',
                    message: 'Confirma a exclusão PERMANENTE de todos os seus dados financeiros? Sua conta permanecerá ativa.',
                    confirmText: 'Confirmar, Apagar Tudo',
                    onConfirm: async () => {
                        setConfirmModal(null);
                        try {
                            showToast('Apagando todos os dados...', 'info');
                            await clearUserDataRemote(firebaseUser.uid);
                            // Limpa storage local
                            const keysToRemove = [
                                'rotaDoLucroData', 'rotaDoLucroKm', 'rotaDoLucroDailyManualStats',
                                'rotaDoLucroVehicles', 'rotaDoLucroFixedExpenses', 'rotaDoLucroPastDueExpenses',
                                'rotaDoLucroScheduledTrips', 'rotaDoLucroGoals', 'rotaDoLucroGoal',
                                'rotaDoLucroCustomCategories', 'rotaDoLucroActiveShift', 'rotaDoLucroActiveShiftKey',
                                'rotaDoLucroPastDueTombstones', 'earnings_tracker_local',
                            ];
                            keysToRemove.forEach(k => storage.removeItem(k));
                            // Reseta estados em memória
                            setTransactions([]);
                            transactionsRef.current = [];
                            setKmRecords({});
                            kmRecordsRef.current = {};
                            setDailyManualStats({});
                            dailyManualStatsRef.current = {};
                            setActiveShift(null);
                            setActiveShiftKey(null);
                            activeShiftRef.current = null;
                            activeShiftKeyRef.current = null;
                            showToast('Todos os dados foram apagados. Sua conta continua ativa.', 'success');
                        } catch (error) {
                            console.error('Erro ao apagar dados:', error);
                            showToast('Erro ao apagar dados: ' + error.message, 'error');
                        }
                    }
                });
            }
        });
    }, [firebaseUser, showToast]);


    // --- SINCRONIZAÇÃO AUTOMÁTICA (UPLOAD / BACKGROUND) ---
    useEffect(() => {
        if (!firebaseUser?.uid || !isInitialLoadDone) return;

        // 1. Sincronização periódica (a cada 5 minutos)
        const activeSyncTimer = setInterval(() => {
            const now = Date.now();
            if (now - lastRemoteSync > 5 * 60 * 1000) {
                console.log('APP_JS: Auto-sync periódico (5 min)...');
                triggerRemoteSync();
            }
        }, 60 * 1000);

        // 2. Sincronização ao minimizar/trocar app (Mobile/Desktop)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                console.log('APP_JS: App minimizado. Forçando sincronização imediata...');
                triggerRemoteSync(true);
            }
        };

        // 3. Sincronização ao fechar aba/navegador (Web)
        const handleBeforeUnload = () => {
            console.log('APP_JS: App fechando. Salvando tudo...');
            triggerRemoteSync(true);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            clearInterval(activeSyncTimer);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [firebaseUser, isInitialLoadDone, lastRemoteSync, triggerRemoteSync]);


    // --- NOVA LÓGICA: SINCRONIZAÇÃO EM TEMPO REAL COM ONSNAPSHOT ---
    useEffect(() => {
        if (!firebaseUser?.uid || !isStorageReady || !isLocalDataLoaded || !isFirstRunChecked) return;

        console.log(`APP_JS: Inicializando listener onSnapshot para o usuário ${firebaseUser.uid}...`);

        const userDocRef = doc(db, 'user_data', firebaseUser.uid, 'private', 'main');

        const unsubscribe = onSnapshot(userDocRef, async (docSnap) => {
            if (docSnap.exists()) {
                const remoteData = docSnap.data();
                console.log('APP_JS: Snapshot recebido do Firestore.');
                // --- NOVIDADE: Verifica e aplica cupom de indicação pendente (Google Login / Registro) ---
                const pendingCoupon = storage.getItemSync('pending_coupon');
                if (pendingCoupon) {
                    try {
                        console.log('APP_JS: Aplicando cupom pendente detectado no login...');
                        const { redeemCoupon } = await import('./services/dbService');
                        const result = await redeemCoupon(firebaseUser.uid, pendingCoupon);
                        storage.removeItem('pending_coupon');

                        if (result.success) {
                            const discount = result.discount || result.discountPercentage || 0;
                            const extraDays = result.extraDays || 0;
                            let msg = `Parabéns! Você ganhou ${discount}% de desconto`;
                            if (extraDays > 0) msg += ` e mais ${extraDays} dias de teste!`;
                            else msg += '!';
                            showToast(msg, 'success');
                        }
                    } catch (err) {
                        console.error('APP_JS: Erro ao aplicar cupom pendente:', err);
                        storage.removeItem('pending_coupon');
                    }
                }

                // --- NOVIDADE: Verifica e aplica dados de rastreamento UTM pendentes ---
                const pendingUtm = storage.getItemSync('pending_utm');
                const currentAcq = remoteData.acquisition || {};
                const hasNoRealSource = !currentAcq.source && !currentAcq.gclid && !currentAcq.fbclid;

                if (pendingUtm && hasNoRealSource) {
                    try {
                        console.log('APP_JS: Salvando dados de rastreamento pendentes no Firestore...');
                        const { saveUserData } = await import('./services/dbService');
                        await saveUserData(firebaseUser.uid, { acquisition: pendingUtm });
                        storage.removeItem('pending_utm');
                        console.log('APP_JS: Rastreamento UTM salvo com sucesso.');
                    } catch (err) {
                        console.error('APP_JS: Erro ao salvar dados de rastreamento:', err);
                    }
                }

                // --- NOVIDADE: Verifica e aplica dados de campanha de marketing pendentes ---
                const pendingCampaign = storage.getItemSync('pending_campaign');
                const hasNoCampaign = !remoteData.referredByCampaign;

                if (pendingCampaign && hasNoCampaign) {
                    try {
                        console.log('APP_JS: Salvando dados de campanha pendentes no Firestore...');
                        // 1. Vincula a campanha no perfil do usuário
                        const { saveUserData } = await import('./services/dbService');
                        await saveUserData(firebaseUser.uid, { referredByCampaign: pendingCampaign });

                        // 2. Incrementa conversões no documento da campanha
                        const campRef = doc(db, 'marketing_links', pendingCampaign);
                        const campSnap = await getDoc(campRef);
                        if (campSnap.exists()) {
                            await updateDoc(campRef, {
                                conversionsCount: increment(1)
                            });
                        }

                        storage.removeItem('pending_campaign');
                        console.log('APP_JS: Campanha de marketing salva com sucesso.');
                    } catch (err) {
                        console.error('APP_JS: Erro ao salvar campanha de marketing:', err);
                    }
                }

                if (remoteData) {
                    // 1. Verificação de Bloqueio (Segurança Máxima)
                    if (remoteData.isBlocked) {
                        showToast('Seu acesso foi suspenso. Entre em contato com o administrador.', 'error');
                        handleLogout();
                        return;
                    }

                    // 1.1 Comandos Remotos Administrativos (Suporte à Distância)
                    if (remoteData.remoteCommand && remoteData.remoteCommand.status !== 'completed') {
                        const cmd = remoteData.remoteCommand;
                        const cmdAction = cmd.action;
                        const cmdId = String(cmd.id || cmd.timestamp || cmd.requestedAt);
                        const lastCmd = storage.getItemSync('last_handled_remote_cmd');

                        if (lastCmd !== cmdId) {
                            storage.setItem('last_handled_remote_cmd', cmdId);

                            try {
                                if (cmdAction === 'FORCE_RESYNC') {
                                    sessionStorage.clear();
                                    await completeRemoteCommand(firebaseUser.uid, cmdId, 'completed');
                                    showToast('Sincronização forçada realizada. O app será reiniciado...', 'info');
                                    setTimeout(() => window.location.reload(), 1500);
                                } else if (cmdAction === 'DUMP_DIAGNOSTICS') {
                                    const distributionInfo = await getAppDistributionInfo();
                                    const diagPayload = {
                                        userId: firebaseUser.uid,
                                        userEmail: firebaseUser.email,
                                        appVersion: APP_VERSION,
                                        platform: getPlatform(),
                                        isNative: isNativeApp(),
                                        isPWA: isPWA(),
                                        distributionChannel: distributionInfo.distributionChannel,
                                        appId: distributionInfo.appId,
                                        timestamp: new Date().toISOString(),
                                        userAgent: navigator.userAgent,
                                        screen: `${window.innerWidth}x${window.innerHeight}`,
                                        online: navigator.onLine
                                    };
                                    const { setDoc, doc } = await import('firebase/firestore');
                                    await setDoc(doc(db, 'admin_logs', 'diagnostics', 'items', `${firebaseUser.uid}_${Date.now()}`), diagPayload);

                                    await completeRemoteCommand(firebaseUser.uid, cmdId, 'completed');
                                    showToast('Diagnóstico do aparelho enviado ao suporte.', 'info');
                                } else if (cmdAction === 'FORCE_LOGOUT') {
                                    await completeRemoteCommand(firebaseUser.uid, cmdId, 'completed');
                                    showToast('Sua sessão foi encerrada pelo suporte administrativo.', 'warning');
                                    await handleLogout();
                                    return;
                                } else {
                                    await completeRemoteCommand(firebaseUser.uid, cmdId, 'failed', `Ação desconhecida: ${cmdAction}`);
                                }
                            } catch (e) {
                                console.warn('APP_JS: Erro ao executar comando remoto:', e);
                                await completeRemoteCommand(firebaseUser.uid, cmdId, 'failed', e?.message || 'Erro ao executar comando');
                                showToast('Não foi possível executar o comando remoto.', 'error');
                            }
                        } else if (cmd.status !== 'failed') {
                            // Comando já executado neste aparelho, mas o status ficou pendente
                            // em versões anteriores (gravação com dot-notation via set()).
                            await completeRemoteCommand(firebaseUser.uid, cmdId, 'completed');
                        }
                    }

                    // 2. Cálculo Unificado de Assinatura
                    const calculateFinalSubscription = () => {
                        const subData = remoteData.subscription || {};
                        const isTrialFromRoot = remoteData.isTrial === true;
                        const plan = subData.plan || remoteData.plan || (isTrialFromRoot ? 'trial' : 'free');
                        const expiresAt = subData.expiresAt || remoteData.expiresAt || null;
                        const isTrial = subData.isTrial === true || isTrialFromRoot;

                        const sub = { ...subData, plan, expiresAt, isTrial };
                        // PRIORIDADE: Se o Firestore já tem um plano definido (mesmo que seja 'free' por estorno), respeitamos.
                        // Só concedemos Trial se o campo estiver totalmente ausente ou for a primeira vez.
                        if (sub && sub.plan && sub.plan !== 'free' && sub.plan !== 'trial') {
                            return sub;
                        }

                        const creationTime = new Date(firebaseUser.metadata.creationTime || firebaseUser.metadata.lastSignInTime || Date.now()).getTime();
                        const nowTotal = Date.now();

                        const parseDate = (val) => {
                            if (!val) return new Date(0);
                            if (val.toDate) return val.toDate();
                            if (typeof val === 'number') {
                                if (val > 100000000000) return new Date(val);
                                return new Date(val * 1000);
                            }
                            return new Date(String(val).replace(' ', 'T'));
                        };

                        // Lógica de Cupons com Dias Extras (Prioridade para bônus ativos)
                        const appliedCoupons = remoteData?.appliedCoupons || [];
                        let couponTrialExp = null;
                        appliedCoupons.forEach(c => {
                            if (c.extraDays > 0) {
                                const appliedTime = c.appliedAt ? new Date(c.appliedAt).getTime() : (c.expiresAt ? (new Date(c.expiresAt).getTime() - (c.extraDays * 24 * 60 * 60 * 1000)) : creationTime);
                                const cExp = appliedTime + (c.extraDays * 24 * 60 * 60 * 1000);
                                if (!couponTrialExp || cExp > couponTrialExp) {
                                    couponTrialExp = cExp;
                                }
                            }
                        });

                        if (couponTrialExp && nowTotal < couponTrialExp && (!sub || !sub.plan || sub.plan === 'trial' || sub.plan === 'free' || sub.isTrial)) {
                            return { ...sub, plan: 'premium', planId: sub?.planId || 'trial_7_days', expiresAt: couponTrialExp, isTrial: true };
                        }

                        // Lógica de Trial (Usuários em período de teste gratuito ou sem plano pago)
                        const baseTrialDays = Number(appSettings?.defaultTrialDays) || getDefaultTrialDays() || 7;
                        const extraDaysFromCoupons = appliedCoupons.reduce((sum, c) => sum + (c.extraDays || 0), 0);
                        const trialDays = baseTrialDays + extraDaysFromCoupons;
                        const trialMillis = trialDays * 24 * 60 * 60 * 1000;
                        const trialExpiresAt = creationTime + trialMillis;

                        // Se o usuário ainda está dentro do período de teste gratuito global
                        if (nowTotal < trialExpiresAt && (!sub || !sub.plan || sub.plan === 'trial' || sub.plan === 'free' || sub.isTrial)) {
                            return { ...sub, plan: 'premium', planId: sub?.planId || 'trial_days', expiresAt: trialExpiresAt, isTrial: true };
                        }

                        if (sub && sub.expiresAt) {
                            const end = parseDate(sub.expiresAt);
                            if (nowTotal > end.getTime()) return { ...sub, plan: 'free', isExpired: true };
                            return sub; // Se não expirou e tem data, mantém o que está no Firestore
                        }

                        return sub || { plan: 'free' };
                    };

                    const finalSub = calculateFinalSubscription();
                    setSubscription(finalSub);
                    setIsSubscriptionLoaded(true);

                    // 3. Atualização de Perfil (Parceiro, Nome, etc)
                    if (remoteData.userProfile || remoteData.isPartner !== undefined) {
                        const nowIsPartner = remoteData.isPartner || false;
                        const prevIsPartner = userProfile?.isPartner || false;

                        if (nowIsPartner && !prevIsPartner) {
                            showToast('Modo Parceiro Ativado!', 'info');
                        }

                        console.log('[DEBUG] Snapshot Recebido (isPartner):', nowIsPartner);
                        setUserProfile(prev => {
                            // BUG FIX: Preserva o nome local se o usuário já o alterou.
                            // O nome remoto só sobrescreve se o local ainda for o padrão ('Piloto' ou vazio).
                            // Isso evita que o snapshot do Firestore reverta edições recentes ainda não sincronizadas.
                            const localName = prev?.name;
                            const remoteName = remoteData.userProfile?.name;
                            const localNameIsDefault = !localName || localName === 'Piloto';
                            const resolvedName = localNameIsDefault ? (remoteName || localName) : localName;

                            return {
                                ...prev,
                                ...remoteData.userProfile,
                                name: resolvedName, // Garante que o nome local não seja sobrescrito
                                isPartner: nowIsPartner,
                                partnerCoupon: remoteData.partnerCoupon || null,
                                referralCode: remoteData.referralCode || null
                            };
                        });
                    }
                    if (remoteData.hideAdminPanel !== undefined) setHideAdminPanel(remoteData.hideAdminPanel);

                    // --- SINCRONIZAÇÃO E RESTAURAÇÃO DE DADOS ---

                    // 4. Restauração/Sincronização de Dados (Lógica de First Run ou Merge necessário)
                    if (!hasSyncedWithFirestoreRef.current) {
                        const localTransactions = transactionsRef.current || [];
                        const localVehicles = vehiclesRef.current || [];
                        const localKm = kmRecordsRef.current || {};

                        // Assinatura estável p/ comparar conteúdo entre aparelhos (tolerante a ordem de chaves).
                        // Usada no merge de metas e veículos p/ detectar edições mesmo com relógios dessincronizados.
                        const stableSig = (o) => JSON.stringify(o, (k, val) => (val && typeof val === 'object' && !Array.isArray(val)) ? Object.keys(val).sort().reduce((a, kk) => { a[kk] = val[kk]; return a; }, {}) : val);
                        const SYNC_SKEW_TOLERANCE = 5 * 60 * 1000; // 5min de tolerância p/ relógio celular x PC

                        const hasSignificantLocalData = (
                            localTransactions.length > 2 ||
                            localVehicles.length > 0 ||
                            Object.keys(localKm).length > 0
                        );

                        const cloudTransactions = remoteData.transactions || [];
                        const cloudVehicles = remoteData.vehicles || [];
                        const cloudTxCount = remoteData.transactionsCount || cloudTransactions.length || 0;

                        const cloudHasData = cloudTxCount > 0 || cloudVehicles.length > 0 || (remoteData.fixedExpenses && remoteData.fixedExpenses.length > 0) || (remoteData.goals && remoteData.goals.length > 0) || (remoteData.scheduledTrips && remoteData.scheduledTrips.length > 0) || (remoteData.pastDueExpenses && remoteData.pastDueExpenses.length > 0);

                        const cloudHasMoreData = (
                            cloudTxCount > localTransactions.length ||
                            cloudVehicles.length > localVehicles.length
                        );

                        // LÓGICA DE DECISÃO DE RESTAURAÇÃO:
                        // A. Se a nuvem tem MUITO mais dados e o local está quase vazio -> AUTO-RESTORE (Novo Dispositivo)
                        // B. Se a nuvem tem mais dados e o local tem dados significativos -> PERGUNTAR (Conflito)
                        // C. Se local tem dados e nuvem está vazia -> UPLOAD (Sync Reverso)
                        // D. Caso contrário -> MERGE (Sincroniza apenas configurações)

                        // Ajuste: Aumentamos a tolerância para 3 registros para evitar modais chatos por pequenas falhas de sync de deleção
                        const SIGNIFICANT_DIFF = 3;
                        const hasCloudMoreDataSignificantly = (
                            (cloudTxCount > (localTransactions.length + SIGNIFICANT_DIFF)) ||
                            (cloudVehicles.length > localVehicles.length)
                        );

                        if (hasCloudMoreDataSignificantly && hasSignificantLocalData && localTransactions.length > 0) {
                            console.log('APP_JS: Nuvem tem significativamente mais dados. Exibindo modal de restauração...');
                            setCloudRestoreData({
                                remoteData,
                                cloudStats: { transactions: cloudTxCount, vehicles: cloudVehicles.length },
                                localStats: { transactions: localTransactions.length, vehicles: localVehicles.length }
                            });
                        } else if (cloudHasData && (!hasSignificantLocalData || isFirstRun)) {

                            // AUTO-RESTORE PROATIVO: Se o aparelho está "limpo" ou é a primeira execução
                            console.log('APP_JS: Restaurando dados FULL via Snapshot (Auto-Restore)...');
                            showToast('Sincronizando seus dados da nuvem...', 'info');

                            if (remoteData.transactions && remoteData.transactions.length > 0) {
                                setTransactions(remoteData.transactions);
                            } else if (cloudTxCount > 0) {
                                // Se migrado (V2), busca da subcoleção proativamente
                                fetchTransactionsRemote(firebaseUser.uid).then(txs => {
                                    if (txs && txs.length > 0) {
                                        setTransactions(txs);
                                        transactionsRef.current = txs;
                                        storage.setItem('rotaDoLucroData', txs);
                                    }
                                });
                            }
                            if (remoteData.vehicles) {
                                // Auto-restore (aparelho novo): une frota local + nuvem e
                                // respeita exclusões (tombstones) para não ressuscitar nada.
                                const tombAR = mergeRemoteVehicleTombstones(remoteData.vehicleTombstones);
                                const mergedAR = mergeVehicleLists(
                                    vehiclesRef.current || [],
                                    remoteData.vehicles || [],
                                    true,
                                    tombAR
                                );
                                setVehiclesDirectly(mergedAR);
                                vehiclesRef.current = mergedAR;
                                const mergedTS = Math.max(
                                    Number(remoteData.vehiclesLastUpdated || 0),
                                    Number(vehiclesLastUpdatedRef.current || 0)
                                );
                                if (mergedTS > 0) {
                                    setVehiclesLastUpdated(mergedTS);
                                    vehiclesLastUpdatedRef.current = mergedTS;
                                    storage.setItem('mp_vehicles_lastUpdated', mergedTS);
                                }
                                storage.setItem('rotaDoLucroVehicles', mergedAR);
                            }
                            if (remoteData.kmRecords) setKmRecords(remoteData.kmRecords);
                            if (remoteData.customCategories) setCustomCategories(remoteData.customCategories);
                            if (remoteData.fixedExpenses) {
                                setFixedExpensesDirectly(remoteData.fixedExpenses);
                                fixedExpensesRef.current = remoteData.fixedExpenses;
                                if (remoteData.fixedExpensesLastUpdated) {
                                    setFixedExpensesLastUpdated(remoteData.fixedExpensesLastUpdated);
                                }
                            }
                            if (remoteData.pastDueExpenses) {
                                // Anti-zumbi: une tombstones da nuvem e filtra dívidas já baixadas
                                const tombstonesAR = mergeRemoteTombstones(remoteData.pastDueTombstones);
                                const filteredAR = (remoteData.pastDueExpenses || []).filter(e => !tombstonesAR.includes(e.id));
                                setPastDueExpensesDirectly(filteredAR);
                                pastDueExpensesRef.current = filteredAR;
                                if (remoteData.pastDueExpensesLastUpdated) {
                                    setPastDueExpensesLastUpdated(remoteData.pastDueExpensesLastUpdated);
                                }
                            }
                            if (remoteData.scheduledTrips) setScheduledTrips(remoteData.scheduledTrips);
                            if (remoteData.dailyManualStats) setDailyManualStats(remoteData.dailyManualStats);

                            // Configurações Globais
                            if (remoteData.dailyGoal !== undefined) {
                                const parsed = parseValueBRL(remoteData.dailyGoal) || 0;
                                const sanitized = parsed >= 100000 ? 300 : parsed;
                                setDailyGoal(sanitized);
                                dailyGoalRef.current = sanitized;
                            }
                            if (remoteData.monthlyWorkDays !== undefined) setMonthlyWorkDays(remoteData.monthlyWorkDays);
                            if (remoteData.monthlyProfitTarget !== undefined) setMonthlyProfitTarget(remoteData.monthlyProfitTarget);
                            if (remoteData.manualVarCost !== undefined) setManualVarCost(remoteData.manualVarCost);
                            if (remoteData.manualSavedAmount !== undefined) setManualSavedAmount(remoteData.manualSavedAmount);
                            if (remoteData.manualVarCostType) setManualVarCostType(remoteData.manualVarCostType);
                            if (remoteData.varCostMode) setVarCostMode(remoteData.varCostMode);

                            // Metas e Simulador (Proteção por Timestamp + Barreira de Integridade)
                            const remoteGoalsLastUpdated = Number(remoteData.goalsLastUpdated || 0);
                            const localGoalsLastUpdated = Number(goalsLastUpdatedRef.current || 0);
                            const isRemoteNewer = remoteGoalsLastUpdated > localGoalsLastUpdated;
                            const remoteGoals = Array.isArray(remoteData.goals) ? remoteData.goals : null;
                            const localGoals = Array.isArray(goalsRef.current) ? goalsRef.current : [];

                            if (remoteGoals !== null) {
                                // Só atualiza da nuvem se a nuvem for estritamente mais recente, se o conteúdo
                                // divergir dentro da tolerância de relógio, ou se o dispositivo for recém-instalado.
                                const localSigSet = new Set(localGoals.map(stableSig));
                                const hasGoalsContentDiff = remoteGoals.some(rg => !localSigSet.has(stableSig(rg))) ||
                                    localGoals.some(lg => !remoteGoals.some(rg => stableSig(rg) === stableSig(lg)));
                                const goalsWithinSkew = remoteGoalsLastUpdated > 0 &&
                                    (remoteGoalsLastUpdated + SYNC_SKEW_TOLERANCE >= localGoalsLastUpdated);
                                if (isRemoteNewer || (hasGoalsContentDiff && goalsWithinSkew) || (!localGoals.length && remoteGoals.length > 0 && remoteGoalsLastUpdated > 0 && !localGoalsLastUpdated)) {
                                    setGoalsDirectly(remoteGoals);
                                    if (remoteGoalsLastUpdated > 0) {
                                        setGoalsLastUpdated(remoteGoalsLastUpdated);
                                    }
                                }
                            }

                            if (remoteData.pinnedGoalId !== undefined) setPinnedGoalId(remoteData.pinnedGoalId);
                            if (remoteData.simulationPeriod) setSimulationPeriod(remoteData.simulationPeriod);
                            if (remoteData.simulationDailyAmount !== undefined) setSimulationDailyAmount(remoteData.simulationDailyAmount);
                            // Planejador de Conquistas e referência da virada também vêm da nuvem
                            // no auto-restore (aparelho novo), para não ficar com padrões locais.
                            if (remoteData.dynamicGoalsConfig) {
                                setDynamicGoalsConfig(remoteData.dynamicGoalsConfig);
                                dynamicGoalsConfigRef.current = remoteData.dynamicGoalsConfig;
                                storage.setItem('rotaDoLucroDynamicGoalsConfig', remoteData.dynamicGoalsConfig);
                            }
                            if (remoteData.maintenanceFundConfig) {
                                setMaintenanceFundConfig(remoteData.maintenanceFundConfig);
                                maintenanceFundConfigRef.current = remoteData.maintenanceFundConfig;
                                storage.setItem('rotaDoLucroMaintenanceFundConfig', remoteData.maintenanceFundConfig);
                            }
                            if (remoteData.selectedVehicleId) setSelectedVehicleId(remoteData.selectedVehicleId);
                            if (remoteData.lastRolloverMonth) {
                                setLastRolloverMonth(remoteData.lastRolloverMonth);
                                lastRolloverMonthRef.current = remoteData.lastRolloverMonth;
                                storage.setItem('meupainel-last-rollover-month', remoteData.lastRolloverMonth);
                            }
                        } else {
                            // Merge Inteligente: Garante que configurações críticas (Metas, etc) venham sempre da nuvem
                            console.log('APP_JS: Realizando Merge de configurações via Snapshot...');

                            // --- MIGRAÇÃO / CARREGAMENTO DE TRANSAÇÕES ---
                            if (remoteData.transactions && Array.isArray(remoteData.transactions) && remoteData.transactions.length > 0) {
                                migrateTransactionsToSubcollection(firebaseUser.uid, remoteData.transactions);
                                if (!transactionsRef.current || transactionsRef.current.length === 0) {
                                    setTransactions(remoteData.transactions);
                                }
                            } else if (!hasFetchedRemoteTransactionsRef.current && !isMigratingRef.current) {
                                hasFetchedRemoteTransactionsRef.current = true;
                                fetchTransactionsRemote(firebaseUser.uid).then(txs => {
                                    if (txs && txs.length > 0) {
                                        setTransactions(txs);
                                        transactionsRef.current = txs;
                                        storage.setItem('rotaDoLucroData', txs);
                                    }
                                });
                            } else {
                                if (hasFetchedRemoteTransactionsRef.current && !isMigratingRef.current) {
                                    const remoteLastSynced = remoteData.lastSynced?.toMillis ? remoteData.lastSynced.toMillis() : (typeof remoteData.lastSynced === 'number' ? remoteData.lastSynced : 0);
                                    const localLastSynced = lastRemoteSync || 0;
                                    if (remoteLastSynced > (localLastSynced + 1000)) {
                                        fetchUpdatedTransactionsRemote(firebaseUser.uid, localLastSynced).then(newTxs => {
                                            if (newTxs && newTxs.length > 0) {
                                                setTransactions(prev => {
                                                    const merged = [...prev];
                                                    let updatedCount = 0;
                                                    newTxs.forEach(newTx => {
                                                        const idx = merged.findIndex(t => t.id === newTx.id);
                                                        if (idx !== -1) {
                                                            merged[idx] = newTx;
                                                            updatedCount++;
                                                        } else {
                                                            merged.unshift(newTx);
                                                            updatedCount++;
                                                        }
                                                    });
                                                    if (updatedCount > 0) {
                                                        storage.setItem('rotaDoLucroData', merged);
                                                    }
                                                    return merged;
                                                });
                                            }
                                        });
                                    }
                                }
                            }

                            if (remoteData.dailyGoal !== undefined) {
                                const parsed = parseValueBRL(remoteData.dailyGoal) || 0;
                                const sanitized = parsed >= 100000 ? 300 : parsed;
                                setDailyGoal(sanitized);
                                dailyGoalRef.current = sanitized;
                            }
                            if (remoteData.monthlyProfitTarget !== undefined) setMonthlyProfitTarget(remoteData.monthlyProfitTarget);
                            if (remoteData.monthlyWorkDays !== undefined) setMonthlyWorkDays(remoteData.monthlyWorkDays);
                            if (remoteData.manualVarCost !== undefined) setManualVarCost(remoteData.manualVarCost);
                            if (remoteData.manualSavedAmount !== undefined) setManualSavedAmount(remoteData.manualSavedAmount);
                            if (remoteData.manualVarCostType) setManualVarCostType(remoteData.manualVarCostType);
                            if (remoteData.varCostMode) setVarCostMode(remoteData.varCostMode);

                            const remoteGoalsLastUpdatedM = Number(remoteData.goalsLastUpdated || 0);
                            const localGoalsLastUpdatedM = Number(goalsLastUpdatedRef.current || 0);
                            const isRemoteNewerM = remoteGoalsLastUpdatedM > localGoalsLastUpdatedM;
                            const remoteGoalsM = Array.isArray(remoteData.goals) ? remoteData.goals : null;
                            const localGoalsM = Array.isArray(goalsRef.current) ? goalsRef.current : [];

                            if (remoteGoalsM !== null) {
                                const localMSigSet = new Set(localGoalsM.map(stableSig));
                                const hasGoalsMContentDiff = remoteGoalsM.some(rg => !localMSigSet.has(stableSig(rg))) ||
                                    localGoalsM.some(lg => !remoteGoalsM.some(rg => stableSig(rg) === stableSig(lg)));
                                const goalsMWithinSkew = remoteGoalsLastUpdatedM > 0 &&
                                    (remoteGoalsLastUpdatedM + SYNC_SKEW_TOLERANCE >= localGoalsLastUpdatedM);
                                // Empate sem timestamp (dados legados): prefere a nuvem.
                                const goalsMBothUntimed = !remoteGoalsLastUpdatedM && !localGoalsLastUpdatedM &&
                                    remoteGoalsM.length > 0;
                                if (isRemoteNewerM || (!localGoalsM.length && remoteGoalsM.length > 0 && remoteGoalsLastUpdatedM > 0 && !localGoalsLastUpdatedM) || goalsMBothUntimed) {
                                    setGoalsDirectly(remoteGoalsM);
                                    if (remoteGoalsLastUpdatedM > 0) setGoalsLastUpdated(remoteGoalsLastUpdatedM);
                                }
                            }

                            if (remoteData.pinnedGoalId !== undefined) setPinnedGoalId(remoteData.pinnedGoalId);
                            if (remoteData.simulationPeriod) setSimulationPeriod(remoteData.simulationPeriod);
                            if (remoteData.simulationDailyAmount !== undefined) setSimulationDailyAmount(remoteData.simulationDailyAmount);
                            if (remoteData.simulatorDaysOff !== undefined) setSimulatorDaysOff(remoteData.simulatorDaysOff);
                            if (remoteData.simulatorRideValue !== undefined) setSimulatorRideValue(remoteData.simulatorRideValue);

                            if (remoteData.hideAdminPanel !== undefined) setHideAdminPanel(remoteData.hideAdminPanel);
                            if (remoteData.opCostData) storage.setItem('opCostData', remoteData.opCostData);

                            const remoteVehiclesLastUpdatedM = Number(remoteData.vehiclesLastUpdated || 0);
                            const localVehiclesLastUpdatedM = Number(vehiclesLastUpdatedRef.current || 0);
                            const remoteVehicles = Array.isArray(remoteData.vehicles) ? remoteData.vehicles : null;
                            const localVehiclesL = Array.isArray(vehiclesRef.current) ? vehiclesRef.current : [];

                            if (remoteVehicles) {
                                // MERGE POR UNIÃO: um veículo novo criado no aparelho A precisa
                                // aparecer no aparelho B mesmo que B tenha timestamp local mais
                                // novo (ex: editou odômetro depois). Last-write-wins puro perdia
                                // o veículo novo — e o re-upload de B ainda apagava a nuvem.
                                const tombM = mergeRemoteVehicleTombstones(remoteData.vehicleTombstones);
                                const localVSigSet = new Set(localVehiclesL.map(stableSig));
                                const hasVehiclesContentDiff = remoteVehicles.some(rv => !localVSigSet.has(stableSig(rv))) ||
                                    localVehiclesL.some(lv => !remoteVehicles.some(rv => stableSig(rv) === stableSig(lv)));
                                const preferRemote = remoteVehiclesLastUpdatedM >= localVehiclesLastUpdatedM;
                                const mergedVehicles = mergeVehicleLists(localVehiclesL, remoteVehicles, preferRemote, tombM);
                                const mergedSigSet = new Set(mergedVehicles.map(stableSig));
                                const mergedDiffers = mergedVehicles.length !== localVehiclesL.length ||
                                    localVehiclesL.some(lv => !mergedSigSet.has(stableSig(lv)));
                                if (hasVehiclesContentDiff && mergedDiffers) {
                                    setVehiclesDirectly(mergedVehicles);
                                    vehiclesRef.current = mergedVehicles;
                                    const mergedTS = Math.max(remoteVehiclesLastUpdatedM, localVehiclesLastUpdatedM);
                                    if (mergedTS > 0) {
                                        setVehiclesLastUpdated(mergedTS);
                                        vehiclesLastUpdatedRef.current = mergedTS;
                                        storage.setItem('mp_vehicles_lastUpdated', mergedTS);
                                    }
                                    storage.setItem('rotaDoLucroVehicles', mergedVehicles);
                                } else if (remoteVehiclesLastUpdatedM > 0 && !localVehiclesLastUpdatedM && localVehiclesL.length === 0 && mergedVehicles.length > 0) {
                                    setVehiclesDirectly(mergedVehicles);
                                    vehiclesRef.current = mergedVehicles;
                                    setVehiclesLastUpdated(remoteVehiclesLastUpdatedM);
                                    vehiclesLastUpdatedRef.current = remoteVehiclesLastUpdatedM;
                                    storage.setItem('mp_vehicles_lastUpdated', remoteVehiclesLastUpdatedM);
                                    storage.setItem('rotaDoLucroVehicles', mergedVehicles);
                                }
                            }
                            if ((!localKm || Object.keys(localKm).length === 0) && remoteData.kmRecords) setKmRecords(remoteData.kmRecords);

                            const remoteFixedExpensesLastUpdatedM = Number(remoteData.fixedExpensesLastUpdated || 0);
                            const localFixedExpensesLastUpdatedM = Number(fixedExpensesLastUpdatedRef.current || 0);
                            const isRemoteFixedExpensesNewerM = remoteFixedExpensesLastUpdatedM > localFixedExpensesLastUpdatedM;
                            const remoteFixedExpenses = Array.isArray(remoteData.fixedExpenses) ? remoteData.fixedExpenses : null;
                            const localFixedExpensesL = Array.isArray(fixedExpensesRef.current) ? fixedExpensesRef.current : [];

                            if (remoteFixedExpenses !== null) {
                                const hasRemotePaidDiff = remoteFixedExpenses.some(re => re.paid && !localFixedExpensesL.find(le => le.id === re.id)?.paid);
                                // Detecta edição de conteúdo (nome/valor/dia/parcelas) mesmo com relógios dessincronizados:
                                // compara os campos relevantes item a item. Tolerância de 5min no relógio.
                                const CLOCK_SKEW_TOLERANCE = 5 * 60 * 1000;
                                const sig = (e) => [e.id, e.name, e.amount, e.day, e.frequency, e.weekDay, e.category, e.paid, e.paidDate, e.currentInstallment, e.totalInstallments, e.priority].join('|');
                                const localSigSet = new Set(localFixedExpensesL.map(sig));
                                const hasRemoteContentDiff = remoteFixedExpenses.some(re => !localSigSet.has(sig(re))) ||
                                    localFixedExpensesL.some(le => !remoteFixedExpenses.some(re => sig(re) === sig(le)));
                                const withinSkew = remoteFixedExpensesLastUpdatedM > 0 &&
                                    (remoteFixedExpensesLastUpdatedM + CLOCK_SKEW_TOLERANCE >= localFixedExpensesLastUpdatedM);
                                // Empate sem timestamp nos dois lados (dados legados): prefere a nuvem,
                                // pois o cache local pode ser de um navegador/dispositivo desatualizado.
                                // É isso que impedia "despesas antigas aparecendo do nada".
                                const fixedBothUntimed = !remoteFixedExpensesLastUpdatedM && !localFixedExpensesLastUpdatedM &&
                                    remoteFixedExpenses.length > 0;
                                const shouldAdoptRemote = isRemoteFixedExpensesNewerM ||
                                    (!localFixedExpensesL.length && remoteFixedExpenses.length > 0) ||
                                    (remoteFixedExpensesLastUpdatedM > 0 && !localFixedExpensesLastUpdatedM) ||
                                    fixedBothUntimed ||
                                    hasRemotePaidDiff;

                                if (shouldAdoptRemote) {
                                    setFixedExpensesDirectly(remoteFixedExpenses);
                                    fixedExpensesRef.current = remoteFixedExpenses;
                                    if (remoteFixedExpensesLastUpdatedM > 0) setFixedExpensesLastUpdated(remoteFixedExpensesLastUpdatedM);
                                }
                            }
                            // Atrasados com relógio lógico próprio: só adota se a nuvem for mais nova
                            // (ou aparelho local vazio). Nunca sobrescreve cegamente.
                            const remotePastDueLastUpdatedM = Number(remoteData.pastDueExpensesLastUpdated || 0);
                            const localPastDueLastUpdatedM = Number(pastDueExpensesLastUpdatedRef.current || 0);
                            const remotePastDue = Array.isArray(remoteData.pastDueExpenses) ? remoteData.pastDueExpenses : null;
                            const localPastDueL = Array.isArray(pastDueExpensesRef.current) ? pastDueExpensesRef.current : [];
                            if (remotePastDue !== null) {
                                const pastDueBothUntimed = !remotePastDueLastUpdatedM && !localPastDueLastUpdatedM &&
                                    remotePastDue.length > 0;
                                const shouldAdoptPastDue = (remotePastDueLastUpdatedM > localPastDueLastUpdatedM) ||
                                    (!localPastDueL.length && remotePastDue.length > 0) ||
                                    (remotePastDueLastUpdatedM > 0 && !localPastDueLastUpdatedM) ||
                                    pastDueBothUntimed;
                                if (shouldAdoptPastDue) {
                                    setPastDueExpensesDirectly(remotePastDue);
                                    pastDueExpensesRef.current = remotePastDue;
                                    if (remotePastDueLastUpdatedM > 0) setPastDueExpensesLastUpdated(remotePastDueLastUpdatedM);
                                }
                            }
                            // Anti-zumbi (merge): une tombstones da nuvem e remove da lista final
                            // qualquer dívida que já foi baixada/excluída em outro aparelho.
                            // É isso que impede a "Prestação do Carro" paga de reaparecer.
                            const mergedTombM = mergeRemoteTombstones(remoteData.pastDueTombstones);
                            if (mergedTombM.length > 0) {
                                const currentPastDue = pastDueExpensesRef.current || [];
                                const cleaned = currentPastDue.filter(e => !mergedTombM.includes(e.id));
                                if (cleaned.length !== currentPastDue.length) {
                                    setPastDueExpensesDirectly(cleaned);
                                    pastDueExpensesRef.current = cleaned;
                                    storage.setItem('rotaDoLucroPastDueExpenses', cleaned);
                                }
                            }
                            // Mês de referência da virada: sincronizado para que um aparelho
                            // desatualizado não gere atrasados duplicados/ressuscitados de meses
                            // que outro aparelho já virou.
                            if (remoteData.lastRolloverMonth && remoteData.lastRolloverMonth > (lastRolloverMonthRef.current || '')) {
                                setLastRolloverMonth(remoteData.lastRolloverMonth);
                                lastRolloverMonthRef.current = remoteData.lastRolloverMonth;
                                storage.setItem('meupainel-last-rollover-month', remoteData.lastRolloverMonth);
                            }
                            // Configurações do Planejador de Conquistas e veículo selecionado:
                            // agora sincronizam junto (antes ficavam só no aparelho local).
                            if (remoteData.dynamicGoalsConfig) {
                                setDynamicGoalsConfig(remoteData.dynamicGoalsConfig);
                                dynamicGoalsConfigRef.current = remoteData.dynamicGoalsConfig;
                                storage.setItem('rotaDoLucroDynamicGoalsConfig', remoteData.dynamicGoalsConfig);
                            }
                            if (remoteData.maintenanceFundConfig) {
                                setMaintenanceFundConfig(remoteData.maintenanceFundConfig);
                                maintenanceFundConfigRef.current = remoteData.maintenanceFundConfig;
                                storage.setItem('rotaDoLucroMaintenanceFundConfig', remoteData.maintenanceFundConfig);
                            }
                            if (remoteData.selectedVehicleId && !selectedVehicleId) {
                                setSelectedVehicleId(remoteData.selectedVehicleId);
                            }
                            if (remoteData.customCategories) setCustomCategories(remoteData.customCategories);
                            if (remoteData.scheduledTrips) setScheduledTrips(remoteData.scheduledTrips);
                            if (remoteData.dailyManualStats) setDailyManualStats(remoteData.dailyManualStats);
                            if (!activeShiftKeyRef.current && remoteData.activeShiftKey) setActiveShiftKey(remoteData.activeShiftKey);
                            if (!activeShiftRef.current && remoteData.activeShift) setActiveShift(remoteData.activeShift);
                        }

                        hasSyncedWithFirestoreRef.current = true;
                        storage.setItem('rotaDoLucroInstalled', 'true');
                        setIsFirstRun(false);
                    }

                    // Sincroniza métricas resumidas se estiverem ausentes no Firestore (migrados V2)
                    if (remoteData.transactions === undefined && (remoteData.totalIncome === undefined || remoteData.totalExpense === undefined)) {
                        console.log('APP_JS: Usuário migrado mas sem totais na nuvem. Sincronizando métricas ativamente...');
                        syncUserMetrics();
                    }

                    setLastRemoteSync(Date.now());
                    setIsInitialLoadDone(true);
                }
            } else {
                console.log('APP_JS: Documento inexistente. Inicializando trial com baseTrialDays...');
                const creationTime = new Date(firebaseUser.metadata.creationTime || Date.now()).getTime();
                const baseTrialDays = Number(appSettings?.defaultTrialDays) || getDefaultTrialDays() || 7;
                const trialMillis = baseTrialDays * 24 * 60 * 60 * 1000;
                setSubscription({ 
                    plan: 'premium', 
                    planId: 'trial_days',
                    expiresAt: creationTime + trialMillis, 
                    isTrial: true 
                });
                setIsSubscriptionLoaded(true);
                setIsInitialLoadDone(true);
                hasSyncedWithFirestoreRef.current = true;
                storage.setItem('rotaDoLucroInstalled', 'true');
                setIsFirstRun(false);
            }
        }, (error) => {
            console.error('APP_JS: Erro no listener onSnapshot:', error);
            showToast('Erro ao sincronizar em tempo real.', 'error');
            setIsInitialLoadDone(true); // Evita trava eterna de loading
        });

        return () => unsubscribe();
    }, [firebaseUser?.uid, isStorageReady, isLocalDataLoaded, isFirstRunChecked, appSettings?.defaultTrialDays]);

    // --- CARREGAMENTO INICIAL COMPLETO DO STORAGE ---
    useEffect(() => {
        const loadLocalData = async () => {
            try {
                // 1. Inicializa cache (crucial para Android nativo)
                await storage.initCache();
                setIsStorageReady(true);

                // 2. Carrega todos os dados persistidos paralelamente
                const [
                    data, vehs, profile, theme_val, km, cats,
                    fixed, trips, stats, goal, fallbackGoal, workDays, profitTarget,
                    varCost, saved, varCostType, varMode, pastDue, sub, shiftKey,
                    savedGoals, savedPinnedGoalId, savedPeriod, savedDailyAmount,
                    savedDaysOff, savedRideValue, savedCompactView,
                    deletedCats, selVehId, actShift, opCost, timer,
                    tsVehicles, tsGoals, tsFixedExpenses, tsPastDueExpenses,
                    savedLastRolloverMonth, savedTombstones, savedVehicleTombs
                ] = await Promise.all([
                    storage.getItem('rotaDoLucroData'),
                    storage.getItem('rotaDoLucroVehicles'),
                    storage.getItem('rotaDoLucroProfile'),
                    storage.getItem('rotaDoLucroTheme'),
                    storage.getItem('rotaDoLucroKm'),
                    storage.getItem('rotaDoLucroCustomCategories'),
                    storage.getItem('rotaDoLucroFixedExpenses'),
                    storage.getItem('rotaDoLucroScheduledTrips'),
                    storage.getItem('rotaDoLucroDailyManualStats'),
                    storage.getItem('dailyGoal'),
                    storage.getItem('rotaDoLucroGoal'), // Fallback key
                    storage.getItem('rotaDoLucroWorkDays'),
                    storage.getItem('rotaDoLucroProfitTarget'),
                    storage.getItem('rotaDoLucroManualVarCost'),
                    storage.getItem('rotaDoLucroManualSaved'),
                    storage.getItem('rotaDoLucroManualVarCostType'),
                    storage.getItem('rotaDoLucroVarCostMode'),
                    storage.getItem('rotaDoLucroPastDueExpenses'),
                    storage.getItem('rotaDoLucroSubscription'),
                    storage.getItem('rotaDoLucroActiveShiftKey'),
                    storage.getItem('mp_goalSimulator_goals'),
                    storage.getItem('mp_goalSimulator_pinnedGoalId'),
                    storage.getItem('mp_goalSimulator_period'),
                    storage.getItem('mp_goalSimulator_dailyAmount'),
                    storage.getItem('mp_goalSimulator_daysOff'),
                    storage.getItem('mp_goalSimulator_rideValue'),
                    storage.getItem('mp_goalSimulator_compactView'),
                    storage.getItem('rotaDoLucroDeletedCategories'),
                    storage.getItem('rotaDoLucroSelectedVehicle'),
                    storage.getItem('rotaDoLucroActiveShift'),
                    storage.getItem('opCostData'),
                    storage.getItem('meupainel-work-timer'),
                    storage.getItem('mp_vehicles_lastUpdated'),
                    storage.getItem('mp_goalSimulator_lastUpdated'),
                    storage.getItem('mp_fixedExpenses_lastUpdated'),
                    storage.getItem('mp_pastDueExpenses_lastUpdated'),
                    storage.getItem('meupainel-last-rollover-month'),
                    storage.getItem('rotaDoLucroPastDueTombstones'),
                    storage.getItem('rotaDoLucroVehicleTombstones'),
                ]);

                // 3. Aplica os dados carregados no estado
                if (data && Array.isArray(data)) {
                    const incomeCats = typeof getCategories === 'function' ? getCategories('income') : [];
                    const sanitized = data.map(t => normalizeTransaction(t, incomeCats));
                    setTransactions(sanitized);
                    transactionsRef.current = sanitized;
                    storage.setItem('rotaDoLucroData', sanitized);
                }
                if (vehs && Array.isArray(vehs)) { setVehiclesDirectly(vehs); vehiclesRef.current = vehs; }
                if (profile && typeof profile === 'object') { setUserProfile(prev => ({ ...prev, ...profile })); userProfileRef.current = { ...userProfileRef.current, ...profile }; }
                if (theme_val) setTheme(theme_val);
                if (km && typeof km === 'object') { setKmRecords(km); kmRecordsRef.current = km; }
                if (cats && typeof cats === 'object') { setCustomCategories(cats); customCategoriesRef.current = cats; }
                if (fixed && Array.isArray(fixed)) { setFixedExpensesDirectly(fixed); fixedExpensesRef.current = fixed; }
                if (trips && Array.isArray(trips)) { setScheduledTrips(trips); scheduledTripsRef.current = trips; }
                if (stats && typeof stats === 'object') { setDailyManualStats(stats); dailyManualStatsRef.current = stats; }

                if (tsVehicles !== null) { setVehiclesLastUpdated(tsVehicles); }
                if (tsGoals !== null) { setGoalsLastUpdated(tsGoals); }
                if (tsFixedExpenses !== null) { setFixedExpensesLastUpdated(tsFixedExpenses); }
                if (tsPastDueExpenses !== null) { setPastDueExpensesLastUpdated(tsPastDueExpenses); }
                if (savedLastRolloverMonth) {
                    setLastRolloverMonth(savedLastRolloverMonth);
                    lastRolloverMonthRef.current = savedLastRolloverMonth;
                }
                if (Array.isArray(savedTombstones) && savedTombstones.length > 0) {
                    const capped = savedTombstones.slice(-1000);
                    setPastDueTombstones(capped);
                    pastDueTombstonesRef.current = capped;
                }
                if (Array.isArray(savedVehicleTombs) && savedVehicleTombs.length > 0) {
                    const cappedV = savedVehicleTombs.map(String).slice(-1000);
                    setVehicleTombstones(cappedV);
                    vehicleTombstonesRef.current = cappedV;
                }
                const actualGoal = goal !== null ? goal : fallbackGoal;
                if (actualGoal !== null && actualGoal !== undefined) {
                    const parsedGoal = parseValueBRL(actualGoal) || 0;
                    // Sanitização: valor absurdo (>= 100.000) indica dado corrompido → reseta para padrão
                    const sanitizedGoal = parsedGoal >= 100000 ? 300 : parsedGoal;
                    setDailyGoal(sanitizedGoal);
                    dailyGoalRef.current = sanitizedGoal;
                    if (parsedGoal !== sanitizedGoal) {
                        console.warn('[App.js] dailyGoal corrompido detectado e resetado:', parsedGoal, '→', sanitizedGoal);
                        storage.setItem('dailyGoal', sanitizedGoal);
                    }
                }
                if (workDays !== null && workDays !== undefined) {
                    const parsedWorkDays = parseValueBRL(workDays) || 0;
                    setMonthlyWorkDays(parsedWorkDays);
                    monthlyWorkDaysRef.current = parsedWorkDays;
                }
                if (profitTarget !== null && profitTarget !== undefined) {
                    const parsedProfitTarget = parseValueBRL(profitTarget) || 0;
                    setMonthlyProfitTarget(parsedProfitTarget);
                    monthlyProfitTargetRef.current = parsedProfitTarget;
                }
                if (varCost !== null && varCost !== undefined) {
                    const parsedVarCost = parseValueBRL(varCost) || 0;
                    setManualVarCost(parsedVarCost);
                    manualVarCostRef.current = parsedVarCost;
                }
                if (saved !== null && saved !== undefined) {
                    const parsedSaved = parseValueBRL(saved) || 0;
                    setManualSavedAmount(parsedSaved);
                    manualSavedAmountRef.current = parsedSaved;
                }
                if (varCostType) { setManualVarCostType(varCostType); manualVarCostTypeRef.current = varCostType; }
                if (varMode) { setVarCostMode(varMode); varCostModeRef.current = varMode; }
                if (pastDue && Array.isArray(pastDue)) { setPastDueExpensesDirectly(pastDue); pastDueExpensesRef.current = pastDue; }
                if (sub && typeof sub === 'object') setSubscription(sub);
                if (shiftKey) { setActiveShiftKey(shiftKey); activeShiftKeyRef.current = shiftKey; }

                // 4. Aplica metas do simulador
                if (savedGoals && Array.isArray(savedGoals)) setGoalsDirectly(savedGoals);
                if (savedPinnedGoalId !== null) setPinnedGoalId(savedPinnedGoalId);
                if (savedPeriod) setSimulationPeriod(savedPeriod);
                if (savedDailyAmount !== null) setSimulationDailyAmount(Number(savedDailyAmount) || 0);
                if (savedDaysOff !== null) setSimulatorDaysOff(Number(savedDaysOff) || 0);
                if (savedRideValue !== null) setSimulatorRideValue(Number(savedRideValue) || 0);
                if (savedCompactView !== null) setIsCompactViewSettings(!!savedCompactView);

                if (deletedCats && typeof deletedCats === 'object') setDeletedDefaultCategories(deletedCats);
                if (selVehId) setSelectedVehicleId(selVehId);
                if (actShift) setActiveShift(actShift);
                if (timer) setWorkTimer(timer);
                // opCost é carregado pela própria página, mas aquecer o cache aqui ajuda.

                // Restaura o rastreamento ativo da Jornada Pro se houver
                try {
                    await TrackingService.restoreActiveState();
                } catch (trackerErr) {
                    console.error('[App.js] Erro ao restaurar Jornada Pro:', trackerErr);
                }

                console.log('[App.js] Storage local carregado com sucesso.');
            } catch (err) {
                console.error('[App.js] Erro ao carregar storage local:', err);
            } finally {
                // 4. CRÍTICO: ativa as flags para desbloquear persistência e sincronização
                setIsLocalDataLoaded(true);
            }
        };
        loadLocalData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- CARREGAMENTO INICIAL DO FIRESTORE (DOWNLOAD) ---
    // REMOVIDO: loadRemoteData foi substituído pelo useEffect com onSnapshot acima.
    useEffect(() => {
        if (!firebaseUser) {
            setIsInitialLoadDone(true);
        }
    }, [firebaseUser]);

    // --- CÃ LCULO ESTABILIZADO DE EXPIRAÃ‡ÃƒO ---
    const subscriptionStatus = useMemo(() => {
        // Segurança: Enquanto carrega, NÃO liberamos Premium automaticamente.
        // O app usa o que estiver no estado 'subscription' (que pode ser o cache do LocalStorage).
        if (!isInitialLoadDone || !firebaseUser || !isSubscriptionLoaded) {
            const isProbablyPremium = subscription && (subscription.plan === 'premium' || subscription.plan === 'vip');
            // Durante o carregamento, não assumimos que o usuário expirou para evitar "flash" de Modo Leitura.
            // Retornamos isExpired: false e deixamos o isSubscriptionLoaded controlar a visibilidade no Dashboard.
            return { isPremium: isProbablyPremium, isExpired: false, isNotExpired: isProbablyPremium };
        }

        const userIsAdmin = isAdmin(firebaseUser?.uid);

        // Melhora a conversão de data (aceita Timestamp do Firestore, string ISO ou milissegundos)
        const parseDate = (dateVal) => {
            if (!dateVal) return new Date(0);
            if (dateVal.toDate) return dateVal.toDate();
            if (typeof dateVal === 'number') return new Date(dateVal);
            return new Date(String(dateVal).replace(' ', 'T'));
        };

        const end = parseDate(subscription?.expiresAt);
        const isTrial = subscription?.isTrial === true;
        const isNotExpired = subscription?.expiresAt && !isNaN(end.getTime()) && end.getTime() > new Date().getTime();

        const isPremium = subscription?.plan === 'vip' ||
            (subscription?.plan === 'premium' && isNotExpired);

        const expiredFlag = !userIsAdmin && !isPremium;

        return { isPremium, isExpired: expiredFlag, isNotExpired };
    }, [subscription, firebaseUser?.uid, isInitialLoadDone, isSubscriptionLoaded]);

    // Sincroniza o estado global de expiração
    useEffect(() => {
        setIsExpired(subscriptionStatus.isExpired);
    }, [subscriptionStatus.isExpired]);

    // Avaliação do Sistema de Pop-ups (Exibe no máximo 1x ao dia por tipo de aviso para não incomodar)
    useEffect(() => {
        if (!isInitialLoadDone || !firebaseUser || !isSubscriptionLoaded) return;
        if (!subscriptionAlertSettings || subscriptionAlertSettings.enabled === false) return;

        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const todayStr = `${year}-${month}-${day}`;

        // 1. CASO: MODO LEITURA (Assinatura ou Teste Expirado)
        if (subscriptionStatus.isExpired) {
            const lastDismissedReadOnly = storage.getItemSync('last_dismissed_alert_read_only', null);
            if (lastDismissedReadOnly !== todayStr) {
                const timer = setTimeout(() => {
                    setActiveAlert({ type: 'read_only', daysLeft: 0 });
                }, 1800);
                return () => clearTimeout(timer);
            }
            return;
        }

        // 2. CASO: TESTE OU PLANO EM VENCIMENTO
        if (subscription?.expiresAt) {
            const end = subscription.expiresAt.toDate ? subscription.expiresAt.toDate() : new Date(subscription.expiresAt);
            if (!isNaN(end.getTime()) && end.getTime() > now.getTime()) {
                const diffTime = end.getTime() - now.getTime();
                const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                const isTrial = subscription?.isTrial === true;

                if (isTrial) {
                    const warningDays = Number(subscriptionAlertSettings.trialWarningDays) || 3;
                    if (daysLeft <= warningDays) {
                        const lastDismissedTrial = storage.getItemSync('last_dismissed_alert_trial', null);
                        if (lastDismissedTrial !== todayStr) {
                            const timer = setTimeout(() => {
                                setActiveAlert({ type: 'trial_expiring', daysLeft });
                            }, 2200);
                            return () => clearTimeout(timer);
                        }
                    }
                } else if (subscription?.plan === 'premium') {
                    const warningDays = Number(subscriptionAlertSettings.planWarningDays) || 3;
                    if (daysLeft <= warningDays) {
                        const lastDismissedPlan = storage.getItemSync('last_dismissed_alert_plan', null);
                        if (lastDismissedPlan !== todayStr) {
                            const timer = setTimeout(() => {
                                setActiveAlert({ type: 'plan_expiring', daysLeft });
                            }, 2200);
                            return () => clearTimeout(timer);
                        }
                    }
                }
            }
        }
    }, [isInitialLoadDone, firebaseUser, isSubscriptionLoaded, subscription, subscriptionStatus.isExpired, subscriptionAlertSettings]);

    const handleDismissSubscriptionAlert = useCallback(() => {
        if (!activeAlert) return;
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const todayStr = `${year}-${month}-${day}`;

        if (activeAlert.type === 'read_only') {
            storage.setItem('last_dismissed_alert_read_only', todayStr);
        } else if (activeAlert.type === 'trial_expiring') {
            storage.setItem('last_dismissed_alert_trial', todayStr);
        } else if (activeAlert.type === 'plan_expiring') {
            storage.setItem('last_dismissed_alert_plan', todayStr);
        }
        setActiveAlert(null);
    }, [activeAlert]);

    // --- EFEITO DE REDIRECIONAMENTO INICIAL ---
    useEffect(() => {
        if (!isInitialLoadDone || !firebaseUser || initialRedirectDone) return;

        const loadDelay = Date.now() - loadDoneAtRef.current;
        if (loadDelay < 1500) return;

        if (location.pathname === '/login' || location.pathname === '/register') {
            navigate('/');
        }
        setInitialRedirectDone(true);
    }, [isInitialLoadDone, firebaseUser, initialRedirectDone, subscriptionStatus.isExpired, location.pathname, navigate]);

    // --- HOOK DE NOTIFICAÇÃ•ES ---
    const {
        notifications,
        permission,
        requestPermission,
        dismissNotification,
        dismissAll: dismissAllNotifications,
        updateTimerNotification
    } = useNotifications(scheduledTrips, selectedVehicleId);

    // --- HANDLER: Jornada Pro ---
    const handleStartTrackerWithDisclosure = useCallback(async (action) => {
        if (!isNativeApp()) {
            TrackingService.start(action);
            return;
        }

        const accepted = await TrackingService.hasAcceptedDisclosure();
        if (accepted) {
            TrackingService.start(action);
        } else {
            setPendingTrackerAction(() => action);
            setShowLocationDisclosure(true);
        }
    }, [showToast]);

    // --- FUNÇÃO DE RESTAURAÇÃO DA NUVEM ---
    const handleCloudRestore = useCallback(async () => {
        if (!cloudRestoreData?.remoteData) return;
        const rd = cloudRestoreData.remoteData;

        showToast('Restaurando dados da nuvem...', 'info');

        // Se a array de transações está vazia mas o contador diz que tem dados, busca da subcoleção (V2)
        let transactionsToRestore = rd.transactions || [];
        const cloudTxCount = rd.transactionsCount || transactionsToRestore.length || 0;

        if (transactionsToRestore.length === 0 && cloudTxCount > 0) {
            try {
                const fetchedTxs = await fetchTransactionsRemote(firebaseUser.uid);
                if (fetchedTxs && fetchedTxs.length > 0) {
                    transactionsToRestore = fetchedTxs;
                }
            } catch (err) {
                console.error('Erro ao buscar transações durante restore:', err);
            }
        }

        if (transactionsToRestore && transactionsToRestore.length > 0) {
            setTransactions(transactionsToRestore);
            transactionsRef.current = transactionsToRestore;
            storage.setItem('rotaDoLucroData', transactionsToRestore);
        }

        if (rd.vehicles && rd.vehicles.length > 0) {
            const tombRestoreV = mergeRemoteVehicleTombstones(rd.vehicleTombstones);
            const mergedRestoreV = mergeVehicleLists(vehiclesRef.current || [], rd.vehicles, true, tombRestoreV);
            setVehiclesDirectly(mergedRestoreV);
            vehiclesRef.current = mergedRestoreV;
            storage.setItem('rotaDoLucroVehicles', mergedRestoreV);
            const restoreTS = Math.max(Number(rd.vehiclesLastUpdated || 0), Number(vehiclesLastUpdatedRef.current || 0));
            if (restoreTS) {
                setVehiclesLastUpdated(restoreTS);
                vehiclesLastUpdatedRef.current = restoreTS;
                storage.setItem('mp_vehicles_lastUpdated', restoreTS);
            }
        }
        if (rd.kmRecords) { setKmRecords(rd.kmRecords); kmRecordsRef.current = rd.kmRecords; storage.setItem('rotaDoLucroKm', rd.kmRecords); }
        if (rd.customCategories) { setCustomCategories(rd.customCategories); customCategoriesRef.current = rd.customCategories; storage.setItem('rotaDoLucroCustomCategories', rd.customCategories); }
        if (rd.fixedExpenses) { setFixedExpensesDirectly(rd.fixedExpenses); fixedExpensesRef.current = rd.fixedExpenses; storage.setItem('rotaDoLucroFixedExpenses', rd.fixedExpenses); if (rd.fixedExpensesLastUpdated) { setFixedExpensesLastUpdated(rd.fixedExpensesLastUpdated); } }
        if (rd.pastDueExpenses) {
            // Restaura tombstones primeiro e filtra dívidas já baixadas
            const tombRestore = Array.isArray(rd.pastDueTombstones) ? rd.pastDueTombstones.slice(-1000) : [];
            if (tombRestore.length > 0) {
                pastDueTombstonesRef.current = tombRestore;
                setPastDueTombstones(tombRestore);
                storage.setItem('rotaDoLucroPastDueTombstones', tombRestore);
            }
            const tombSetR = new Set(pastDueTombstonesRef.current || []);
            const filteredRestore = (rd.pastDueExpenses || []).filter(e => !tombSetR.has(e.id));
            setPastDueExpensesDirectly(filteredRestore); pastDueExpensesRef.current = filteredRestore; storage.setItem('rotaDoLucroPastDueExpenses', filteredRestore); if (rd.pastDueExpensesLastUpdated) { setPastDueExpensesLastUpdated(rd.pastDueExpensesLastUpdated); }
        }
        if (rd.scheduledTrips) { setScheduledTrips(rd.scheduledTrips); scheduledTripsRef.current = rd.scheduledTrips; storage.setItem('rotaDoLucroScheduledTrips', rd.scheduledTrips); }
        if (rd.dailyManualStats) { setDailyManualStats(rd.dailyManualStats); dailyManualStatsRef.current = rd.dailyManualStats; storage.setItem('rotaDoLucroDailyManualStats', rd.dailyManualStats); }
        if (rd.dailyGoal !== undefined) { const parsedG = parseValueBRL(rd.dailyGoal) || 0; setDailyGoal(parsedG); dailyGoalRef.current = parsedG; storage.setItem('dailyGoal', parsedG); }
        if (rd.monthlyWorkDays !== undefined) { setMonthlyWorkDays(rd.monthlyWorkDays); monthlyWorkDaysRef.current = rd.monthlyWorkDays; storage.setItem('rotaDoLucroWorkDays', rd.monthlyWorkDays); }
        if (rd.monthlyProfitTarget !== undefined) { setMonthlyProfitTarget(rd.monthlyProfitTarget); monthlyProfitTargetRef.current = rd.monthlyProfitTarget; storage.setItem('rotaDoLucroProfitTarget', rd.monthlyProfitTarget); }
        if (rd.manualVarCost !== undefined) { setManualVarCost(rd.manualVarCost); manualVarCostRef.current = rd.manualVarCost; storage.setItem('rotaDoLucroManualVarCost', rd.manualVarCost); }
        if (rd.manualSavedAmount !== undefined) { setManualSavedAmount(rd.manualSavedAmount); manualSavedAmountRef.current = rd.manualSavedAmount; storage.setItem('rotaDoLucroManualSaved', rd.manualSavedAmount); }
        if (rd.manualVarCostType) { setManualVarCostType(rd.manualVarCostType); manualVarCostTypeRef.current = rd.manualVarCostType; storage.setItem('rotaDoLucroManualVarCostType', rd.manualVarCostType); }
        if (rd.varCostMode) { setVarCostMode(rd.varCostMode); varCostModeRef.current = rd.varCostMode; storage.setItem('rotaDoLucroVarCostMode', rd.varCostMode); }
        if (rd.goals) {
            setGoalsDirectly(rd.goals);
            storage.setItem('mp_goalSimulator_goals', rd.goals);
            if (rd.goalsLastUpdated) {
                setGoalsLastUpdated(rd.goalsLastUpdated);
            }
        }
        if (rd.theme) { setTheme(rd.theme); storage.setItem('rotaDoLucroTheme', rd.theme); }

        setCloudRestoreData(null);
        showToast('Dados restaurados da nuvem com sucesso!', 'success');
    }, [cloudRestoreData, firebaseUser?.uid, showToast, mergeRemoteVehicleTombstones, mergeVehicleLists]);

    // --- FUNÇÃO PARA ALTERNAR TELA LIGADA ---
    const toggleKeepScreenOn = useCallback(() => {
        setKeepScreenOn(prev => {
            const newVal = !prev;
            storage.setItem('meupainel-keep-screen-on', newVal);

            // Se o cronômetro estiver rodando, aplica a mudança imediatamente
            if (workTimer && workTimer.isRunning) {
                if (newVal) requestWakeLock();
                else releaseWakeLock();
            }

            return newVal;
        });
    }, [workTimer, requestWakeLock, releaseWakeLock]);

    // --- FUNÇÃO PARA ABRIR EARNINGS TRACKER ---
    const handleOpenEarningsTracker = useCallback(() => {
        setShowEarningsModal(true);
    }, []);

    // --- FUNÇÕES DO CRONÔMETRO ---
    const startWorkTimer = useCallback(() => {
        if (isExpired) {
            handleOpenPremium();
            return;
        }

        if (keepScreenOn) {
            requestWakeLock();
        }
        const now = Date.now();
        setWorkTimer({
            serviceStartTime: now,
            startTime: now,
            elapsedTime: 0,
            isRunning: true,
            isPaused: false,
            offlineElapsed: 0,
            offlinePauseStart: null,
            hasPausedOnce: false
        });

        // NOVO: Persiste a data de início no tracker para lançamentos noturnos (mantém se já existia)
        setLocalTracker(prev => ({ ...prev, startDate: prev?.startDate || now }));

        requestWakeLock();
        showToast('Cronômetro iniciado!', 'success');
    }, [showToast, isExpired, handleOpenPremium, requestWakeLock, setLocalTracker]);

    const pauseWorkTimer = useCallback(() => {
        if (workTimer.isRunning && !workTimer.isPaused) {
            const now = Date.now();
            // Acumula o tempo ativo do segmento atual
            const elapsed = workTimer.elapsedTime + (now - workTimer.startTime);
            setWorkTimer(prev => ({
                ...prev,
                elapsedTime: elapsed,
                isPaused: true,
                offlinePauseStart: now,
                hasPausedOnce: true
            }));
            showToast('Cronômetro pausado', 'info');
        }
    }, [workTimer, showToast]);

    const resumeWorkTimer = useCallback(() => {
        if (workTimer.isPaused) {
            const now = Date.now();
            const pauseDuration = workTimer.offlinePauseStart ? (now - workTimer.offlinePauseStart) : 0;
            setWorkTimer(prev => ({
                ...prev,
                startTime: now,
                isPaused: false,
                offlineElapsed: (prev.offlineElapsed || 0) + pauseDuration,
                offlinePauseStart: null
            }));
            showToast('Cronômetro retomado', 'success');
        }
    }, [workTimer.isPaused, workTimer.offlinePauseStart, showToast]);

    // Função auxiliar para calcular tempos do cronômetro
    const getTimerMetrics = useCallback(() => {
        const now = Date.now();
        // Serviço = desde o início até agora (SEMPRE conta, nunca para)
        const serviceMs = workTimer.serviceStartTime ? (now - workTimer.serviceStartTime) : 0;
        // Offline = tempo acumulado em pausas + pausa atual
        let offlineMs = workTimer.offlineElapsed || 0;
        if (workTimer.isPaused && workTimer.offlinePauseStart) {
            offlineMs += (now - workTimer.offlinePauseStart);
        }
        // Online = serviço - offline
        const onlineMs = Math.max(0, serviceMs - offlineMs);
        return { serviceMs, offlineMs, onlineMs };
    }, [workTimer]);

    // Parar cronômetro COM confirmação (usado pelo botão na UI)
    const stopWorkTimer = useCallback(() => {
        const { serviceMs, offlineMs, onlineMs } = getTimerMetrics();

        const fmtMs = (ms) => {
            const s = Math.floor(ms / 1000);
            const h = Math.floor(s / 3600);
            const m = Math.floor((s % 3600) / 60);
            const sec = s % 60;
            return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
        };
        const serviceDisplay = fmtMs(serviceMs);
        // Horas trabalhadas = sempre o tempo Online (serviço - offline)
        const workHoursToSave = onlineMs / 3600000;
        const serviceHoursToSave = serviceMs / 3600000;
        const offlineHoursToSave = offlineMs / 3600000;

        setConfirmModal({
            type: 'stopTimer',
            title: 'Encerrar Cronômetro?',
            message: `Deseja realmente encerrar? Tempo em serviço: ${serviceDisplay}`,
            onConfirm: () => {
                if (workHoursToSave > 0.002) {
                    let dateKey = activeShift?.startDate;
                    if (!dateKey && workTimer.serviceStartTime) {
                        const d = new Date(workTimer.serviceStartTime);
                        const y = d.getFullYear();
                        const mo = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        dateKey = `${y}-${mo}-${day}`;
                    }
                    if (!dateKey) dateKey = getTodayDateKey();

                    setKmRecords(prev => {
                        const current = prev[dateKey] || { startOdo: null, endOdo: null, manualKm: null, workHours: 0 };
                        return {
                            ...prev,
                            [dateKey]: {
                                ...current,
                                workHours: (current.workHours || 0) + workHoursToSave,
                                serviceHours: (current.serviceHours || 0) + serviceHoursToSave,
                                offlineHours: (current.offlineHours || 0) + offlineHoursToSave,
                                lastUpdated: new Date().toISOString()
                            }
                        };
                    });
                }

                setWorkTimer({
                    serviceStartTime: null,
                    startTime: null,
                    elapsedTime: 0,
                    isRunning: false,
                    isPaused: false,
                    offlineElapsed: 0,
                    offlinePauseStart: null,
                    hasPausedOnce: false
                });

                releaseWakeLock();
                handleSubmitLocalTracker(true); // Lança o tracker silenciosamente ao parar o cronômetro

                showToast(`Encerrado: ${serviceDisplay} em serviço (Salvo)`, 'info');
                setConfirmModal(null);
            }
        });
    }, [showToast, activeShift, workTimer, getTimerMetrics, getTodayDateKey, releaseWakeLock, handleSubmitLocalTracker]);

    // Parar cronômetro SEM confirmação (usado ao encerrar turno)
    const stopWorkTimerForce = useCallback(() => {
        if (workTimer.isRunning) {
            const finalTime = timerDisplay;
            setWorkTimer({
                serviceStartTime: null,
                startTime: null,
                elapsedTime: 0,
                isRunning: false,
                isPaused: false,
                offlineElapsed: 0,
                offlinePauseStart: null,
                hasPausedOnce: false
            });
            releaseWakeLock();
            return finalTime;
        }
        return "00:00:00";
    }, [workTimer, timerDisplay, releaseWakeLock]);

    // --- LISTENER DE NOTIFICAÇÕES (REDIRECIONAMENTO E AÇÕES) ---
    useEffect(() => {
        if (!isNativeApp()) return;

        // Cria canais de notificação (Obrigatório para Android 8.0+)
        const initNotifications = async () => {
            try {
                await LocalNotifications.createChannel({
                    id: 'meupainel-notifications',
                    name: 'Alertas e Lembretes',
                    description: 'Notificações de agenda, missões e lembretes gerais',
                    importance: 3,
                    visibility: 1,
                    sound: 'beep.wav'
                });
                await LocalNotifications.createChannel({
                    id: 'meupainel-timer',
                    name: 'Cronômetro de Turno',
                    description: 'Notificação persistente durante o turno de trabalho',
                    importance: 2,
                    visibility: 1
                });
                console.log('APP_JS: Canais de notificação configurados.');
            } catch (e) {
                console.error('APP_JS: Erro ao configurar canais de notificação:', e);
            }
        };
        initNotifications();

        console.log('APP_JS: Configurando listener de notificações...');
        const listener = LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
            console.log('APP_JS: Notificação interagida:', action);

            const { notification, actionId } = action;

            if (notification.id === 999) {
                if (actionId === 'pause_resume') {
                    if (workTimer.isPaused) {
                        resumeWorkTimer();
                    } else {
                        pauseWorkTimer();
                    }
                } else if (actionId === 'stop') {
                    stopWorkTimer();
                } else {
                    navigate('/');
                }
                return;
            }

            navigate('/agenda');
        });

        return () => {
            listener.remove();
        };
    }, [navigate, workTimer.isPaused, pauseWorkTimer, resumeWorkTimer, stopWorkTimer]);


    // --- VERIFICAÇÃO DE ATUALIZAÇÃO (PLAY STORE OU APK DIRETO) ---
    useEffect(() => {
        const checkForAppUpdate = async () => {
            if (!isNativeApp()) return;

            try {
                // Checa se o app está rodando como APK Direto (distribuição fora da loja)
                const isDirect = await isDirectDistribution();

                if (isDirect) {
                    console.log('APP_JS: Verificando atualizações do APK Direto...');
                    const updateInfo = await checkDirectUpdate();
                    if (updateInfo.hasUpdate) {
                        console.log('APP_JS: Nova versão do APK direto disponível:', updateInfo.latestVersion);
                        setDirectUpdateData(updateInfo);
                    }
                    return;
                }

                // Fluxo padrão para Play Store
                const result = await AppUpdate.getAppUpdateInfo();
                console.log('APP_JS: Verificando atualizações Play Store...', result);

                if (result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
                    console.log('APP_JS: Nova versão disponível na Play Store! Iniciando atualização...');

                    if (result.flexibleUpdateAllowed) {
                        await AppUpdate.startFlexibleUpdate();

                        AppUpdate.addListener('onFlexibleUpdateStateChange', async (state) => {
                            if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
                                showToast('Atualização baixada! O app será reiniciado.', 'success');
                                setTimeout(async () => {
                                    await AppUpdate.completeFlexibleUpdate();
                                }, 2000);
                            }
                        });
                    } else if (result.immediateUpdateAllowed) {
                        await AppUpdate.performImmediateUpdate();
                    }
                }
            } catch (error) {
                console.error('APP_JS: Erro ao verificar atualizações:', error);
            }
        };

        // Executa a verificação 3 segundos após o app carregar
        const timeout = setTimeout(checkForAppUpdate, 3000);
        return () => clearTimeout(timeout);
    }, [showToast]);

    // --- PERSISTÊNCIA DO CRONÔMETRO ---
    useEffect(() => {
        if (isLocalDataLoaded) {
            storage.setItem('meupainel-work-timer', workTimer);
        }
    }, [workTimer, isLocalDataLoaded]);

    // --- INTERVALO DO CRONÔMETRO ---
    // Roda SEMPRE que isRunning (ativo ou pausado), pois o Tempo em Serviço nunca para
    useEffect(() => {
        let interval = null;

        if (workTimer.isRunning) {
            // interval a cada segundo para atualizar o display interno do app
            interval = setInterval(() => {
                const now = Date.now();
                const serviceMs = workTimer.serviceStartTime ? (now - workTimer.serviceStartTime) : 0;
                const hours = Math.floor(serviceMs / 3600000);
                const minutes = Math.floor((serviceMs % 3600000) / 60000);
                const seconds = Math.floor((serviceMs % 60000) / 1000);
                const display = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                setTimerDisplay(display);
            }, 1000);
        } else {
            setTimerDisplay('00:00:00');
        }

        return () => clearInterval(interval);
    }, [workTimer.isRunning, workTimer.serviceStartTime]);

    // Atualiza a notificação nativa apenas quando o status muda (Início, Pausa, Retomada, Parada)
    // Isso economiza bateria e permite que o sistema Android gerencie o tempo "ticking" via 'chronometer: true'
    useEffect(() => {
        if (!isInitialLoadDone) return;

        const { onlineMs } = getTimerMetrics();
        const baseTime = Date.now() - onlineMs;

        updateTimerNotification(workTimer.isRunning, workTimer.isPaused, timerDisplay, baseTime);
    }, [workTimer.isRunning, workTimer.isPaused, isInitialLoadDone]);



    // --- CORREÇÃO 3: EFEITO PARA OUVIR MUDANÇAS NO TEMA DO SISTEMA ---
    useEffect(() => {
        if (theme !== 'system') {
            // Se não for 'system', defina o estado inicial para corresponder
            setSystemPrefersDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
            return;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        // Atualiza o estado inicial
        setSystemPrefersDark(mediaQuery.matches);

        // Handler para a mudança de tema do sistema
        const handleSystemChange = (e) => {
            setSystemPrefersDark(e.matches);
        };

        // Adiciona o listener com suporte a navegadores antigos
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleSystemChange);
        } else {
            mediaQuery.addListener(handleSystemChange);
        }

        // NOVO: Ouvinte do Capacitor pra forçar check quando APP voltar de Minimizado no Celular
        let appStateListenerPromise = null;
        try {
            appStateListenerPromise = CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
                if (isActive) {
                    setSystemPrefersDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
                    // Garante a integridade do rastreamento de geolocalização ao retornar do background
                    try {
                        await TrackingService.restoreActiveState();
                    } catch (err) {
                        console.error('Erro ao restaurar tracker no appStateChange:', err);
                    }
                }
            });
        } catch (err) {
            console.error('Erro ao adicionar listener do CapacitorApp', err);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleSystemChange);
            } else {
                mediaQuery.removeListener(handleSystemChange);
            }
            if (appStateListenerPromise) {
                appStateListenerPromise.then(handle => handle.remove()).catch(() => { });
            }
        };
    }, [theme, setSystemPrefersDark]);

    // --- EFEITO PARA APLICAR DARK MODE NO HTML E META TAGS ---
    useEffect(() => {
        const htmlElement = document.documentElement;

        // Determina se dark mode deve estar ativo
        const shouldBeDark = typeof isDarkModeActive === 'function' 
            ? isDarkModeActive() 
            : (theme === 'dark' ? true : (theme === 'light' ? false : systemPrefersDark));

        // Aplica ou remove a classe dark no HTML
        if (shouldBeDark) {
            htmlElement.classList.add('dark');
        } else {
            htmlElement.classList.remove('dark');
        }

        // Atualiza a meta tag theme-color para o topo do celular/navegador
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
            themeColorMeta.setAttribute('content', shouldBeDark ? '#0f172a' : '#4f46e5');
        }
    }, [theme, systemPrefersDark, isDarkModeActive]);



    // --- EFEITOS (Persistência com localStorage) ---
    // Nota: O carregamento inicial agora é feito pelo AppContext
    useEffect(() => {
        if (!firebaseUser && !isInitialLoadDone) {
            setIsInitialLoadDone(true);
        }
    }, [firebaseUser, isInitialLoadDone]);

    // NOVO: Mostrar Modal de Instalação do App após o login (1x vez)
    useEffect(() => {
        // Só verificamos se o usuário está logado, o carregamento inicial terminou e o cache do storage está pronto
        if (firebaseUser && isInitialLoadDone && isStorageReady) {
            // Não mostramos se for app nativo, se já estiver em modo standalone (PWA instalado e aberto)
            // ou se o usuário já dispensou permanentemente
            if (!isNativeApp() && !isPwaInstalled) {
                const hasSeenPrompt = storage.getItemSync('hasSeenInstallPromptAfterLogin', false);
                const isPermanentlyDismissed = storage.getItemSync('pwaInstallPermanentlyDismissed', false);

                if (!hasSeenPrompt && !isPermanentlyDismissed) {
                    // Atrasa 2.5s para não sobrepor outras verificações (ex: premium)
                    const timer = setTimeout(() => {
                        setShowPwaModal(true);
                        // Marcamos como visto imediatamente ao agendar a exibição
                        storage.setItem('hasSeenInstallPromptAfterLogin', true);
                    }, 2500);
                    return () => clearTimeout(timer);
                }
            }
        }
    }, [firebaseUser, isInitialLoadDone, isStorageReady, isPwaInstalled]);

    // Nota: O salvamento local agora é feito pelo AppContext de forma centralizada

    useEffect(() => {
        if (vehicles.length > 0) {
            const exists = vehicles.find(v => String(v.id) === String(selectedVehicleId));
            if (!selectedVehicleId || !exists) {
                setSelectedVehicleId(vehicles[0].id);
            }
        } else {
            setSelectedVehicleId(null);
        }
    }, [vehicles, selectedVehicleId]);



    // --- LÓGICA DO PWA (Instalação) ---
    useEffect(() => {
        const checkStandalone = () => {
            return window.matchMedia('(display-mode: standalone)').matches ||
                (window.navigator.standalone) ||
                document.referrer.includes('android-app://') ||
                window.location.search.includes('mode=standalone');
        };

        const isStandalone = checkStandalone();
        setIsPwaInstalled(isStandalone);

        // Se detectarmos que já está em standalone, marcamos a dispensa permanente para não incomodar no futuro
        if (isStandalone) {
            storage.setItem('pwaInstallPermanentlyDismissed', true);
        }

        // Detectar iOS
        const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(ios);

        // Verificar se o evento já foi capturado antecipadamente no index.js
        if (window.__pwaInstallPromptEvent && !isStandalone) {
            console.log('[PWA] Usando evento capturado antecipadamente no index.js');
            setDeferredPrompt(window.__pwaInstallPromptEvent);
            window.__pwaInstallPromptEvent = null; // Limpa para evitar uso duplicado
        }

        const handler = (e) => {
            e.preventDefault();
            console.log('[PWA] beforeinstallprompt event received', e);
            if (!isStandalone) {
                setDeferredPrompt(e);
            }
        };

        window.addEventListener('beforeinstallprompt', handler);

        // Limpar o listener
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, [isStorageReady]);

    // Funções removidas pois agora são tratadas no componente InstallPwaModal




    // Função mantida para possível uso futuro


    // Função mantida para possível uso futuro
    // eslint-disable-next-line no-unused-vars
    const finishWorkDay = () => {
        setShowFinishDayConfirmModal(true);
    };

    const confirmFinishWorkDay = () => {
        setShowFinishDayConfirmModal(false);
        // Usa a chave ativa para carregar o Odômetro Inicial
        const keyToUse = activeShiftKey || getTodayDateKey();
        const record = kmRecords[keyToUse] || { startOdo: null, endOdo: null, manualKm: null, workHours: 0 };

        // --- NOVO: Sincronizar com Cronômetro ao Encerrar ---
        let timerHours = record.workHours > 0 ? record.workHours.toString() : '';
        let timerWasRunning = false;

        if (workTimer.isRunning && !workTimer.isPaused) {
            const now = Date.now();
            const elapsed = workTimer.elapsedTime + (now - workTimer.startTime);
            const h = Math.floor(elapsed / 3600000);
            const m = Math.floor((elapsed % 3600000) / 60000);
            const s = Math.floor((elapsed % 60000) / 1000);

            timerHours = (h + (m / 60) + (s / 3600)).toFixed(2);

            setWorkTimer(prev => ({
                ...prev,
                elapsedTime: elapsed,
                isPaused: true
            }));
            timerWasRunning = true;
        }

        setKmInput({
            isOpen: true,
            type: 'end',
            // O valor inicial sugerido para o Odômetro Final é o último Odômetro Final registrado (se houver)
            tempValue: record.endOdo !== null ? record.endOdo.toString() : '',
            tempStartValue: record.startOdo !== null ? record.startOdo.toString() : (activeShift?.startKm?.toString() || ''),
            tempHours: timerHours,
            timerWasRunning: timerWasRunning
        });
    };

    const getTodayRecord = (dateStr) => {
        const key = dateStr || (activeShiftKey ? activeShiftKey : getTodayDateKey());
        return kmRecords[key] || { startOdo: null, endOdo: null, manualKm: null, workHours: 0 };
    }

    const getTodayKm = (dateStr) => {
        const record = getTodayRecord(dateStr);
        if (record.manualKm !== null && record.manualKm > 0) return record.manualKm;
        if (record.startOdo !== null && record.endOdo !== null && record.endOdo >= record.startOdo) {
            return record.endOdo - record.startOdo;
        }
        return 0;
    };



    const getLatestOdometerReading = useCallback(() => {
        // 1. Check daily records first (most recent)
        const todayKey = getTodayDateKey();
        const todayRecord = kmRecords[todayKey];

        let maxFromRecords = 0;
        if (todayRecord && todayRecord.endOdo !== null) maxFromRecords = todayRecord.endOdo;
        else if (todayRecord && todayRecord.startOdo !== null) maxFromRecords = todayRecord.startOdo;
        else {
            const sortedDates = Object.keys(kmRecords).sort().reverse();
            for (const date of sortedDates) {
                if (kmRecords[date].endOdo !== null) {
                    maxFromRecords = kmRecords[date].endOdo;
                    break;
                }
            }
        }

        // 2. Check Vehicle Manual Odometer
        const currentVehicle = vehicles.find(v => String(v.id) === String(selectedVehicleId)) || vehicles[0];
        const manualOdo = currentVehicle?.currentOdometer || 0;

        // Return the highest value (assuming odometer always increases)
        return Math.max(maxFromRecords, manualOdo);
    }, [kmRecords, vehicles, selectedVehicleId]);

    // Helper para formatar milissegundos em HH:MM:SS
    const formatTime = (milliseconds) => {
        if (!milliseconds) return '00:00:00';
        const hours = Math.floor(milliseconds / 3600000);
        const minutes = Math.floor((milliseconds % 3600000) / 60000);
        const seconds = Math.floor((milliseconds % 60000) / 1000);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // --- HELPERS DO MODAL DE KM ---
    const getKmModalTitle = () => {
        if (kmInput.isShiftStart && kmInput.type === 'manual') return 'Registrar Turno';
        if (kmInput.isShiftStart) return 'Iniciar Turno';
        if (kmInput.type === 'end') return 'Encerrar Turno (Odômetro)';
        if (kmInput.type === 'manual') {
            const [y, m, d] = kmInput.date.split('-');
            return `Registro Manual(${d} / ${m} / ${y})`;
        }
        return 'Registro de KM';
    };

    const getKmModalPlaceholder = () => {
        if (kmInput.isShiftStart && kmInput.type === 'manual') return 'Ex: 150';
        if (kmInput.type === 'start') return 'Ex: 15300 (KM Atual)';
        if (kmInput.type === 'end') {
            return activeShift?.isManual ? 'Ex: 120.5 (KM Rodado)' : 'Ex: 15450 (KM Final)';
        }
        return 'Ex: 120.5 (KM Rodado)';
    };

    // --- HANDLER: Jornada Pro → Salvar stats no registro diário ---
    const handleSaveTrackerStats = (routeDistKm, routeTimeSec, routeDateKey) => {
        if (!routeDistKm || routeDistKm <= 0 || !routeTimeSec || routeTimeSec <= 0) {
            showToast('Rota finalizada (Sem dados ou muito curta).', 'info');
            return;
        }
        const targetDate = routeDateKey || getTodayDateKey();
        const addedHours = routeTimeSec / 3600;

        // Acumula (não sobrescreve) com eventual registro manual já existente no dia
        const prevRec = (kmRecordsRef.current && kmRecordsRef.current[targetDate]) || {};
        const newKm = (prevRec.manualKm || 0) + routeDistKm;
        const newHours = (prevRec.workHours || 0) + addedHours;

        const updated = {
            ...(kmRecordsRef.current || {}),
            [targetDate]: { ...prevRec, manualKm: newKm, workHours: newHours }
        };
        setKmRecords(updated);
        kmRecordsRef.current = updated;
        storage.setItem('rotaDoLucroKm', updated);

        // Espelha em dailyManualStats se já existir entrada no dia (evita divergência de UI)
        if (dailyManualStatsRef.current && dailyManualStatsRef.current[targetDate]) {
            const prevStats = dailyManualStatsRef.current[targetDate] || {};
            const newStats = {
                ...dailyManualStatsRef.current,
                [targetDate]: {
                    ...prevStats,
                    km: (prevStats.km || 0) + routeDistKm,
                    hours: (prevStats.hours || 0) + addedHours
                }
            };
            setDailyManualStats(newStats);
            dailyManualStatsRef.current = newStats;
            storage.setItem('rotaDoLucroDailyManualStats', newStats);
        }

        triggerRemoteSync(true); // Força um sync remoto ao finalizar
        showToast('Jornada Pro: ' + routeDistKm.toFixed(1) + ' km e ' + addedHours.toFixed(1) + 'h registrados com sucesso!', 'success');
    };

    const handleManualKm = useCallback((initialType, fromEarningsTracker = false) => {
        if (isExpired) {
            handleOpenPremium();
            return;
        }
        const isToday = dashboardDate === getTodayDateKey();

        if (!isToday) {
            // Abre o modal de edição do dia diretamente sobre o Dashboard
            setDailyFilter(dashboardDate);
            setShowDailyManualModal(true);
            return;
        }

        if (dashboardDate > getTodayDateKey()) {
            showToast("Não é possível iniciar ou editar registros em datas futuras.", 'info');
            return;
        }

        if (activeShift) {
            // --- NOVO: Sincronizar com Cronômetro ao Encerrar ---
            let timerHours = '';
            let timerWasRunning = false;

            if (workTimer.isRunning && !workTimer.isPaused) {
                // Capturar tempo atual e pausar
                const now = Date.now();
                const elapsed = workTimer.elapsedTime + (now - workTimer.startTime);
                const h = Math.floor(elapsed / 3600000);
                const m = Math.floor((elapsed % 3600000) / 60000);
                const s = Math.floor((elapsed % 60000) / 1000);

                // Converter para horas decimais para o input
                timerHours = (h + (m / 60) + (s / 3600)).toFixed(2);

                // NOVO: Encerrar e resetar o cronômetro (não apenas pausar) para ele sumir do painel
                setWorkTimer({
                    startTime: null,
                    elapsedTime: 0,
                    isRunning: false,
                    isPaused: false
                });
                timerWasRunning = true;
            }

            setKmInput({
                isOpen: true,
                type: 'end',
                tempValue: '',
                tempStartValue: activeShift?.startKm?.toString() || '',
                tempHours: timerHours,
                date: dashboardDate,
                timerWasRunning: timerWasRunning, // Flag para retomar ao cancelar
                fromEarningsTracker: Boolean(fromEarningsTracker)
            });
        } else {
            // Se já houver dados para hoje, carregar para edição
            const todayRecord = kmRecords[dashboardDate];
            const hasTransactionsToday = transactions.some(t => t.date.startsWith(dashboardDate));
            const hasData = (todayRecord && (
                todayRecord.startOdo !== null ||
                todayRecord.endOdo !== null ||
                (todayRecord.manualKm !== null && todayRecord.manualKm > 0) ||
                (todayRecord.workHours > 0)
            )) || (dailyManualStats && dailyManualStats[dashboardDate]) || hasTransactionsToday;

            if (!initialType && (hasData || dashboardDate !== getTodayDateKey())) {
                // Abre o modal detalhado diretamente (sem trocar de view)
                setDailyFilter(dashboardDate);
                setShowDailyManualModal(true);
            } else {
                // Sugerir o último odômetro conhecido
                const lastOdo = getLatestOdometerReading();

                setKmInput({
                    isOpen: true,
                    type: initialType || 'start', // Usa o tipo inicial se passado
                    isShiftStart: true, // Novo registro ("início")
                    tempValue: '',
                    tempHours: '',
                    timerWasRunning: false,
                    date: dashboardDate,
                    forcedType: !!initialType // Flag para saber se veio de um botão direto
                });
            }
        };
    }, [isExpired, handleOpenPremium, dashboardDate, setDailyFilter, setShowDailyManualModal, showToast, activeShift, workTimer, setWorkTimer, setKmInput, kmRecords, transactions, dailyManualStats, getLatestOdometerReading]);

    const saveKmReading = () => {
        if (isExpired) {
            handleOpenPremium();
            return;
        }
        const val = parseFloat(kmInput.tempValue.replace(',', '.'));
        const hours = parseFloat(kmInput.tempHours.replace(',', '.')) || 0;

        // Validação rigorosa para Início/Fim de turno (Odômetro é obrigatório)
        if (kmInput.type !== 'manual') {
            if (isNaN(val) || val < 0) {
                showToast("Valor inválido.", 'error');
                return;
            }
        }

        if (kmInput.type === 'manual') {
            // Se for REGISTRO de turno usando a aba manual (Lançamento final direto)
            if (kmInput.isShiftStart) {
                const targetDate = kmInput.date || getTodayDateKey();

                let newKm;
                setKmRecords(prev => {
                    newKm = {
                        ...prev,
                        [targetDate]: {
                            ...prev[targetDate],
                            manualKm: !isNaN(val) ? val : 0,
                            workHours: hours
                        }
                    };
                    return newKm;
                });
                kmRecordsRef.current = newKm;
                storage.setItem('rotaDoLucroKm', newKm);

                // Se houver stats manuais, sincroniza para evitar conflito de UI
                if (dailyManualStats[targetDate]) {
                    let newStats;
                    setDailyManualStats(prev => {
                        newStats = {
                            ...prev,
                            [targetDate]: {
                                ...prev[targetDate],
                                km: !isNaN(val) ? val : 0,
                                hours: hours > 0 ? hours : prev[targetDate].hours
                            }
                        };
                        return newStats;
                    });
                    dailyManualStatsRef.current = newStats;
                    storage.setItem('rotaDoLucroDailyManualStats', newStats);
                }

                showToast('Registro manual salvo!', 'success');

                // Se salvou manual para o dia do turno ativo, encerra o turno
                if (activeShift && activeShift.startDate === targetDate) {
                    setActiveShift(null);
                    setActiveShiftKey(null);
                    activeShiftRef.current = null;
                    activeShiftKeyRef.current = null;
                    storage.removeItem('rotaDoLucroActiveShift');
                    storage.removeItem('rotaDoLucroActiveShiftKey');
                }

                // Disparar sincronização imediata
                triggerRemoteSync(true);

                setKmInput({ ...kmInput, isOpen: false });
                return;
            }

            // Registro Manual clássico (sem ser início de turno)
            if ((workTimer.isRunning || workTimer.elapsedTime > 0) && !kmInput.ignoreTimer) {
                const currentFormatted = formatTime(workTimer.elapsedTime);
                setConfirmModal({
                    type: 'stopTimer',
                    title: 'Cronômetro Ativo',
                    message: `O cronômetro está marcando ${currentFormatted}. Deseja usar este tempo no registro ? `,
                    cancelText: 'Não, manter digitado',
                    onConfirm: () => {
                        const timerTime = stopWorkTimerForce();
                        let timerHours = 0;
                        if (timerTime) {
                            const [h, m, s] = timerTime.split(':').map(Number);
                            timerHours = h + (m / 60) + (s / 3600);
                        }

                        const targetDate = kmInput.date || getTodayDateKey();

                        let updatedKm;
                        setKmRecords(prev => {
                            updatedKm = {
                                ...prev,
                                [targetDate]: {
                                    ...prev[targetDate],
                                    manualKm: !isNaN(val) ? val : 0,
                                    workHours: timerHours
                                }
                            };
                            return updatedKm;
                        });
                        kmRecordsRef.current = updatedKm;
                        storage.setItem('rotaDoLucroKm', updatedKm);

                        if (dailyManualStats[targetDate]) {
                            let updatedStats;
                            setDailyManualStats(prev => {
                                updatedStats = {
                                    ...prev,
                                    [targetDate]: {
                                        ...prev[targetDate],
                                        km: !isNaN(val) ? val : 0,
                                        hours: timerHours > 0 ? timerHours : prev[targetDate].hours
                                    }
                                };
                                return updatedStats;
                            });
                            dailyManualStatsRef.current = updatedStats;
                            storage.setItem('rotaDoLucroDailyManualStats', updatedStats);
                        }

                        showToast(`Registro salvo com ${timerTime} do cronômetro!`, 'success');
                        triggerRemoteSync(true);

                        setKmInput({ ...kmInput, isOpen: false });
                        setConfirmModal(null);
                    },
                    onCancel: () => {
                        stopWorkTimerForce();
                        const targetDate = kmInput.date || getTodayDateKey();

                        let updatedKm;
                        setKmRecords(prev => {
                            updatedKm = {
                                ...prev,
                                [targetDate]: {
                                    ...prev[targetDate],
                                    manualKm: !isNaN(val) ? val : 0,
                                    workHours: hours
                                }
                            };
                            return updatedKm;
                        });
                        kmRecordsRef.current = updatedKm;
                        storage.setItem('rotaDoLucroKm', updatedKm);

                        if (dailyManualStats[targetDate]) {
                            let updatedStats;
                            setDailyManualStats(prev => {
                                updatedStats = {
                                    ...prev,
                                    [targetDate]: {
                                        ...prev[targetDate],
                                        km: !isNaN(val) ? val : 0,
                                        hours: hours > 0 ? hours : prev[targetDate].hours
                                    }
                                };
                                return updatedStats;
                            });
                            dailyManualStatsRef.current = updatedStats;
                            storage.setItem('rotaDoLucroDailyManualStats', updatedStats);
                        }

                        showToast('Registro manual salvo (Cronômetro parado).', 'info');
                        triggerRemoteSync(true);

                        setKmInput({ ...kmInput, isOpen: false });
                        setConfirmModal(null);
                    }
                });
                return;
            }

            const targetDate = kmInput.date || getTodayDateKey();
            let finalKm;
            setKmRecords(prev => {
                finalKm = {
                    ...prev,
                    [targetDate]: {
                        ...prev[targetDate],
                        manualKm: !isNaN(val) ? val : 0,
                        workHours: hours
                    }
                };
                return finalKm;
            });
            kmRecordsRef.current = finalKm;
            storage.setItem('rotaDoLucroKm', finalKm);

            if (dailyManualStats[targetDate]) {
                let finalStats;
                setDailyManualStats(prev => {
                    finalStats = {
                        ...prev,
                        [targetDate]: {
                            ...prev[targetDate],
                            km: !isNaN(val) ? val : 0,
                            hours: hours > 0 ? hours : prev[targetDate].hours
                        }
                    };
                    return finalStats;
                });
                dailyManualStatsRef.current = finalStats;
                storage.setItem('rotaDoLucroDailyManualStats', finalStats);
            }

            showToast('Registro manual salvo!', 'success');

            if (activeShift && activeShift.startDate === targetDate) {
                setActiveShift(null);
                setActiveShiftKey(null);
                activeShiftRef.current = null;
                activeShiftKeyRef.current = null;
                storage.removeItem('rotaDoLucroActiveShift');
                storage.removeItem('rotaDoLucroActiveShiftKey');
                handleSubmitLocalTracker(true); // Salvar ganhos ao encerrar turno manual
            }

            triggerRemoteSync(true);
            setKmInput({ ...kmInput, isOpen: false });

            setConfirmModal({
                type: 'postShiftAction',
                title: 'Registro Concluído!',
                message: 'Deseja registrar seus ganhos ou gastos deste turno agora?',
                confirmText: 'Sim, agora',
                cancelText: 'Depois',
                onConfirm: () => {
                    setDailyFilter(kmInput.date);
                    setHistoryView('daily');
                    setView('history');
                    setShowDailyManualModal(true);
                    setConfirmModal(null);
                },
                onCancel: () => setConfirmModal(null)
            });
            return;
        }

        if (kmInput.type === 'start') {
            const newShift = { startKm: val, startTime: Date.now(), startDate: kmInput.date, vehicleId: selectedVehicleId };
            setActiveShift(newShift);

            // NOVO: Persiste a data de início no tracker para lançamentos noturnos
            setLocalTracker(prev => ({ ...prev, startDate: newShift.startTime }));
            setActiveShiftKey(kmInput.date);
            activeShiftRef.current = newShift;
            activeShiftKeyRef.current = kmInput.date;
            storage.setItem('rotaDoLucroActiveShift', newShift);
            storage.setItem('rotaDoLucroActiveShiftKey', kmInput.date);

            let newKm;
            setKmRecords(prev => {
                newKm = { ...prev, [kmInput.date]: { ...prev[kmInput.date], startOdo: val } };
                return newKm;
            });
            kmRecordsRef.current = newKm;
            storage.setItem('rotaDoLucroKm', newKm);

            const updatedVehicles = vehiclesRef.current.map(v => String(v.id) === String(selectedVehicleId) ? { ...v, currentOdometer: val } : v);
            const nowTs = Date.now();
            vehiclesRef.current = updatedVehicles;
            vehiclesLastUpdatedRef.current = nowTs;
            setVehicles(updatedVehicles);
            setVehiclesLastUpdated(nowTs);
            storage.setItem('rotaDoLucroVehicles', updatedVehicles);
            storage.setItem('mp_vehicles_lastUpdated', nowTs);

            showToast('Turno iniciado! Bom trabalho.', 'success');
            if (autoStartTimer) { startWorkTimer(); setAutoStartTimer(false); }
            triggerRemoteSync(true);
            setKmInput({ ...kmInput, isOpen: false });

        } else if (kmInput.type === 'end') {
            if (!activeShift) { showToast('Nenhum turno ativo para encerrar.', 'error'); return; }

            const editedStartOdo = parseFloat(kmInput.tempStartValue.replace(',', '.'));
            const finalStartKm = !isNaN(editedStartOdo) ? editedStartOdo : (activeShift.startKm || 0);
            const kmDriven = activeShift.isManual ? val : (val - finalStartKm);

            const updatedVehicles = vehiclesRef.current.map(v => v.id === activeShift.vehicleId ? { ...v, currentOdometer: val } : v);
            const nowTsEnd = Date.now();
            vehiclesRef.current = updatedVehicles;
            vehiclesLastUpdatedRef.current = nowTsEnd;
            setVehicles(updatedVehicles);
            setVehiclesLastUpdated(nowTsEnd);
            storage.setItem('rotaDoLucroVehicles', updatedVehicles);
            storage.setItem('mp_vehicles_lastUpdated', nowTsEnd);

            const shiftDateKey = activeShift.startDate || getTodayDateKey();
            const currentRecord = kmRecords[shiftDateKey] || { startOdo: finalStartKm, manualKm: 0, workHours: 0 };
            const timerTime = stopWorkTimerForce();
            let timerHours = 0;
            if (timerTime) { const [h, m, s] = timerTime.split(':').map(Number); timerHours = h + (m / 60) + (s / 3600); }
            const finalHours = timerHours > 0 ? timerHours : hours;

            const newRecord = { ...currentRecord, startOdo: finalStartKm, endOdo: activeShift.isManual ? currentRecord.endOdo : val, manualKm: (currentRecord.manualKm || 0) + kmDriven, workHours: (currentRecord.workHours || 0) + finalHours, lastUpdated: new Date().toISOString() };
            let finalKm;
            setKmRecords(prev => {
                finalKm = { ...prev, [shiftDateKey]: newRecord };
                return finalKm;
            });
            kmRecordsRef.current = finalKm;
            storage.setItem('rotaDoLucroKm', finalKm);

            if (dailyManualStats[shiftDateKey]) {
                let finalStats;
                setDailyManualStats(prev => {
                    finalStats = { ...prev, [shiftDateKey]: { ...prev[shiftDateKey], km: kmDriven > 0 ? (prev[shiftDateKey].km || 0) + kmDriven : prev[shiftDateKey].km, hours: finalHours > 0 ? (prev[shiftDateKey].hours || 0) + finalHours : prev[shiftDateKey].hours } };
                    return finalStats;
                });
                dailyManualStatsRef.current = finalStats;
                storage.setItem('rotaDoLucroDailyManualStats', finalStats);
            }

            setActiveShift(null);
            setActiveShiftKey(null);
            activeShiftRef.current = null;
            activeShiftKeyRef.current = null;
            storage.removeItem('rotaDoLucroActiveShift');
            storage.removeItem('rotaDoLucroActiveShiftKey');

            handleSubmitLocalTracker(true); // Salvar ganhos ao encerrar o turno (Odômetro)

            triggerRemoteSync(true);
            if (timerTime) showToast(`Turno encerrado. Rodou ${formatKm(kmDriven)} em ${timerTime}.`, 'success');
            else showToast(`Turno encerrado. Rodou ${formatKm(kmDriven)}.`, 'success');

            setKmInput({ ...kmInput, isOpen: false });

            // NOVO: Perguntar se deseja adicionar ganhos/gastos (somente se não veio do Acompanhar Ganhos)
            if (!kmInput.fromEarningsTracker) {
                setConfirmModal({
                    type: 'postShiftAction',
                    title: 'Turno Encerrado!',
                    message: 'Deseja registrar seus ganhos ou gastos deste turno agora?',
                    confirmText: 'Sim, agora',
                    cancelText: 'Depois',
                    onConfirm: () => {
                        setDailyFilter(shiftDateKey);
                        setHistoryView('daily');
                        setView('history');
                        setShowDailyManualModal(true);
                        setConfirmModal(null);
                    },
                    onCancel: () => setConfirmModal(null)
                });
            }
        }
    };

    // --- LÓGICA DO JORNADA PRO (Parar e registrar KM + horas) ---
    const handleStopTracker = useCallback(async () => {
        const routeData = await TrackingService.finishRoute();

        // Integra KM percorrido no resumo do dia
        if (routeData && routeData.distances > 0) {
            const dateKey = routeData.startDateKey || getTodayDateKey();
            const kmFromGps = parseFloat(routeData.distances.toFixed(2));

            setKmRecords(prev => {
                const rec = prev[dateKey] || { startOdo: null, endOdo: null, manualKm: null, workHours: 0 };
                // Só atualiza manualKm se não houver odômetro configurado (evita conflito)
                const shouldUpdateKm = rec.startOdo === null && rec.endOdo === null;
                const updatedRec = shouldUpdateKm
                    ? { ...rec, manualKm: (rec.manualKm || 0) + kmFromGps }
                    : rec;
                const updated = { ...prev, [dateKey]: updatedRec };
                storage.setItem('rotaDoLucroKm', updated);
                kmRecordsRef.current = updated;
                return updated;
            });
        }

        // NOVO: Lançar ganhos acumulados automaticamente se existirem ao encerrar a Jornada Pro
        if (localTracker && Object.keys(localTracker.platforms || {}).length > 0) {
            handleSubmitLocalTracker(true); // silent = true para não mostrar toast redundante
        }

        return routeData;
    }, [localTracker, handleSubmitLocalTracker]);

    // Atualiza os dados de uma plataforma específica no tracker local
    const handleUpdateLocalTracker = (platformId, updates) => {
        setLocalTracker(prev => {
            const currentPlatforms = prev?.platforms || {};
            const currentPlatformData = currentPlatforms[platformId] || {};
            const updatedPlatformData = { ...currentPlatformData, ...updates };
            const newPlatforms = { ...currentPlatforms, [platformId]: updatedPlatformData };

            // Recalcula totais líquidos
            let totalAmt = 0;
            let totalTrp = 0;
            Object.values(newPlatforms).forEach(p => {
                const baseAmt = Math.max(0, (Number(p.amount) || 0) - (p.hasStartingBalance ? (Number(p.startAmount) || 0) : 0));
                const baseTrp = Math.max(0, (Number(p.trips) || 0) - (p.hasStartingBalance ? (Number(p.startTrips) || 0) : 0));
                totalAmt += baseAmt + (p.hasCutoff ? (Number(p.amountAfterCutoff) || 0) : 0);
                totalTrp += baseTrp + (p.hasCutoff ? (Number(p.tripsAfterCutoff) || 0) : 0);
            });

            const newTracker = {
                ...prev,
                amount: totalAmt,
                trips: totalTrp,
                totalAmount: totalAmt,
                totalTrips: totalTrp,
                platforms: newPlatforms,
                startDate: prev?.startDate || (totalAmt > 0 || totalTrp > 0 ? Date.now() : null),
                lastUpdatedAt: Date.now()
            };

            // Salva imediatamente no storage para evitar perda de dados ao fechar o modal
            storage.setItem('rotaDoLucroLocalTracker', newTracker);

            return newTracker;
        });
    };

    // Remove uma plataforma do tracker local
    const handleRemovePlatformFromTracker = (platformId) => {
        setLocalTracker(prev => {
            const currentPlatforms = { ...(prev?.platforms || {}) };
            delete currentPlatforms[platformId];

            let totalAmt = 0;
            let totalTrp = 0;
            Object.values(currentPlatforms).forEach(p => {
                const baseAmt = Math.max(0, (Number(p.amount) || 0) - (p.hasStartingBalance ? (Number(p.startAmount) || 0) : 0));
                const baseTrp = Math.max(0, (Number(p.trips) || 0) - (p.hasStartingBalance ? (Number(p.startTrips) || 0) : 0));
                totalAmt += baseAmt + (p.hasCutoff ? (Number(p.amountAfterCutoff) || 0) : 0);
                totalTrp += baseTrp + (p.hasCutoff ? (Number(p.tripsAfterCutoff) || 0) : 0);
            });

            const newTracker = {
                ...prev,
                amount: totalAmt,
                trips: totalTrp,
                totalAmount: totalAmt,
                totalTrips: totalTrp,
                platforms: currentPlatforms
            };

            // Salva imediatamente no storage para evitar perda de dados
            storage.setItem('rotaDoLucroLocalTracker', newTracker);

            return newTracker;
        });
        showToast('Plataforma removida do turno.', 'info');
    };

    // --- NOVO: Encerrar turno pelo Modal de Ganhos (Acompanhar Ganhos) ---
    const handleFinishShiftFromTracker = async (skipConfirm = false) => {
        const executeShiftFinish = async () => {
            handleSubmitLocalTracker(true); // Lançar e limpar dados do Tracker

            // 3. Gerenciar Cronômetro e Fluxo de Encerramento
            if (activeShift) {
                // Se houver turno formal (odômetro), chama handleManualKm('end', true) indicando que os ganhos já foram lançados
                handleManualKm('end', true);
            } else if (workTimer.isRunning || workTimer.elapsedTime > 0) {
                // Se houver APENAS cronômetro solto (sem odômetro inicial), encerra direto 
                const finalTimerTime = stopWorkTimerForce();

                if (finalTimerTime) {
                    const [h, m, s] = finalTimerTime.split(':').map(Number);
                    const timerHours = h + (m / 60) + (s / 3600);

                    if (timerHours > 0) {
                        const dateKey = getTodayDateKey();
                        setKmRecords(prev => {
                            const rec = prev[dateKey] || { startOdo: null, endOdo: null, manualKm: null, workHours: 0 };
                            return { ...prev, [dateKey]: { ...rec, workHours: (rec.workHours || 0) + timerHours } };
                        });
                    }
                }
                showToast(finalTimerTime ? `Turno encerrado: ${finalTimerTime}` : "Turno encerrado.", "success");
                triggerRemoteSync(true);
            } else {
                showToast("Acompanhamento finalizado e ganhos lançados no extrato!", "success");
                triggerRemoteSync(true);
            }
        };

        if (skipConfirm === true) {
            await executeShiftFinish();
            return;
        }

        setConfirmModal({
            type: 'finishShiftConfirm',
            title: 'Encerrar Turno Agora?',
            message: 'Deseja realmente encerrar este turno e salvar seus ganhos no extrato?',
            confirmText: 'Sim, Encerrar',
            cancelText: 'Não, Continuar',
            onConfirm: async () => {
                setConfirmModal(null);
                await executeShiftFinish();
            }
        });
    };









    // --- LÓGICA DE CATEGORIAS CUSTOMIZADAS ---





    // --- FUNÇÃ•ES DE NAVEGAÇÃO EXTERNA ---


    // --- LÓGICA GERAL E IA ---
    const handleEditTransaction = (transaction) => {
        setEditingTransactionId(transaction.id);
        setTransType(transaction.type);
        setView('add');
    };

    const handleSaveTransaction = (transactionData) => {
        // Attach current vehicle ID to the transaction if not present
        // This ensures expenses like Maintenance/Gas are linked to the active vehicle
        const dataWithVehicle = {
            ...transactionData,
            vehicleId: transactionData.vehicleId || selectedVehicleId
        };

        if (editingTransactionId) {
            const updatedTransactions = transactions.map(t => t.id === editingTransactionId ? dataWithVehicle : t);
            // Sincronização FORÇADA imediata (Ref e Storage) para evitar perda se fechar rápido
            transactionsRef.current = updatedTransactions;
            storage.setItem('rotaDoLucroData', updatedTransactions);
            setTransactions(updatedTransactions);

            showToast(`Lançamento editado com sucesso.`, 'info');
            setEditingTransactionId(null);

            // Sincronização individual da transação editada
            if (firebaseUser?.uid) {
                saveTransactionRemote(firebaseUser.uid, dataWithVehicle);
                syncUserMetrics(updatedTransactions);
            }
        } else {
            const updatedTransactions = [dataWithVehicle, ...transactions];
            // Sincronização FORÇADA imediata (Ref e Storage) para evitar perda se fechar rápido
            transactionsRef.current = updatedTransactions;
            storage.setItem('rotaDoLucroData', updatedTransactions);
            setTransactions(updatedTransactions);

            showToast(`Lançamento de ${transactionData.type === 'income' ? 'Ganho' : 'Gasto'} registrado.`, transactionData.type === 'income' ? 'success' : 'error');

            // Sincronização individual da nova transação
            if (firebaseUser?.uid) {
                saveTransactionRemote(firebaseUser.uid, dataWithVehicle);
                syncUserMetrics(updatedTransactions);
            }
        }
        setView('dashboard');
        setVoiceData(null);
        // iOS PWA: sincroniza com Firebase IMEDIATAMENTE (bypass throttling) para evitar perda de dados
        triggerRemoteSync(true);
    };

    // Versão da função de salvar transação usada pela Loja a Bordo.
    // Não chama setView() para não redirecionar o usuário para fora da loja.
    // Usa transactionsRef.current para evitar stale closure com transactions de forma limpa e segura.
    const handleLojaTransaction = useCallback((transactionData) => {
        const dataWithVehicle = {
            ...transactionData,
            vehicleId: transactionData.vehicleId || selectedVehicleId
        };

        const currentTransactions = transactionsRef.current || [];
        const updatedTransactions = [dataWithVehicle, ...currentTransactions];

        // 1. Atualização do estado e ref locais (síncronos)
        transactionsRef.current = updatedTransactions;
        storage.setItem('rotaDoLucroData', updatedTransactions);
        setTransactions(updatedTransactions);

        // 2. Sincronização assíncrona (fora do ciclo de renderização direta do React)
        if (firebaseUser?.uid) {
            saveTransactionRemote(firebaseUser.uid, dataWithVehicle);
            syncUserMetrics(updatedTransactions);
        }

        triggerRemoteSync(true);
    }, [selectedVehicleId, firebaseUser, triggerRemoteSync, syncUserMetrics]);

    // handleAddTransactionInternal is defined below with ID return support










    const deleteTransaction = (id) => {
        setConfirmModal({
            id,
            type: 'transaction',
            title: 'Excluir Item?',
            message: 'Deseja remover esta corrida/gasto do histórico?',
            onConfirm: () => confirmDelete('transaction', id) // Passa os parâmetros
        });
    };

    // Exclusão direta de venda da Loja a Bordo (sem modal duplo — confirmação fica na LojaABordoPage)
    const deleteLojaSaleDirect = async (saleId) => {
        const tx = transactions.find(t => t.id === saleId);

        // Remove a transação do extrato global
        const newTransactions = transactions.filter(t => t.id !== saleId);
        transactionsRef.current = newTransactions;
        storage.setItem('rotaDoLucroData', newTransactions);
        setTransactions(newTransactions);

        // Restaura o estoque no Firebase
        if (tx && firebaseUser?.uid) {
            const saleItems = tx.lojaItems || tx.items || [];
            if (saleItems.length > 0) {
                try {
                    const currentLojaItems = await fetchLojaItems(firebaseUser.uid);
                    if (currentLojaItems && currentLojaItems.length > 0) {
                        const restockedItems = currentLojaItems.map(item => {
                            const soldItem = saleItems.find(si => si.itemId === item.id);
                            if (soldItem && item.stock !== null && item.stock !== undefined) {
                                return { ...item, stock: (Number(item.stock) || 0) + (soldItem.qty || 1) };
                            }
                            return item;
                        });
                        await saveLojaItems(firebaseUser.uid, restockedItems);
                    }
                } catch (err) {
                    console.error('APP_JS: Erro ao restaurar estoque da loja:', err);
                }
            }
        }

        // Sincroniza remotamente
        if (firebaseUser?.uid) {
            try {
                await deleteTransactionRemote(firebaseUser.uid, saleId);
                await syncUserMetrics(newTransactions);
            } catch (err) {
                console.error('APP_JS: Erro ao sincronizar exclusão de venda da loja:', err);
            }
        }
        await triggerRemoteSync(true);
    };

    const deleteFixedExpense = (id) => {
        setConfirmModal({
            id,
            type: 'fixedExpense',
            title: 'Excluir Despesa Fixa?',
            message: 'Deseja remover esta despesa recorrente da sua lista de metas?',
            onConfirm: () => confirmDelete('fixedExpense', id) // Passa os parâmetros
        });
    };

    const toggleFixedExpensePriority = (id) => {
        const updated = fixedExpenses.map(e =>
            e.id === id ? { ...e, priority: !e.priority } : e
        );
        setFixedExpenses(updated);
        storage.setItem('rotaDoLucroFixedExpenses', updated);
        // iOS PWA: Sync imediato
        triggerRemoteSync(true);
    };

    // Função Unificada de Confirmação de Deleção (Ajustada para ser explícita e SEGURA)
    const confirmDelete = async (type, id) => {
        if (type === 'transaction') {
            // Find transaction to check if it's linked to a completed trip
            const tx = transactions.find(t => t.id === id);
            if (tx && tx.tripId) {
                // Revert scheduled trip back to pending and remove linked receipts
                const updatedTrips = scheduledTrips.map(t => t.id === tx.tripId ? { ...t, status: 'pending', receiptMeta: undefined } : t);

                // Força sync imediato de agendamentos
                scheduledTripsRef.current = updatedTrips;
                storage.setItem('rotaDoLucroScheduledTrips', updatedTrips);
                setScheduledTrips(updatedTrips);

                showToast('Lançamento removido e agendamento movido de volta para Pendentes (recibos vinculados removidos).', 'info');
            } else if (tx && tx.source === 'loja_abordo') {
                showToast('Venda da Loja removida e estoque restaurado.', 'success');
            } else {
                showToast('Transação removida.', 'error');
            }

            // Remove transaction com sync imediato
            const newTransactions = transactions.filter(t => t.id !== id);
            transactionsRef.current = newTransactions;
            storage.setItem('rotaDoLucroData', newTransactions);
            setTransactions(newTransactions);

            // Se a transação é da Loja a Bordo, restaura o estoque no Firebase
            if (tx && tx.source === 'loja_abordo' && firebaseUser?.uid) {
                const saleItems = tx.lojaItems || tx.items || [];
                if (saleItems.length > 0) {
                    try {
                        const currentLojaItems = await fetchLojaItems(firebaseUser.uid);
                        if (currentLojaItems && currentLojaItems.length > 0) {
                            const restockedItems = currentLojaItems.map(item => {
                                const soldItem = saleItems.find(si => si.itemId === item.id);
                                if (soldItem && item.stock !== null && item.stock !== undefined) {
                                    return { ...item, stock: (Number(item.stock) || 0) + (soldItem.qty || 1) };
                                }
                                return item;
                            });
                            await saveLojaItems(firebaseUser.uid, restockedItems);
                        }
                    } catch (err) {
                        console.error('APP_JS: Erro ao restaurar estoque da loja via extrato:', err);
                    }
                }
            }

            // Sincronização individual (Exclusão remota)
            if (firebaseUser?.uid) {
                try {
                    await deleteTransactionRemote(firebaseUser.uid, id);
                    await syncUserMetrics(newTransactions);
                } catch (err) {
                    console.error('APP_JS: Erro ao sincronizar exclusão:', err);
                }
            }

            await triggerRemoteSync(true); // Sync IMEDIATO (bypass throttling) com Firebase

        } else if (type === 'vehicle') {

            // Exclusão com tombstone: impede que a união por ID ressuscite o
            // veículo em outro aparelho.
            const targetKey = String(id);
            const newVehicles = (vehiclesRef.current || []).filter(v => String(v.id) !== targetKey);
            const nowTsDel = Date.now();
            vehiclesRef.current = newVehicles;
            vehiclesLastUpdatedRef.current = nowTsDel;
            const updatedVTomb = [...(vehicleTombstonesRef.current || []).map(String)];
            if (!updatedVTomb.includes(targetKey)) updatedVTomb.push(targetKey);
            vehicleTombstonesRef.current = updatedVTomb.slice(-1000);
            setVehicleTombstones(vehicleTombstonesRef.current);
            storage.setItem('rotaDoLucroVehicleTombstones', vehicleTombstonesRef.current);
            storage.setItem('rotaDoLucroVehicles', newVehicles);
            storage.setItem('mp_vehicles_lastUpdated', nowTsDel);
            setVehicles(newVehicles);
            setVehiclesLastUpdated(nowTsDel);
            showToast('Veículo removido.', 'error');
            triggerRemoteSync(true);
        } else if (type === 'fixedExpense') {
            const expense = fixedExpenses.find(e => e.id === id);

            // Remove expense com sync imediato
            const newFixedExpenses = fixedExpenses.filter(e => e.id !== id);
            fixedExpensesRef.current = newFixedExpenses;
            storage.setItem('rotaDoLucroFixedExpenses', newFixedExpenses);
            setFixedExpenses(newFixedExpenses);

            if (expense && expense.transactionId) {
                // Remove a transação do extrato se ela foi lançada
                const newTransactions = transactions.filter(tr => tr.id !== expense.transactionId);
                transactionsRef.current = newTransactions;
                storage.setItem('rotaDoLucroData', newTransactions);
                setTransactions(newTransactions);
            }
            showToast('Despesa fixa removida.', 'error');
        }
        triggerRemoteSync(true); // Sync imediato e FORÇADO com Firebase
        setConfirmModal(null); // Fecha o modal após a ação
    };

    const confirmReset = () => {
        setTransactions([]);
        setKmRecords({});
        setDailyManualStats({});
        setFixedExpenses([]);
        setScheduledTrips([]);
        setActiveShift(null);
        setActiveShiftKey(null);

        // --- LIMPEZA DE REFS ---
        transactionsRef.current = [];
        kmRecordsRef.current = {};
        dailyManualStatsRef.current = {};
        fixedExpensesRef.current = [];
        scheduledTripsRef.current = [];
        activeShiftRef.current = null;
        activeShiftKeyRef.current = null;

        // --- LIMPEZA DE STORAGE ---
        storage.removeItem('rotaDoLucroData');
        storage.removeItem('rotaDoLucroKm');
        storage.removeItem('rotaDoLucroDailyManualStats');
        storage.removeItem('rotaDoLucroFixedExpenses');
        storage.removeItem('rotaDoLucroScheduledTrips');
        storage.removeItem('rotaDoLucroActiveShift');
        storage.removeItem('rotaDoLucroActiveShiftKey');

        setShowResetModal(false);
        setView('dashboard');
        showToast('Todos os dados foram resetados localmente.', 'error');

        // Força sincronização de "vazio" para a nuvem
        triggerRemoteSync(true);
    };



    // Helpers: capitalize first letters and format CPF
    const capitalizeName = (s) => {
        if (!s) return '';
        return s.split(' ').map(p => p ? (p.charAt(0).toUpperCase() + p.slice(1)) : '').join(' ');
    };

    const formatCPF = (v) => {
        if (!v) return '';
        const d = v.replace(/\D/g, '').slice(0, 11);
        let r = d;
        if (d.length > 9) r = d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        else if (d.length > 6) r = d.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
        else if (d.length > 3) r = d.replace(/(\d{3})(\d{1,3})/, '$1.$2');
        return r;
    };

    // Capitalize only the first character of the string
    const capitalizeFirstLetter = (s) => {
        if (!s) return '';
        return s.charAt(0).toUpperCase() + s.slice(1);
    };

    // Format phone numbers to (DD) 9XXXX-XXXX or (DD) XXXX-XXXX
    const formatPhone = (raw) => {
        if (!raw) return '';
        let v = raw.replace(/\D/g, '').slice(0, 11);
        if (v.length <= 2) return v;
        const ddd = v.slice(0, 2);
        const rest = v.slice(2);
        if (rest.length <= 4) return `(${ddd}) ${rest} `;
        if (rest.length <= 8) return `(${ddd}) ${rest.slice(0, rest.length - 4)} -${rest.slice(-4)} `;
        return `(${ddd}) ${rest.slice(0, 5)} -${rest.slice(-4)} `;
    };

    // Reprint handler removed â€” printing is available via 'Baixar PDF' or 'Compartilhar' actions

    // Receipts are removed in-place where used; function removed to avoid unused symbol warnings.

    // Removed: handleRemoveTripReceipt is now inlined where used




    // --- LÓGICA DE METAS E DESPESAS FIXAS ---


    // --- FUNÇÃ•ES DE GERENCIAMENTO DE VEÃCULO ---



    // --- FUNÇÃ•ES AUXILIARES RECUPERADAS ---
    const getMonthlyData = () => {
        const months = {};

        transactions.forEach(t => {
            const date = new Date(t.date);
            const key = `${date.getFullYear()} -${String(date.getMonth() + 1).padStart(2, '0')} `;

            if (!months[key]) months[key] = { income: 0, expense: 0, profit: 0, count: 0, km: 0, trips: 0 };

            if (t.type === 'income') months[key].income += t.amount;
            else months[key].expense += t.amount;

            months[key].profit = months[key].income - months[key].expense;
            months[key].count += 1;
            months[key].trips += t.tripCount || 0;
        });

        Object.entries(kmRecords).forEach(([dateKey, record]) => {
            const [year, month] = dateKey.split('-');
            const key = `${year} -${month} `;
            const kmValue = record.manualKm || (record.endOdo !== null && record.startOdo !== null ? record.endOdo - record.startOdo : 0);

            if (!months[key]) months[key] = { income: 0, expense: 0, profit: 0, count: 0, km: 0, trips: 0 };

            // Soma KM ao registro existente ou cria um novo se só houver KM
            months[key].km += kmValue;
        });

        return Object.entries(months).sort((a, b) => b[0].localeCompare(a[0]));
    };





    const getAllMaintenanceAlerts = () => {
        const currentVehicle = vehicles.find(v => String(v.id) === String(selectedVehicleId)) || vehicles[0];
        const currentOdo = getLatestOdometerReading();
        const alerts = [];

        if (!currentVehicle) return [];

        const checkItem = (lastOdo, interval, name, icon) => {
            if (lastOdo === null || lastOdo === undefined) return;
            const nextOdo = lastOdo + interval;
            const remaining = nextOdo - currentOdo;

            if (remaining <= 1000) {
                let status = 'attention';
                if (remaining <= 0) status = 'critical';
                else if (remaining <= 500) status = 'warning';

                alerts.push({ name, remaining, nextOdo, status, icon });
            }
        };

        checkItem(currentVehicle.lastOilOdo, currentVehicle.oilIntervalKm, 'Troca de Óleo', Droplet);
        checkItem(currentVehicle.lastTiresOdo, currentVehicle.tiresIntervalKm, 'Rodízio/Troca Pneus', CircleDashed);
        checkItem(currentVehicle.lastBrakesOdo, currentVehicle.brakesIntervalKm, 'Pastilhas de Freio', Disc);
        checkItem(currentVehicle.lastBeltOdo, currentVehicle.beltIntervalKm, 'Correia Dentada', Wrench);

        return alerts.sort((a, b) => a.remaining - b.remaining);
    };




    const renderAdd = () => (
        <TransactionFormPage
            initialData={editingTransactionId ? transactions.find(t => t.id === editingTransactionId) : (voiceData ? { ...voiceData, date: new Date().toISOString() } : null)}
            initialDate={view === 'history' && historyView === 'daily' ? dailyFilter : dashboardDate}
            type={transType}
            onSave={handleSaveTransaction}
            onCancel={() => {
                setView('dashboard');
                setEditingTransactionId(null);
                setVoiceData(null);
            }}
            getCategories={getCategories}
            customCategories={customCategories}
            setCustomCategories={setCustomCategories}
            deletedDefaultCategories={deletedDefaultCategories}
            setDeletedDefaultCategories={setDeletedDefaultCategories}
            showToast={showToast}
            formatCurrency={formatCurrency}
            vehicles={vehicles}
            activeVehicleId={selectedVehicleId}
        />
    );

    const handleResetDay = (date) => {
        if (isExpired) {
            handleOpenPremium();
            return;
        }

        setConfirmModal({
            type: 'resetDay',
            title: 'Limpar registros do dia?',
            message: 'Deseja remover as corridas, gastos e KM deste dia? Os dados totais do mês serão recalculados.',
            confirmText: 'Sim, Limpar',
            onConfirm: async () => {
                // 1. Identifica as transações do dia a remover
                const dayTransactions = transactions.filter(t => t.date.startsWith(date));
                const newTransactions = transactions.filter(t => !t.date.startsWith(date));
                setTransactions(newTransactions);
                transactionsRef.current = newTransactions;
                storage.setItem('rotaDoLucroData', newTransactions);

                // 1b. Apaga cada transação do dia na subcoleção do Firestore
                // (sem isso, fetchTransactionsRemote restaura os dados ao reabrir)
                if (firebaseUser?.uid && dayTransactions.length > 0) {
                    try {
                        await Promise.all(
                            dayTransactions.map(t => deleteTransactionRemote(firebaseUser.uid, t.id))
                        );
                    } catch (e) {
                        console.warn('handleResetDay: erro ao deletar transações remotas', e);
                    }
                }

                // 2. Remove registro de KM do dia
                const newKm = { ...kmRecordsRef.current };
                delete newKm[date];
                setKmRecords(newKm);
                kmRecordsRef.current = newKm;
                storage.setItem('rotaDoLucroKm', newKm);

                // 3. Remove stats manuais do dia
                const newStats = { ...dailyManualStatsRef.current };
                delete newStats[date];
                setDailyManualStats(newStats);
                dailyManualStatsRef.current = newStats;
                storage.setItem('rotaDoLucroDailyManualStats', newStats);

                // 4. Se houver turno ativo nesse dia, reseta
                if (activeShift && activeShift.startDate === date) {
                    setActiveShift(null);
                    setActiveShiftKey(null);
                    activeShiftRef.current = null;
                    activeShiftKeyRef.current = null;
                    storage.removeItem('rotaDoLucroActiveShift');
                    storage.removeItem('rotaDoLucroActiveShiftKey');
                }

                showToast('Dados do dia apagados com sucesso!', 'success');

                // 5. Sync final para atualizar kmRecords e dailyManualStats na nuvem
                triggerRemoteSync(true);

                setShowDailyManualModal(false);
                setConfirmModal(null);
            }
        });
    };

    const handleSaveDailyManualStats = (date, stats) => {
        let updatedStats;
        setDailyManualStats(prev => {
            updatedStats = {
                ...prev,
                [date]: stats
            };
            return updatedStats;
        });
        dailyManualStatsRef.current = updatedStats;
        storage.setItem('rotaDoLucroDailyManualStats', updatedStats);

        // Sincroniza também o manualKm no kmRecords para evitar discrepâncias nas métricas Operacionais
        let updatedKm;
        setKmRecords(prev => {
            updatedKm = {
                ...prev,
                [date]: {
                    ...(prev[date] || { startOdo: null, endOdo: null, workHours: 0 }),
                    manualKm: Number(stats.km) || 0,
                    workHours: Number(stats.hours) || 0
                }
            };
            return updatedKm;
        });
        kmRecordsRef.current = updatedKm;
        storage.setItem('rotaDoLucroKm', updatedKm);

        showToast('Estatísticas manuais salvas!', 'success');
        triggerRemoteSync(true);
    };


    // --- FUNÇÃO CORRETA PARA ADIÇÃO INTERNA (RETORNA O ID) ---
    const handleAddTransactionInternal = (transactionData) => {
        const id = transactionData.id || Date.now();
        const newTransaction = { id, ...transactionData };

        setTransactions(prev => {
            const updated = [newTransaction, ...prev];
            transactionsRef.current = updated;
            storage.setItem('rotaDoLucroData', updated);
            return updated;
        });

        // Sincronização individual
        if (firebaseUser?.uid) {
            saveTransactionRemote(firebaseUser.uid, newTransaction);
        }

        return id;
    };

    const handleRemoveTransaction = (id) => {
        setTransactions(prev => {
            const updated = prev.filter(t => t.id !== id);
            transactionsRef.current = updated;
            storage.setItem('rotaDoLucroData', updated);
            return updated;
        });
    };

    // --- QUICK ADD TRANSACTIONS FROM MODAL ---

    const handleDeleteQuickTransaction = (id) => {
        setConfirmModal({
            id,
            type: 'transaction',
            title: 'Excluir Lançamento?',
            message: 'Deseja excluir este lançamento?',
            confirmText: 'Excluir',
            onConfirm: () => confirmDelete('transaction', id)
        });
    };
    const renderAnalytics = () => (
        <AnalyticsPage
            monthlyData={getMonthlyData()}
            formatCurrency={formatCurrency}
            formatKm={formatKm}
            onBack={() => setView('history')}
        />
    );



    const payPastDueExpense = (id) => {
        removePastDueWithTombstone([id]);
        showToast('Pendência regularizada!', 'success');
        if (triggerRemoteSync) triggerRemoteSync(true);
    };

    const payMultiplePastDueExpenses = (ids) => {
        if (!ids || !ids.length) return;
        removePastDueWithTombstone(ids);
        showToast(`${ids.length} pendência(s) regularizada(s)!`, 'success');
        if (triggerRemoteSync) triggerRemoteSync(true);
    };

    const deletePastDueExpense = (id) => {
        removePastDueWithTombstone([id]);
        showToast('Despesa atrasada removida!', 'success');
        if (triggerRemoteSync) triggerRemoteSync(true);
    };





    // --- RENDERIZAÇÃO DE ABAS DE AJUSTES ---



    // eslint-disable-next-line no-unused-vars
    const NavItem = ({ id, icon: Icon, label }) => (
        <button
            onClick={() => setView(id)}
            className={`flex flex-col items-center justify-center w-full h-full space-y-1 transition-colors ${view === id ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-600'}`}
        >
            <Icon size={24} strokeWidth={view === id ? 2.5 : 2} />
            <span className="text-[10px] font-medium">{label}</span>
        </button>
    );



    // --- RENDERIZADORES DE CONTEÚDO ---

    // --- BLOQUEIO DE FERRAMENTAS PARA ANÔNIMOS ---
    const handleToolClick = (onAccess) => {
        if (isExpired) {
            handleOpenPremium();
            return;
        }
        if (firebaseUser && firebaseUser.isAnonymous) {
            setConfirmModal({
                type: 'locked',
                title: 'Recurso Exclusivo',
                message: 'Esta ferramenta está disponível apenas para contas vinculadas. Salve seus dados para acessar.',
                onConfirm: () => {
                    setConfirmModal(null);
                    setShowLinkAccountModal(true);
                }
            });
        } else {
            onAccess();
        }
    };

    // --- CALLBACKS MEMOIZADOS PARA O DASHBOARD ---
    const handleOpenDailyDetailsCallback = useCallback((date) => {
        setDailyFilter(date);
        setShowDailyManualModal(true);
    }, [setDailyFilter, setShowDailyManualModal]);

    const handleOpenSubscriptionCallback = useCallback(() => {
        setSettingsInitialTab('subscription');
        changeView('settings');
    }, [setSettingsInitialTab, changeView]);

    const handleOpenEarningsTrackerCallback = useCallback(() => {
        setShowEarningsModal(true);
    }, [setShowEarningsModal]);

    const handleOpenSupportCallback = useCallback(() => {
        setSettingsInitialTab('support');
        changeView('settings');
    }, [setSettingsInitialTab, changeView]);

    const handleOpenAppearanceCallback = useCallback(() => {
        setShowAppearanceModal(true);
    }, [setShowAppearanceModal]);

    const renderTools = () => (
        <ToolsPage
            onOpenAgenda={() => handleToolClick(() => setView('agenda'))}
            onOpenMission={() => handleToolClick(() => setView('missions'))}
            onOpenEV={() => handleToolClick(() => setView('ev_stations'))}
            onOpenCalculators={() => handleToolClick(() => setView('calculators'))}
            onOpenVehicles={() => handleToolClick(() => setView('vehicles'))}
            onOpenSettings={() => setView('settings')}
            onOpenAppTools={() => setView('app_tools')}
            onOpenTracker={() => handleToolClick(() => setView('tracker'))}
            onOpenFlightTracker={() => handleToolClick(() => setView('flight_tracker'))}
            onOpenLoja={() => setView('loja_abordo')}
            onOpenFixedExpenses={() => handleToolClick(() => changeView('fixed-expenses'))}
            showLoja={showLoja}
            lockLoja={lockLoja}
            showJornadaPro={showJornadaPro}
            lockJornadaPro={lockJornadaPro}
            showFlightRadar={showFlightRadar}
            lockFlightRadar={lockFlightRadar}
            showVehicles={showVehicles}
            lockVehicles={lockVehicles}
            isExpired={isExpired}
            onOpenPremium={handleOpenPremium}

            // --- PROPS PWA ---
            isPwaInstalled={isPwaInstalled}
            isNativeApp={isNativeApp()}
            deferredPrompt={deferredPrompt}
            onInstall={() => {
                const isAndroidWeb = !isIOS && typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);
                // Se for Android Web, direciona para o download do APK independente
                if (isAndroidWeb) {
                    setShowPwaModal(true);
                } else if (deferredPrompt) {
                    deferredPrompt.prompt();
                    deferredPrompt.userChoice.then(({ outcome }) => {
                        if (outcome === 'accepted') {
                            setIsPwaInstalled(true);
                            setDeferredPrompt(null);
                            showToast('App instalado com sucesso!', 'success');
                        }
                    });
                } else {
                    setShowPwaModal(true);
                }
            }}
        />
    );

    const renderCalculators = () => (
        <CalculatorsPage
            onBack={() => setView('tools')}
            onNavigate={(view) => handleToolClick(() => setView(view))}
        />
    );

    const renderMissionsPage = () => (
        <MissionsPage
            onBack={() => setView('tools')}
            firebaseUser={firebaseUser}
            isExpired={isExpired}
            onOpenPremium={handleOpenPremium}
            isDark={isDarkModeActive()}
            isInitialLoadDone={isInitialLoadDone}

        />
    );

    const renderAgendaPage = () => (
        <AgendaPage
            scheduledTrips={scheduledTrips}
            setScheduledTrips={setScheduledTrips}
            onBack={() => setView('tools')}
            onOpenTripCalc={() => setShowTripCalcModal(true)}
            formatCurrency={formatCurrency}
            userProfile={userProfile}
            vehicles={vehicles}
            selectedVehicleId={selectedVehicleId}
            transactions={transactions}
            setTransactions={setTransactions}
            showToast={showToast}
        />
    );



    const maintOptions = [
        { id: 'oil', label: 'Troca de Óleo', icon: Droplet },
        { id: 'tires', label: 'Rodízio/Troca Pneus', icon: CircleDashed },
        { id: 'brakes', label: 'Pastilhas de Freio', icon: Disc },
        { id: 'belt', label: 'Correia Dentada', icon: Wrench },
    ];

    // Nav transparency removed: navigation will stay opaque to avoid visual flicker on scroll.

    return (
        // CORREÇÃO 3: Aplica a classe 'dark' baseada na nova lógica isDarkModeActive()
        <div className={`${isDarkModeActive() ? 'dark' : ''} `}>
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-sans transition-colors duration-300">

                <div className="w-full md:max-w-3xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto h-[100dvh] pt-[var(--sat)] pb-[var(--sab)] pl-[var(--sal)] pr-[var(--sar)] bg-gray-50 dark:bg-gray-900 shadow-2xl relative overflow-hidden flex flex-col">

                    {/* BANNER GLOBAL (Notificações Fixas) */}
                    {globalBanner && globalBanner.active && (
                        <div className={`w-full p-2.5 flex items-center justify-center text-center px-4 ${globalBanner.type === 'warning' ? 'bg-amber-500' : globalBanner.type === 'error' ? 'bg-red-500' : 'bg-indigo-600'} text-white z-[60] shrink-0 sticky top-0`}>
                            <p className="text-[11px] sm:text-xs font-bold leading-tight flex items-center gap-2">
                                {globalBanner.type === 'error' ? <AlertTriangle size={14} className="shrink-0" /> : <Bell size={14} className="shrink-0 animate-bounce" />}
                                {globalBanner.message}
                            </p>
                        </div>
                    )}

                    <main
                        className="flex-1 overflow-y-auto custom-scrollbar relative"
                        style={{ paddingBottom: 'calc(8rem + var(--sab, env(safe-area-inset-bottom, 0px)))' }}
                    >
                        <Suspense fallback={<LoadingSpinner />}>
                            <Routes>
                                <Route path="/" element={<DashboardPage
                                    goals={goals}
                                    setGoals={setGoals}
                                    pinnedGoalId={pinnedGoalId}
                                    setGoalsInitialTab={setGoalsInitialTab}
                                    currentView={view}
                                    userProfile={userProfile}
                                    vehicles={vehicles}
                                    selectedVehicleId={selectedVehicleId}
                                    setSelectedVehicleId={setSelectedVehicleId}
                                    dashboardDate={dashboardDate}
                                    setDashboardDate={setDashboardDate}
                                    notifications={notifications}
                                    permission={permission}
                                    requestPermission={requestPermission}
                                    dismissNotification={dismissNotification}
                                    dismissAllNotifications={dismissAllNotifications}
                                    setView={setView}
                                    transactions={transactions}
                                    dailyGoal={dailyGoal}
                                    kmRecords={kmRecords}
                                    activeShift={activeShift}
                                    dailyManualStats={dailyManualStats}
                                    showToast={showToast}
                                    handleManualKm={handleManualKm}
                                    handleOpenDailyDetails={handleOpenDailyDetailsCallback}
                                    formatCurrency={formatCurrency}
                                    formatKm={formatKm}
                                    formatWorkHours={formatWorkHours}
                                    workTimer={workTimer}
                                    startWorkTimer={startWorkTimer}
                                    pauseWorkTimer={pauseWorkTimer}
                                    resumeWorkTimer={resumeWorkTimer}
                                    stopWorkTimer={stopWorkTimer}
                                    showAiVoice={showAiVoice}
                                    lockAiVoice={lockAiVoice}
                                    onOpenAiVoice={handleVoiceAction}
                                    showLoja={showLoja}
                                    lockLoja={lockLoja}
                                    subscription={subscription}
                                    isExpired={isExpired}
                                    isSubscriptionLoaded={isSubscriptionLoaded}
                                    onOpenPremium={handleOpenPremium}
                                    showJornadaPro={showJornadaPro}
                                    lockJornadaPro={lockJornadaPro}
                                    showFlightRadar={showFlightRadar}
                                    lockFlightRadar={lockFlightRadar}
                                    showVehicles={showVehicles}
                                    lockVehicles={lockVehicles}
                                    isSyncing={isSyncing}
                                    triggerRemoteSync={triggerRemoteSync}
                                    lastRemoteSync={lastRemoteSync}
                                    openSubscription={handleOpenSubscriptionCallback}
                                    onOpenEarningsTracker={handleOpenEarningsTrackerCallback}
                                    keepScreenOn={keepScreenOn}
                                    toggleKeepScreenOn={toggleKeepScreenOn}
                                    onStopTracker={handleStopTracker}

                                    onOpenSupport={handleOpenSupportCallback}
                                    isDark={isDarkModeActive()}
                                    onStartTracker={handleStartTrackerWithDisclosure}
                                    featureFlags={featureFlags}
                                    compactMode={compactMode}
                                    onOpenAppearance={handleOpenAppearanceCallback}
                                    scheduledTrips={scheduledTrips}
                                />} />




                                <Route path="/add" element={<div className="p-5 md:p-8 lg:p-10">{renderAdd()}</div>} />

                                <Route path="/history" element={
                                    <div className="p-5 md:p-8 lg:p-10">
                                        <HistoryPage
                                            showCardComparisons={showCardComparisons}
                                            userProfile={userProfile}
                                            transactions={transactions}
                                            historyView={historyView}
                                            setHistoryView={setHistoryView}
                                            dailyFilter={dailyFilter}
                                            setDailyFilter={(date) => {
                                                setDashboardDate(date); // Usa a função validada que já atualiza ambos
                                            }}


                                            weeklyFilter={weeklyFilter}
                                            setWeeklyFilter={setWeeklyFilter}
                                            monthlyFilter={monthlyFilter}
                                            setMonthlyFilter={setMonthlyFilter}
                                            yearlyFilter={yearlyFilter}
                                            setYearlyFilter={setYearlyFilter}
                                            kmRecords={kmRecords}
                                            dailyManualStats={dailyManualStats}
                                            onSaveDailyStats={handleSaveDailyManualStats}
                                            showDailyManualModal={showDailyManualModal}
                                            setShowDailyManualModal={setShowDailyManualModal}
                                            onEditTransaction={handleEditTransaction}
                                            onDeleteTransaction={deleteTransaction}
                                            onResetDay={handleResetDay}
                                            onAddQuickTransaction={handleAddQuickTransaction}
                                            onDeleteQuickTransaction={handleDeleteQuickTransaction}
                                            getCategories={getCategories}
                                            formatCurrency={formatCurrency}
                                            formatNumber={formatNumber}
                                            formatValueBRL={formatValueBRL}
                                            parseValueBRL={parseValueBRL}
                                            showToast={showToast}
                                            vehicles={vehicles}
                                            activeVehicleId={selectedVehicleId}
                                            formatWorkHours={formatWorkHours}
                                            workTimer={workTimer}
                                            isExpired={isExpired}
                                            lockReports={false}
                                            onOpenPremium={handleOpenPremium}

                                            setIsExternalModalOpen={setIsExternalModalOpen}
                                        />
                                    </div>
                                } />

                                <Route path="/analytics" element={<div className="p-5 md:p-8 lg:p-10">{renderAnalytics()}</div>} />

                                <Route path="/settings" element={
                                    <div className="p-5 md:p-8 lg:p-10">
                                        <Suspense fallback={<LoadingSpinner />}>
                                            <SettingsPage
                                                userProfile={userProfile} setUserProfile={setUserProfile}
                                                vehicles={vehicles} setVehicles={setVehicles}
                                                customCategories={customCategories} setCustomCategories={setCustomCategories}
                                                fixedExpenses={fixedExpenses} setFixedExpenses={setFixedExpenses}
                                                transactions={transactions} setTransactions={setTransactions}
                                                scheduledTrips={scheduledTrips} setScheduledTrips={setScheduledTrips}
                                                dailyManualStats={dailyManualStats} setDailyManualStats={setDailyManualStats}
                                                kmRecords={kmRecords}
                                                theme={theme} setTheme={setTheme}
                                                monthlyProfitTarget={monthlyProfitTarget} setMonthlyProfitTarget={setMonthlyProfitTarget}
                                                monthlyWorkDays={monthlyWorkDays} setMonthlyWorkDays={setMonthlyWorkDays}
                                                showToast={showToast}
                                                onLogout={handleLogout}
                                                onResetPassword={onResetPassword}
                                                onDeleteAccount={handleDeleteAccount}
                                                onClearAllData={handleClearAllData}
                                                hideAdminPanel={hideAdminPanel} setHideAdminPanel={setHideAdminPanel}
                                                firebaseUser={firebaseUser}
                                                subscription={subscription}
                                                initialTab={settingsInitialTab}
                                                selectedVehicleId={selectedVehicleId}
                                                userIsAdmin={false}
                                                isExpired={isExpired}
                                                onOpenPremium={() => setShowPremiumModal(true)}
                                                onBack={() => setView('dashboard')}
                                                lastRemoteSync={lastRemoteSync}
                                                isSyncing={isSyncing}
                                                triggerRemoteSync={triggerRemoteSync}
                                                accentColor={accentColor} setAccentColor={setAccentColor}
                                                compactMode={compactMode} setCompactMode={setCompactMode}
                                                showCardComparisons={showCardComparisons} setShowCardComparisons={setShowCardComparisons}
                                                setIsExternalModalOpen={setIsExternalModalOpen}
                                            />
                                        </Suspense>
                                    </div>
                                } />

                                <Route path="/goals" element={
                                    <div className="p-5 md:p-8 lg:p-10">
                                        <GoalsPage
                                            transactions={transactions}
                                            fixedExpenses={fixedExpenses}
                                            setFixedExpenses={setFixedExpenses}
                                            pastDueExpenses={pastDueExpenses}
                                            payPastDueExpense={payPastDueExpense}
                                            payMultiplePastDueExpenses={payMultiplePastDueExpenses}
                                            deletePastDueExpense={deletePastDueExpense}
                                            showToast={showToast}
                                            dailyGoal={dailyGoal}
                                            setDailyGoal={setDailyGoal}
                                            monthlyProfitTarget={monthlyProfitTarget}
                                            setMonthlyProfitTarget={setMonthlyProfitTarget}
                                            monthlyWorkDays={monthlyWorkDays}
                                            setMonthlyWorkDays={setMonthlyWorkDays}
                                            manualSavedAmount={manualSavedAmount}
                                            setManualSavedAmount={setManualSavedAmount}
                                            manualVarCost={manualVarCost}
                                            setManualVarCost={setManualVarCost}
                                            manualVarCostType={manualVarCostType}
                                            setManualVarCostType={setManualVarCostType}
                                            varCostMode={varCostMode}
                                            setVarCostMode={setVarCostMode}
                                            onAddTransaction={handleAddTransactionInternal}
                                            onRemoveTransaction={handleRemoveTransaction}
                                            isExpired={isExpired}
                                            onOpenPremium={handleOpenPremium}
                                            triggerRemoteSync={triggerRemoteSync}
                                            initialTab={goalsInitialTab}

                                            setIsExternalModalOpen={setIsExternalModalOpen}
                                        />
                                    </div>
                                } />

                                <Route path="/fixed-expenses" element={
                                    <FixedExpensesPage
                                        onBack={() => changeView('tools')}
                                        onNavigateToGoals={() => changeView('goals')}
                                        onNavigateToSimulator={() => {
                                            setGoalsInitialTab('simulator');
                                            changeView('goals');
                                        }}
                                        showToast={showToast}
                                        isExpired={isExpired}
                                        onOpenPremium={handleOpenPremium}
                                        triggerRemoteSync={triggerRemoteSync}
                                        payMultiplePastDueExpenses={payMultiplePastDueExpenses}
                                    />
                                } />

                                <Route path="/tools" element={renderTools()} />
                                <Route path="/calculators" element={renderCalculators()} />

                                <Route path="/agenda" element={<AgendaPage
                                    scheduledTrips={scheduledTrips}
                                    setScheduledTrips={setScheduledTrips}
                                    transactions={transactions}
                                    setTransactions={setTransactions}
                                    showToast={showToast}
                                    onOpenTripCalc={() => setShowTripCalcModal(true)}
                                    formatCurrency={formatCurrency}
                                    userProfile={userProfile}
                                    vehicles={vehicles}
                                    selectedVehicleId={selectedVehicleId}
                                    onBack={() => changeView('tools')}
                                    isExpired={isExpired}
                                    onOpenPremium={handleOpenPremium}

                                />} />

                                <Route path="/missions" element={<MissionsPage
                                    onBack={() => changeView('tools')}
                                    isExpired={isExpired}
                                    onOpenPremium={handleOpenPremium}
                                    isDark={isDarkModeActive()}
                                    isInitialLoadDone={isInitialLoadDone}

                                />} />


                                <Route path="/trip_calc" element={<TripCalcPage
                                    onBack={() => changeView('calculators')}
                                    isExpired={isExpired}
                                    onOpenPremium={handleOpenPremium}

                                />} />
                                <Route path="/flex_calc" element={<FlexCalcPage
                                    onBack={() => changeView('calculators')}
                                    isExpired={isExpired}
                                    onOpenPremium={handleOpenPremium}

                                />} />
                                <Route path="/financing_calc" element={<FinancingCalcPage
                                    onBack={() => changeView('calculators')}
                                    isExpired={isExpired}
                                    onOpenPremium={handleOpenPremium}

                                />} />
                                <Route path="/uber_pro_calc" element={<UberProCalcPage
                                    onBack={() => changeView('app_tools')}
                                    isExpired={isExpired}
                                    onOpenPremium={handleOpenPremium}

                                />} />
                                <Route path="/app_tools" element={<AppToolsPage
                                    onBack={() => changeView('tools')}
                                    onNavigate={changeView}
                                    isExpired={isExpired}
                                    onOpenPremium={handleOpenPremium}

                                />} />
                                <Route path="/loja_abordo" element={
                                    featureFlags?.lojaABordo?.visible === true
                                        ? <LojaABordoPage
                                            onBack={() => changeView('tools')}
                                            firebaseUser={firebaseUser}
                                            isExpired={isExpired}
                                            onOpenPremium={handleOpenPremium}
                                            showToast={showToast}
                                            onSaveTransaction={handleLojaTransaction}
                                            transactions={transactions}
                                            onDeleteTransaction={deleteLojaSaleDirect}
                                        />
                                        : <Navigate to="/" replace />
                                } />
                                <Route path="/flight_tracker" element={<FlightTrackerPage
                                    setView={setView}
                                    showToast={showToast}
                                />} />
                                <Route path="/operational_cost_calc" element={<OperationalCostPage
                                    onBack={() => changeView('calculators')}
                                    isExpired={isExpired}
                                    onOpenPremium={handleOpenPremium}

                                />} />

                                <Route path="/tracker" element={
                                    featureFlags?.jornadaPro?.visible === true
                                        ? <TrackerPage onStartTracker={handleStartTrackerWithDisclosure} setConfirmModal={setConfirmModal} showToast={showToast} onSaveTrackerStats={handleSaveTrackerStats} vehicles={vehicles} selectedVehicleId={selectedVehicleId} isDark={isDarkModeActive()} onOpenEarningsTracker={() => setShowEarningsModal(true)} onSubmitLocalTracker={handleSubmitLocalTracker} />
                                        : <Navigate to="/" replace />
                                } />
                                <Route path="/tracker-history" element={
                                    featureFlags?.jornadaPro?.visible === true
                                        ? <TrackerHistoryPage setConfirmModal={setConfirmModal} showToast={showToast} vehicles={vehicles} selectedVehicleId={selectedVehicleId} isDark={isDarkModeActive()} />
                                        : <Navigate to="/" replace />
                                } />
                                <Route path="/vehicles" element={<VehiclesPage
                                    vehicles={vehicles}
                                    setVehicles={setVehicles}
                                    onDeleteVehicle={removeVehicleWithTombstone}
                                    selectedVehicleId={selectedVehicleId}
                                    setSelectedVehicleId={setSelectedVehicleId}
                                    transactions={transactions}
                                    kmRecords={kmRecords}
                                    showToast={showToast}
                                    onBack={() => changeView('tools')}
                                    onAddTransaction={handleSaveTransaction}
                                    isExpired={isExpired}
                                    onOpenPremium={handleOpenPremium}
                                    triggerRemoteSync={triggerRemoteSync}
                                />} />



                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </Suspense>
                    </main>

                    {/* BARRA DE NAVEGAÇÃO (sempre opaca) - APENAS NAS TELAS PRINCIPAIS */}
                    {firebaseUser && !isLocked && !isAnyModalOpen && ['dashboard', 'history', 'goals', 'tools'].includes(view) && (
                        <div
                            className="absolute left-4 right-4 max-w-md md:max-w-xl lg:max-w-2xl mx-auto backdrop-blur-md rounded-2xl shadow-xl border border-gray-100/50 dark:border-gray-700/50 p-2 md:p-3 z-50 transition-all duration-300 bg-white/80 dark:bg-gray-900/80"
                            style={{
                                bottom: 'calc(1rem + var(--sab, env(safe-area-inset-bottom, 0px)))'
                            }}
                        >
                            <nav className="relative grid grid-cols-5 items-center px-4 overflow-visible gap-0">
                                <div className="col-span-1 flex items-center justify-center">
                                    <button
                                        onClick={() => changeView('dashboard')}
                                        className={`flex flex-col items-center justify-center px-3 py-1 transition-all duration-300 ${view === 'dashboard' ? 'text-indigo-600 dark:text-indigo-400 scale-105' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                    >
                                        <Home size={24} strokeWidth={view === 'dashboard' ? 2.5 : 2} />
                                        <span className="text-[11px] font-medium mt-1">Início</span>
                                    </button>
                                </div>

                                <div className="col-span-1 flex items-center justify-center">
                                    <button
                                        onClick={() => changeView('history')}
                                        className={`flex flex-col items-center justify-center px-3 py-1 transition-all duration-300 ${view === 'history' ? 'text-indigo-600 dark:text-indigo-400 scale-105' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                    >
                                        <ListChecks size={24} strokeWidth={view === 'history' ? 2.5 : 2} />
                                        <span className="text-[11px] font-medium mt-1">Extrato</span>
                                    </button>
                                </div>

                                <div className="col-span-1 flex items-center justify-center">
                                    <button
                                        onClick={() => {
                                            if (isExpired) {
                                                handleOpenPremium();
                                            } else {
                                                setShowAddModal(true);
                                            }
                                        }}
                                        className="w-14 h-14 rounded-full transition-all duration-300 active:scale-95 flex items-center justify-center bg-gradient-to-tr from-indigo-500 to-blue-500 text-white shadow-[0_4px_15px_rgba(99,102,241,0.4)] hover:shadow-[0_6px_20px_rgba(99,102,241,0.6)] hover:-translate-y-1 z-10"
                                    >
                                        <Plus size={28} strokeWidth={2.5} />
                                    </button>
                                </div>

                                <div className="col-span-1 flex items-center justify-center">
                                    <button
                                        onClick={() => handleToolClick(() => changeView('goals'))}
                                        className={`flex flex-col items-center justify-center px-3 py-1 transition-all duration-300 ${view === 'goals' ? 'text-indigo-600 dark:text-indigo-400 scale-105' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                    >
                                        <Target size={24} strokeWidth={view === 'goals' ? 2.5 : 2} />
                                        <span className="text-[11px] font-medium mt-1">Metas</span>
                                    </button>
                                </div>

                                <div className="col-span-1 flex items-center justify-center">
                                    <button
                                        onClick={() => changeView('tools')}
                                        className={`flex flex-col items-center justify-center px-3 py-1 transition-all duration-300 ${['tools', 'agenda', 'missionTracker'].includes(view) ? 'text-indigo-600 dark:text-indigo-400 scale-105' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                    >
                                        <LayoutGrid size={24} strokeWidth={['tools', 'agenda', 'missionTracker'].includes(view) ? 2.5 : 2} />
                                        <span className="text-[11px] font-medium mt-1">Ferramentas</span>
                                    </button>
                                </div>
                            </nav>
                        </div>
                    )}

                    {/* TOAST COMPONENT */}
                    <Toast message={toast.message} type={toast.type} isVisible={toast.visible} />

                    {showEarningsModal && (
                        <Suspense fallback={null}>
                            <EarningsTrackerModal
                                isOpen={showEarningsModal}
                                onClose={() => setShowEarningsModal(false)}

                                showToast={showToast}
                                localTracker={localTracker}
                                updateLocalTracker={handleUpdateLocalTracker}
                                submitLocalTracker={handleSubmitLocalTracker}
                                removePlatform={handleRemovePlatformFromTracker}
                                onEndShift={
                                    (activeShift || (workTimer && (workTimer.isRunning || workTimer.elapsedTime > 0)))
                                        ? handleFinishShiftFromTracker
                                        : null
                                }
                                workTimer={workTimer}
                            />
                        </Suspense>
                    )}


                    {/* --- NOVO: MODAL MENU DE SIMULADORES (Unificado) --- */}
                    {showCalculatorMenu && (
                        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                            <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-sm md:max-w-md lg:max-w-lg shadow-2xl relative">
                                <button
                                    onClick={() => setShowCalculatorMenu(false)}
                                    className="absolute top-4 right-4 text-gray-400 hover:text-red-500"
                                >
                                    <X size={24} />
                                </button>

                                <div className="text-center mb-6">
                                    <div className="bg-indigo-100 dark:bg-indigo-900/30 p-4 rounded-full text-indigo-600 dark:text-indigo-400 mb-3 mx-auto w-16 h-16 flex items-center justify-center">
                                        <Calculator size={32} />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">Selecione a Calculadora</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Ferramentas de apoio para sua jornada</p>
                                </div>

                                <div className="space-y-4">
                                    <button
                                        onClick={() => {
                                            setShowCalculatorMenu(false);
                                            setShowFlexModal(true);
                                        }}
                                        className="w-full py-4 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 font-bold rounded-xl transition-colors flex items-center justify-center gap-3 text-lg border border-green-200 dark:border-green-800"
                                    >
                                        <Fuel size={20} /> Flex / Combustível
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowCalculatorMenu(false);
                                            setShowTripCalcModal(true);
                                        }}
                                        className="w-full py-4 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-bold rounded-xl transition-colors flex items-center justify-center gap-3 text-lg border border-indigo-200 dark:border-indigo-800"
                                    >
                                        <MapLucide size={20} /> Viagem / Entrega
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}


                    {/* --- NOVO: MODAL DE LANÇAMENTO (ENTRADA/SAÍDA) --- */}
                    {showAddModal && (
                        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                            <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-[28px] p-6 w-full max-w-sm md:max-w-md shadow-[0_20px_50px_rgba(0,0,0,0.25)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)] relative border border-gray-200/50 dark:border-white/10 transition-all duration-300 overflow-hidden">
                                <button
                                    onClick={() => setShowAddModal(false)}
                                    className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-all hover:scale-110 active:scale-95 duration-200"
                                >
                                    <X size={24} />
                                </button>

                                <div className="text-center mb-6">
                                    <div className="bg-gradient-to-tr from-blue-500/10 to-indigo-500/10 dark:from-blue-500/20 dark:to-indigo-500/20 p-4 rounded-2xl text-blue-500 dark:text-blue-400 mb-3 mx-auto w-14 h-14 flex items-center justify-center shadow-inner">
                                        <Plus size={28} className="animate-pulse" />
                                    </div>
                                    <h3 className="text-xl font-extrabold text-gray-900 dark:text-white tracking-tight">Novo Lançamento</h3>
                                    <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mt-1 uppercase tracking-wider">Escolha o tipo de transação</p>
                                </div>

                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => {
                                                setShowAddModal(false);
                                                setTransType('income');
                                                setView('add');
                                            }}
                                            className="py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-2xl transition-all duration-200 flex flex-col items-center justify-center gap-2 text-base shadow-md shadow-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/40 hover:scale-[1.02] active:scale-[0.98]"
                                        >
                                            <PlusCircle size={24} className="text-white" />
                                            <span>Nova Entrada</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowAddModal(false);
                                                setTransType('expense');
                                                setView('add');
                                            }}
                                            className="py-4 bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-2xl transition-all duration-200 flex flex-col items-center justify-center gap-2 text-base shadow-md shadow-rose-500/20 hover:shadow-lg hover:shadow-rose-500/40 hover:scale-[1.02] active:scale-[0.98]"
                                        >
                                            <MinusCircle size={24} className="text-white" />
                                            <span>Nova Saída</span>
                                        </button>
                                    </div>

                                    {hasLojaItems && featureFlags?.lojaABordo?.visible && (
                                        <button
                                            onClick={() => {
                                                setShowAddModal(false);
                                                navigate('/loja_abordo', { state: { initialTab: 'pdv', fromQuickLaunch: true } });
                                            }}
                                            className="w-full py-3.5 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 text-base shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/40 hover:scale-[1.01] active:scale-[0.99]"
                                        >
                                            <ShoppingBag size={20} className="text-white" /> Venda Rápida (Loja)
                                        </button>
                                    )}

                                    {showAiVoice && (
                                        <div className="pt-2">
                                            <div className="h-px bg-gray-100 dark:bg-gray-800/60 w-full mb-4" />
                                            <div className="relative overflow-hidden rounded-2xl">
                                                {/* Selo PREMIUM */}
                                                {lockAiVoice && (
                                                    <div className="absolute top-0 left-0 z-20 pointer-events-none w-24 h-24 overflow-hidden">
                                                        <div className="absolute top-0 left-0 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-white text-[10px] font-black py-0.5 w-[130px] text-center -rotate-45 -translate-x-[38px] translate-y-[16px] shadow-md tracking-tighter drop-shadow-sm">
                                                            <span className="drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]">PREMIUM</span>
                                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -translate-x-full animate-shimmer"></div>
                                                        </div>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={handleVoiceAction}
                                                    disabled={isVoiceRecording || voiceLoading}
                                                    className={`w-full py-4 rounded-2xl font-black transition-all duration-200 flex items-center justify-center gap-3 text-lg border border-transparent shadow-lg ${isVoiceRecording
                                                        ? 'bg-red-500 text-white animate-pulse border-red-400 scale-102 shadow-red-500/20'
                                                        : voiceLoading
                                                            ? 'bg-gray-100 dark:bg-gray-800/80 text-gray-400 border-gray-200 dark:border-gray-700 cursor-wait'
                                                            : lockAiVoice
                                                                ? 'bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 text-white/60 border-gray-600 cursor-pointer hover:brightness-110'
                                                                : 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-md shadow-indigo-500/20 hover:shadow-lg hover:shadow-indigo-500/40 hover:scale-[1.02] active:scale-[0.98]'
                                                        }`}
                                                >
                                                    {isVoiceRecording ? (
                                                        <>
                                                            <div className="flex gap-1 items-center">
                                                                <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                                                <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                                                <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                                            </div>
                                                            Ouvindo...
                                                        </>
                                                    ) : voiceLoading ? (
                                                        <>
                                                            <RefreshCw className="animate-spin text-gray-400" size={20} /> Processando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="p-1.5 bg-white/10 rounded-xl backdrop-blur-md">
                                                                <Mic size={20} className={lockAiVoice ? 'text-white/40' : 'text-white'} />
                                                            </div>
                                                            Falar para Adicionar
                                                            {lockAiVoice && <Lock size={16} className="ml-1 opacity-60 text-white/60" />}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                            <p className="text-[10px] text-center text-gray-400 dark:text-gray-500 mt-2 font-medium tracking-wide">
                                                {lockAiVoice ? '⭐ Recurso exclusivo para assinantes Premium' : 'Ex: "Gastei 50 reais de gasolina"'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {/* --- NOVO: MODAL DE INSTALAÇÃO PWA --- */}
                    {/* Flight Info Modal Removed */}
                    {isFinancingCalcOpen && (
                        <Suspense fallback={null}>
                            <FinancingCalculatorModal
                                isOpen={isFinancingCalcOpen}
                                onClose={() => setIsFinancingCalcOpen(false)}
                            />
                        </Suspense>
                    )}
                    {showMissionModal && (
                        <div className="fixed inset-0 bg-black/60 z-[99999] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in overflow-y-auto">
                            <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-4xl shadow-2xl relative border border-gray-100 dark:border-gray-700 max-h-[90vh] overflow-y-auto custom-scrollbar">
                                <button
                                    onClick={() => setShowMissionModal(false)}
                                    className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors z-10"
                                >
                                    <X size={24} />
                                </button>
                                <div className="p-2 sm:p-4">
                                    <MissionTracker
                                        isDark={isDarkModeActive()}
                                        isExpired={isExpired}
                                        onOpenPremium={handleOpenPremium}
                                        isInitialLoadDone={isInitialLoadDone}

                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {showLinkAccountModal && (
                        <Suspense fallback={null}>
                            <LinkAccountModal
                                isOpen={showLinkAccountModal}
                                onClose={() => setShowLinkAccountModal(false)}
                                onConfirmExit={() => {
                                    // Lógica de logout/sair se necessário, ou apenas fechar
                                    setShowLinkAccountModal(false);
                                }}
                            />
                        </Suspense>
                    )}
                    {!!activeAnnouncement && (
                        <Suspense fallback={null}>
                            <AnnouncementModal
                                isOpen={!!activeAnnouncement}
                                onClose={() => setActiveAnnouncement(null)}
                                onConfirm={handleConfirmAnnouncement}
                                title={activeAnnouncement?.title || ''}
                                message={activeAnnouncement?.body || ''}
                            />
                        </Suspense>
                    )}
                    {!!directUpdateData && (
                        <Suspense fallback={null}>
                            <DirectUpdateModal
                                isOpen={!!directUpdateData}
                                onClose={() => setDirectUpdateData(null)}
                                updateData={directUpdateData}
                            />
                        </Suspense>
                    )}
                    {showPwaModal && (
                        <Suspense fallback={null}>
                            <InstallPwaModal
                                isOpen={showPwaModal}
                                onClose={() => {
                                    setShowPwaModal(false);
                                    // Se o usuário fechar o modal, marcamos como dispensado permanentemente para este navegador/dispositivo
                                    storage.setItem('pwaInstallPermanentlyDismissed', true);
                                }}
                                deferredPrompt={deferredPrompt}
                                isIOS={isIOS}
                            />
                        </Suspense>
                    )}

                    {/* --- BOTÃO FLUTUANTE DE INSTALAÇÃO REMOVIDO (Movido para Ferramentas) --- */}

                    {showFlexModal && (
                        <Suspense fallback={null}>
                            <FlexCalcModal
                                isOpen={showFlexModal}
                                onClose={() => setShowFlexModal(false)}
                            />
                        </Suspense>
                    )}

                    {showTripCalcModal && (
                        <Suspense fallback={null}>
                            <TripCalcModal
                                isOpen={showTripCalcModal}
                                onClose={() => setShowTripCalcModal(false)}
                            />
                        </Suspense>
                    )}

                    {kmInput.isOpen && (
                        <div className="fixed inset-0 bg-black/60 z-[99999] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                            <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-sm shadow-2xl border border-gray-100 dark:border-gray-700 transform transition-all scale-100">
                                <div className="flex flex-col text-center">
                                    <div className="bg-indigo-100 dark:bg-indigo-900/30 p-4 rounded-full text-indigo-600 dark:text-indigo-400 mb-4 mx-auto">
                                        <Edit3 size={32} />
                                    </div>
                                    <div className="flex items-center justify-center gap-2 mb-2">
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{getKmModalTitle()}</h3>
                                        <button
                                            onClick={() => {
                                                const msg = kmInput.type === 'start'
                                                    ? 'Use este modo para marcar o KM inicial do painel. Ao final do dia, você informará o KM final e o app calculará a distância exata.'
                                                    : kmInput.type === 'manual'
                                                        ? 'Use este modo se preferir informar apenas o valor total de KM rodados ao encerrar o turno, sem precisar marcar o painel agora.'
                                                        : 'Informe o odômetro final do painel para que o app calcule o total rodado hoje.';
                                                showToast(msg, 'info');
                                            }}
                                            className="text-indigo-500 hover:text-indigo-600 transition-colors"
                                        >
                                            <HelpCircle size={18} />
                                        </button>
                                    </div>

                                    {/* TABS PARA ALTERNAR TIPO NO MODO START (SÓ SE FOR HOJE E INICIO E NÃO FOR FORÇADO) */}
                                    {kmInput.isShiftStart && !kmInput.forcedType && (
                                        <div className="flex justify-center mb-4 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
                                            <button
                                                onClick={() => setKmInput(prev => ({ ...prev, type: 'start' }))}
                                                className={`flex-1 py-1 text-xs font-bold rounded-lg transition-all ${kmInput.type === 'start' ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-white shadow-sm' : 'text-gray-500'}`}
                                            >
                                                Com Odômetro
                                            </button>
                                            <button
                                                onClick={() => setKmInput(prev => ({ ...prev, type: 'manual' }))}
                                                className={`flex-1 py-1 text-xs font-bold rounded-lg transition-all ${kmInput.type === 'manual' ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-white shadow-sm' : 'text-gray-500'}`}
                                            >
                                                Entrada Manual
                                            </button>
                                        </div>
                                    )}

                                    <div className="p-5 space-y-6 animate-fade-in">
                                        <div className="text-center px-2">
                                            <p className="text-xs text-gray-500 dark:text-gray-400 italic font-medium leading-relaxed">
                                                {kmInput.type === 'start'
                                                    ? 'Registre a quilometragem atual do painel do seu veículo.'
                                                    : kmInput.type === 'end'
                                                        ? 'Informe o odômetro final para calcular quanto rodou.'
                                                        : 'A entrada manual serve para você informar o valor total direto apenas quando encerrar o turno.'}
                                            </p>
                                        </div>
                                        {(kmInput.type === 'start' || kmInput.type === 'end') && (
                                            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded-lg border border-yellow-100 dark:border-yellow-800 mb-2">
                                                <p className="text-[10px] text-yellow-700 dark:text-yellow-400 font-bold flex items-center justify-center gap-1">
                                                    <AlertCircle size={12} /> Verificar odômetro no painel do veículo
                                                </p>
                                            </div>
                                        )}

                                        {kmInput.type === 'end' && (
                                            <div>
                                                <label className="text-gray-600 dark:text-gray-400 text-sm font-bold block mb-1 text-left">
                                                    Odômetro Inicial (Confirmar)
                                                </label>
                                                <input
                                                    type="number"
                                                    step="any"
                                                    inputMode="decimal"
                                                    value={kmInput.tempStartValue}
                                                    onChange={(e) => setKmInput(prev => ({ ...prev, tempStartValue: e.target.value }))}
                                                    className="w-full p-3 text-lg font-bold bg-gray-50 dark:bg-gray-900 rounded-xl border border-indigo-200 dark:border-indigo-700 outline-none dark:text-white text-center mb-4"
                                                    placeholder="Ex: 1500"
                                                />
                                            </div>
                                        )}

                                        <div>
                                            <label className="text-gray-600 dark:text-gray-400 text-sm font-bold block mb-1 text-left">
                                                {kmInput.type === 'start'
                                                    ? 'Odômetro Inicial'
                                                    : (kmInput.type === 'end' && !activeShift?.isManual)
                                                        ? 'Odômetro Final'
                                                        : 'KM Total Rodado'}
                                            </label>
                                            <input
                                                type="number"
                                                step="any"
                                                inputMode="decimal"
                                                value={kmInput.tempValue}
                                                onChange={(e) => setKmInput(prev => ({ ...prev, tempValue: e.target.value }))}
                                                className="w-full p-3 text-lg font-bold bg-gray-50 dark:bg-gray-900 rounded-xl border border-indigo-200 dark:border-indigo-700 outline-none dark:text-white text-center"
                                                placeholder={getKmModalPlaceholder()}
                                                autoFocus={kmInput.type !== 'end'}
                                            />
                                        </div>

                                        {(kmInput.type === 'manual' || kmInput.type === 'end') && (
                                            <div>
                                                <label className="text-gray-600 dark:text-gray-400 text-sm font-bold block mb-1 text-left">
                                                    Horas Trabalhadas (Opcional)
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    inputMode="decimal"
                                                    value={kmInput.tempHours}
                                                    onChange={(e) => setKmInput(prev => ({ ...prev, tempHours: e.target.value }))}
                                                    className="w-full p-3 text-lg font-bold bg-gray-50 dark:bg-gray-900 rounded-xl border border-indigo-200 dark:border-indigo-700 outline-none dark:text-white text-center"
                                                    placeholder="Ex: 8.5"
                                                />
                                            </div>
                                        )}

                                        {/* Seleção de ferramentas para início automático */}
                                        {kmInput.isShiftStart && kmInput.type !== 'manual' && (
                                            <div className="space-y-4">
                                                <div className="text-center">
                                                    <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-[0.2em]">
                                                        Iniciar Ferramenta
                                                    </p>
                                                </div>

                                                <div className="flex justify-center">
                                                    {/* Opção Cronômetro */}
                                                    <button
                                                        onClick={() => {
                                                            const newState = !autoStartTimer;
                                                            setAutoStartTimer(newState);
                                                        }}
                                                        className={`flex flex-col items-center justify-center gap-2 p-6 w-32 rounded-[24px] border-2 transition-all active:scale-95 ${autoStartTimer
                                                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl shadow-indigo-500/30'
                                                            : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 text-gray-400 hover:border-indigo-200 dark:hover:border-indigo-900'}`}
                                                    >
                                                        <Timer size={32} />
                                                        <span className="text-[12px] font-black uppercase tracking-tight">Cronômetro</span>
                                                    </button>
                                                </div>

                                                {autoStartTimer && (
                                                    <div className="flex items-center justify-center gap-2 animate-in fade-in zoom-in duration-300 mt-4">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-tighter">
                                                            O Cronômetro será iniciado
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex gap-3 w-full">
                                        <button
                                            onClick={() => {
                                                if (kmInput.timerWasRunning) {
                                                    resumeWorkTimer();
                                                }
                                                setKmInput({ isOpen: false, type: null, tempValue: '', tempHours: '' });
                                            }}
                                            className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={saveKmReading}
                                            className="flex-1 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-600/20"
                                        >
                                            Salvar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}




                    {showFinishDayConfirmModal && (
                        <div className="fixed inset-0 bg-black/60 z-[99999] flex items-center justify-center p-6 backdrop-blur-md animate-in fade-in duration-300">
                            <div className="bg-white dark:bg-gray-900 w-full max-w-[340px] rounded-[32px] overflow-hidden shadow-2xl border border-gray-100 dark:border-white/10 animate-in zoom-in-95 duration-300">
                                <div className="p-8 pb-4 text-center">
                                    <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 shadow-lg">
                                        <AlertTriangle size={32} />
                                    </div>
                                    <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight">Encerrar o Dia?</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                                        Isso irá parar o cronômetro. Você será solicitado(a) a informar o Odômetro Final.
                                    </p>
                                </div>
                                <div className="px-8 pb-8 space-y-3">
                                    <button
                                        onClick={confirmFinishWorkDay}
                                        className="w-full py-4 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-2xl shadow-lg shadow-orange-500/20 transition-all active:scale-[0.98]"
                                    >
                                        Encerrar e Finalizar
                                    </button>
                                    <button
                                        onClick={() => setShowFinishDayConfirmModal(false)}
                                        className="w-full py-3 text-sm font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                    >
                                        Continuar Pausado
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}




                    {/* MODAIS GLOBAIS AGORA GERENCIADOS PELO MODALPROVIDER */}




                    {showResetModal && (
                        <div className="fixed inset-0 bg-black/60 z-[99999] flex items-center justify-center p-6 backdrop-blur-md animate-in fade-in duration-300">
                            <div className="bg-white dark:bg-gray-900 w-full max-w-[340px] rounded-[32px] shadow-2xl border border-gray-100 dark:border-white/10 animate-in zoom-in-95 duration-300">
                                <div className="p-8 pb-4 text-center">
                                    <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 shadow-lg">
                                        <AlertTriangle size={32} />
                                    </div>
                                    <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight">Tem certeza?</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                                        Esta ação apagará <b>todas</b> as suas corridas, gastos e dados de KM. Isso não pode ser desfeito.
                                    </p>
                                </div>
                                <div className="px-8 pb-8 space-y-3">
                                    <button
                                        onClick={confirmReset}
                                        className="w-full py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-2xl shadow-lg shadow-red-500/20 transition-all active:scale-[0.98]"
                                    >
                                        Sim, Apagar Tudo
                                    </button>
                                    <button
                                        onClick={() => setShowResetModal(false)}
                                        className="w-full py-3 text-sm font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* MODAL DE RENOVAÇÃO PREMIUM (CENTRALIZADO) */}
                    {showPremiumModal && (
                        <div className="fixed inset-0 bg-black/60 z-[200000] flex items-center justify-center p-6 backdrop-blur-md animate-in fade-in duration-300">
                            <div className="bg-white dark:bg-gray-900 w-full max-w-[340px] rounded-[32px] overflow-hidden shadow-2xl border border-gray-100 dark:border-white/10 animate-in zoom-in-95 duration-300 relative">
                                <button
                                    onClick={() => setShowPremiumModal(false)}
                                    className="absolute top-6 right-6 p-2 rounded-full bg-gray-100 dark:bg-white/5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                >
                                    <X size={18} />
                                </button>

                                <div className="p-8 pb-4 text-center">
                                    <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-amber-50 dark:bg-amber-500/10 text-amber-500 shadow-lg">
                                        <Star size={32} fill="currentColor" />
                                    </div>
                                    <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight">Recurso Premium</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                                        Este recurso é exclusivo. Renove seu plano para manter seus dados sincronizados e acesso total.
                                    </p>
                                </div>

                                <div className="px-8 pb-8 space-y-3">
                                    <button
                                        onClick={() => {
                                            setShowPremiumModal(false);
                                            setSettingsInitialTab('subscription');
                                            navigate('/settings');
                                        }}
                                        className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-indigo-500/20 transition-all active:scale-[0.98]"
                                    >
                                        Ver Planos Agora
                                    </button>
                                    <button
                                        onClick={() => setShowPremiumModal(false)}
                                        className="w-full py-3 text-sm font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                    >
                                        Agora não
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <Suspense fallback={null}>
                        <DailyStatsEditModal
                            isOpen={showDailyManualModal}
                            onClose={() => setShowDailyManualModal(false)}
                            date={dailyFilter}
                            hasExistingEntries={transactions.filter(t => t.date.startsWith(dailyFilter)).length > 0 || Object.keys(kmRecords).includes(dailyFilter)}
                            currentStats={
                                dailyManualStats && dailyManualStats[dailyFilter]
                                    ? { ...dailyManualStats[dailyFilter] }
                                    : (() => {
                                        let mIncome = 0; let mExpense = 0; let mTrips = 0;
                                        transactions.filter(t => t.date.startsWith(dailyFilter)).forEach(t => {
                                            if (t.type === 'income') mIncome += Number(t.amount) || 0;
                                            if (t.type === 'expense') mExpense += Number(t.amount) || 0;
                                            mTrips += Number(t.tripCount) || 0;
                                        });
                                        const record = kmRecords[dailyFilter];
                                        let mKm = 0;
                                        if (record && record.manualKm != null && !isNaN(Number(record.manualKm))) {
                                            mKm = Number(record.manualKm);
                                        } else if (record && record.startOdo != null && record.endOdo != null &&
                                            !isNaN(Number(record.startOdo)) && !isNaN(Number(record.endOdo)) &&
                                            Number(record.endOdo) >= Number(record.startOdo)) {
                                            mKm = Number(record.endOdo) - Number(record.startOdo);
                                        }
                                        const mHours = (record && record.workHours ? Number(record.workHours) : 0);
                                        return { trips: mTrips, hours: mHours, km: mKm, income: mIncome, expense: mExpense };
                                    })()
                            }
                            onSave={handleSaveDailyManualStats}
                            onReset={(date) => handleResetDay(date)}
                            transactions={(transactions || []).filter(t => t.date.startsWith(dailyFilter))}
                            onAddTransaction={handleAddQuickTransaction}
                            onDeleteTransaction={handleDeleteQuickTransaction}
                            onEditTransaction={(t) => { setShowDailyManualModal(false); handleEditTransaction(t); }}
                            categories={{ income: getCategories('income'), expense: getCategories('expense') }}
                            vehicles={vehicles}
                            activeVehicleId={selectedVehicleId}
                            formatWorkHours={formatWorkHours}
                            isExpired={isExpired}
                            onOpenPremium={handleOpenPremium}

                        />
                    </Suspense>
                    {/* MODAIS DE RASTREAMENTO GPS */}
                    <LocationDisclosureModal
                        isOpen={showLocationDisclosure}
                        onCancel={() => setShowLocationDisclosure(false)}
                        onAccept={async () => {
                            setShowLocationDisclosure(false);

                            // Solicita permissão de notificações para o lembrete de inatividade
                            if (isNativeApp()) {
                                try {
                                    await LocalNotifications.requestPermissions();
                                } catch (err) {
                                    console.error('Erro ao solicitar permissão de notificações:', err);
                                }
                            }

                            await TrackingService.acceptDisclosure();
                            if (pendingTrackerAction) {
                                TrackingService.start(pendingTrackerAction);
                                setPendingTrackerAction(null);
                            } else {
                                TrackingService.start();
                            }
                        }}
                    />

                    {/* MODAL DE RESTAURAÇÃO DA NUVEM */}
                    {cloudRestoreData && (
                        <Suspense fallback={null}>
                            <CloudRestoreModal
                                cloudStats={cloudRestoreData.cloudStats}
                                localStats={cloudRestoreData.localStats}
                                onRestore={handleCloudRestore}
                                onKeepLocal={() => setCloudRestoreData(null)}
                            />
                        </Suspense>
                    )}

                    <Suspense fallback={null}>
                        <AppearanceQuickModal
                            isOpen={showAppearanceModal}
                            onClose={() => setShowAppearanceModal(false)}
                            theme={theme}
                            setTheme={setTheme}
                            accentColor={accentColor}
                            setAccentColor={setAccentColor}
                            compactMode={compactMode}
                            setCompactMode={setCompactMode}
                            showCardComparisons={showCardComparisons}
                            setShowCardComparisons={setShowCardComparisons}
                            isExpired={isExpired}
                            onOpenPremium={handleOpenPremium}
                            isTrial={subscription?.isTrial}
                            isAdmin={isAdmin(firebaseUser?.uid)}
                            isPartner={userProfile?.isPartner}
                        />
                    </Suspense>

                    {/* POP-UP INTELIGENTE DE AVISO (TRIAL, VENCIMENTO E MODO LEITURA) */}
                    <SubscriptionAlertModal
                        isOpen={!!activeAlert}
                        type={activeAlert?.type}
                        daysLeft={activeAlert?.daysLeft}
                        onDismiss={handleDismissSubscriptionAlert}
                        onGoToPayment={handleOpenSubscriptionCallback}
                    />

                </div>
            </div>
        </div>
    );
}

export default function App(props) {
    return (
        <BrowserRouter>
            <AppProvider firebaseUser={props.firebaseUser}>
                <ModalProvider>
                    <AppContent {...props} />
                </ModalProvider>
            </AppProvider>
        </BrowserRouter>
    );
}
